import { slot } from "ts-event-bus";
import Ably from "ably";
import _, { uniqBy } from "lodash";
import npmDebug from "debug";
import { v1 as v1$1, v5 as v5$1 } from "uuid";
import omit from "lodash/omit";
import { Buffer } from "buffer";
import isEqual$1 from "lodash/isEqual";
const TARGET_RENDERER = {
  sendToMainApp: "_sendToMainApp",
  fromLauncherToMainApp: "_fromLauncherToMainApp",
  fromKindleToMainApp: "_fromKindleToMainApp",
  fromMainAppToKindle: "_fromMainAppToKindle",
  sendToKindle: "_sendToKindle",
  fromMainAppToLauncher: "_fromMainAppToLauncher",
  sendToLauncher: "_sendToLauncher"
};
const INTERNAL_SLOT_BETWEEN_RENDERERS = {
  _sendToMainApp: slot(),
  _fromLauncherToMainApp: slot(),
  _sendToLauncher: slot(),
  _fromMainAppToLauncher: slot()
};
const SLOT_LAUNCHER_TO_MAIN_APP = {
  saveCard: slot(),
  insertFromLauncherToMainApp: slot(),
  saveAssociatedCard: slot(),
  createANewCardFromMainWindow: slot(),
  addPropertiesValues: slot(),
  updateCard: slot(),
  getAppCurrentState: slot(),
  getReferenceCard: slot(),
  getCardUuidWhereToCaptureToCard: slot(),
  getTodayCard: slot(),
  setHighlightInOpenTextEditor: slot(),
  addAIPromptToUser: slot()
};
const SLOT_MAIN_APP_TO_LAUNCHER = {
  resetCard: slot(),
  sendInitialInformation: slot(),
  syncAuthentication: slot()
};
const SLOT_MAIN_APP_TO_EDITOR = {
  refreshEditor: slot()
};
const SLOT_BETWEEN_RENDERERS = {
  ...SLOT_LAUNCHER_TO_MAIN_APP,
  ...SLOT_MAIN_APP_TO_LAUNCHER,
  ...SLOT_MAIN_APP_TO_EDITOR
};
const SLOT_KINDLE_MAIN_APP = {
  showKindleWindow: slot(),
  hideKindleWindow: slot(),
  sendMessageFromMainAppToKindleWindow: slot(),
  sendMessageFromKindleToMainAppWindow: slot()
};
const LazyAppEvents = {
  ...INTERNAL_SLOT_BETWEEN_RENDERERS,
  ...SLOT_BETWEEN_RENDERERS,
  ...SLOT_KINDLE_MAIN_APP,
  ping: slot(),
  setUserState: slot(),
  closeLauncher: slot(),
  hideLauncher: slot(),
  getOSAppDataById: slot(),
  nativeLogger: slot(),
  exportAllCards: slot(),
  exportPDF: slot(),
  getAttachment: slot(),
  saveAttachments: slot(),
  requestAttachmentBuffer: slot(),
  getFileFromPath: slot(),
  goToAdminPage: slot(),
  imageIsSaved: slot(),
  generateMacMenuBar: slot(),
  destroyMacMenuBar: slot(),
  openLauncherNotification: slot(),
  setNotification: slot(),
  setNotificationShortcut: slot(),
  openNotification: slot(),
  findInPage: slot(),
  toggleSearch: slot(),
  matchesSearch: slot(),
  clearSearch: slot(),
  getPermission: slot(),
  getPermissions: slot(),
  promptPermission: slot(),
  watchPermission: slot(),
  resetPermission: slot(),
  onboardingStatus: slot(),
  syncAppWithApi: slot(),
  appUpdateAvailable: slot(),
  updateApp: slot(),
  executeCapture: slot(),
  disableLauncherShortcuts: slot(),
  enableLauncherShortcuts: slot(),
  getLauncherShortcut: slot(),
  setLauncherShortcut: slot(),
  getLauncherShortcutWithoutContext: slot(),
  setLauncherShortcutWithoutContext: slot(),
  getLauncherShortcutAI: slot(),
  setLauncherShortcutAI: slot(),
  getLauncherShortcutAIWithoutContext: slot(),
  setLauncherShortcutAIWithoutContext: slot(),
  getNotificationOpenShortcut: slot(),
  setNotificationOpenShortcut: slot(),
  getConfigPreference: slot(),
  setConfigPreference: slot()
};
const SLOT_BETWEEN_RENDERERS_KEYS = Object.keys(SLOT_BETWEEN_RENDERERS);
const IPC_RENDERER_CONNECTION = "ipcRenderer-connection";
const IPC_RENDERER_DISCONNECTION = "ipcRenderer-disconnection";
const IPC_RENDERER_TO_IPC_MAIN = "ipcRenderer-to-ipcMain";
const IPC_MAIN_TO_IPC_RENDERER = "ipcMain-to-ipcRenderer";
const APP = "app";
const WEB = "web";
const getWebsocket = (ABLY_KEY) => new Promise((resolve) => {
  const WS = new Ably.Realtime.Promise(ABLY_KEY);
  WS == null ? void 0 : WS.connection.once("connected").then((...props) => {
    resolve(WS);
  });
});
const close = (WS) => () => {
  WS.then((ws) => {
    ws == null ? void 0 : ws.close();
  });
};
const sendTemplate = (WS) => ({ channelName, data, channelId }) => {
  WS.then(async (ws) => {
    const channel = ws == null ? void 0 : ws.channels.get(channelId);
    await (channel == null ? void 0 : channel.publish(channelName, data));
  });
};
const listenTemplate = (WS) => ({ channelName, callback, channelId }) => {
  WS.then(async (ws) => {
    const channel = ws == null ? void 0 : ws.channels.get(channelId);
    await (channel == null ? void 0 : channel.subscribe(channelName, (message) => {
      callback(message.data);
    }));
  });
};
const WsWebToAppService = (ABLY_KEY) => {
  const WS = getWebsocket(ABLY_KEY);
  return {
    send: ({ channelId, data }) => sendTemplate(WS)({ channelId, channelName: `${channelId}-from-${WEB}`, data }),
    listen: ({ channelId, callback }) => listenTemplate(WS)({ channelId, channelName: `${channelId}-from-${APP}`, callback }),
    close: close(WS)
  };
};
const WsAppToWebService = (ABLY_KEY) => {
  const WS = getWebsocket(ABLY_KEY);
  return {
    send: ({ channelId, data }) => sendTemplate(WS)({ channelId, channelName: `${channelId}-from-${APP}`, data }),
    listen: ({ channelId, callback }) => listenTemplate(WS)({ channelId, channelName: `${channelId}-from-${WEB}`, callback }),
    close: close(WS)
  };
};
function isTypeFilterCardView(value) {
  return "includeTypes" in value;
}
function makeFilterFromCardView(view) {
  if (isTypeFilterCardView(view))
    return makeFilterFromTypeFilterCardView(view);
  return {};
}
function makeFilterFromTypeFilterCardView({ includeTypes }) {
  return { types: includeTypes };
}
function makeTypeFilterCardView({
  currentDescriptor,
  includeTypes
}) {
  return {
    currentDescriptor,
    includeTypes: includeTypes != null ? includeTypes : []
  };
}
function makeTypeFilterFromMetadata(metadata, filters) {
  var _a;
  if (!metadata)
    return {};
  const view = metadata.views[(_a = filters.view) != null ? _a : "default"];
  if (!view)
    return {};
  return makeFilterFromCardView(view);
}
const addToPropertyValue = ({
  propertyDescriptorId,
  values,
  metadata
}) => {
  var _a, _b;
  return {
    values: _.uniq([...((_b = (_a = metadata == null ? void 0 : metadata.propertiesValues) == null ? void 0 : _a[propertyDescriptorId]) == null ? void 0 : _b.values) || [], ...values])
  };
};
function getPropertyValues({
  propertyDescriptorId,
  metadata
}) {
  var _a, _b, _c;
  if (!metadata)
    return [];
  return (_c = (_b = (_a = metadata == null ? void 0 : metadata.propertiesValues) == null ? void 0 : _a[propertyDescriptorId]) == null ? void 0 : _b.values) != null ? _c : [];
}
function makePropertyValueWithValue({
  propertyDescriptorId,
  values
}) {
  return { [propertyDescriptorId]: { values } };
}
function makePropertyValueCardContext({
  sourceUuid,
  meetingUuid,
  personUuid
}) {
  return {
    ...sourceUuid ? makePropertyValueWithValue({ propertyDescriptorId: "source", values: [sourceUuid] }) : {},
    ...meetingUuid ? makePropertyValueWithValue({ propertyDescriptorId: "meeting", values: [meetingUuid] }) : {},
    ...personUuid ? makePropertyValueWithValue({ propertyDescriptorId: "person", values: [personUuid] }) : {}
  };
}
const isTwitterUrl = (href) => /^https?:\/\/(mobile|platform\.twitter|twitter|x)\.com/i.test(href);
const isTweetUrl = (href) => href.match(/^https?:\/\/(mobile\.twitter|twitter|x)\.com\/(?:#!\/)?(\w+)\/status\/(\d*)/);
const jsonToQueryString = (json) => {
  return "?" + Object.keys(json).reduce((result, key) => {
    if (Array.isArray(json[key])) {
      json[key].forEach(
        (subKey) => result.push(encodeURIComponent(key) + "[]=" + encodeURIComponent(subKey))
      );
    } else {
      result.push(encodeURIComponent(key) + "=" + encodeURIComponent(json[key]));
    }
    return result;
  }, []).join("&");
};
const isUrl = (string) => {
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_2) {
    return false;
  }
};
const isPDFUrl = (url) => url.toLowerCase().endsWith(".pdf");
const removePrefixInFrontOrUrl = (url) => url == null ? void 0 : url.replace("https://", "").replace("http://", "").replace("www.", "");
const slackToUrl = (teamId, userId) => `slack://user?team=${teamId}&id=${userId}`;
const slackToChannelUrl = (teamId, userId) => `slack://channel?team=${teamId}&id=${userId}`;
const emailToUrl = (email) => `mailto:${email}`;
const getCardUrl = ({ workspaceName, cardUuid }) => `/app/${workspaceName}/cards/${cardUuid}`;
const getCardReadViewUrl = ({
  workspaceName,
  cardUuid,
  clipUuid
}) => `/app/${workspaceName}/cards/${cardUuid}/clip/${clipUuid}`;
const getCardUuidFromUrl = (url) => {
  var _a;
  return (_a = url.split("/cards/").pop()) == null ? void 0 : _a.split("/")[0];
};
const getClipUuidFromUrl = (url) => {
  const id = url.split("/clip/").pop();
  return id !== url ? id : void 0;
};
const getTwitterUrl = (username) => `https://x.com/@${username}`;
const YOUTUBE_TITLE_LENGTH = 50;
const YOUTUBE_IFRAME_DOMAIN = "https://www.youtube.com/embed/";
const YOUTUBE_REGEX = /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/gm;
const formatTranscriptAsText = (transcript) => transcript == null ? void 0 : transcript.map((t) => t["#text"]).join(" ");
const isYoutubeUrl = (href) => {
  var _a, _b;
  return !!((_b = (_a = href == null ? void 0 : href.match) == null ? void 0 : _a.call(
    href,
    /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/|\?v)?)([\w-]+|\S)(\S+)?$/gm
  )) == null ? void 0 : _b.length);
};
const EMBED_YOUTUBE_CONFIG = {
  rel: 0,
  modestbranding: 1,
  iv_load_policy: 3,
  fs: 1,
  color: "white",
  enablejsapi: 1,
  disablekb: 0,
  autoplay: 0
};
const getYoutubeQuery = (additionalQuery) => {
  return Object.entries({ ...EMBED_YOUTUBE_CONFIG, ...additionalQuery }).map(([key, value]) => `${key}=${value}`).join("&");
};
const addQueryToYoutubeUrl = (url, additionalQuery) => {
  return `${url.split("?")[0]}?${getYoutubeQuery(additionalQuery)}`;
};
const parseYoutubeEmbedUrl = (url) => {
  const id = url.searchParams.get("v");
  return `${YOUTUBE_IFRAME_DOMAIN}${id}?${getYoutubeQuery()}`;
};
const getIframe = ({ selector }) => {
  const iframe = document.querySelector(selector);
  if (iframe)
    return iframe;
  return void 0;
};
const getOnTopIframe = (props) => {
  const iframe = getIframe(props);
  if (iframe)
    return iframe;
  return [...document.querySelectorAll("iframe")].pop();
};
const isYTPlayer = (player) => !!player && "getCurrentTime" in player;
const isPlayerInfo = (player) => !!player && "playerInfo" in player;
const isPlayer = (player) => isYTPlayer(player) || isPlayerInfo(player);
const YoutubeGetterPlayer = (id) => YT.get(id);
const getYoutubeInPlayer = (iframe) => {
  let player = iframe.player;
  if (isPlayer(player))
    return player;
  player = YoutubeGetterPlayer(iframe.id);
  if (isPlayer(player)) {
    iframe.player = player;
    return player;
  }
  player = new YT.Player(iframe);
  if (!isPlayer(player))
    return;
  iframe.player = player;
  return player;
};
const maybeGetCurrentTimeFromYTGetPlayer = (id) => {
  const ytGetPlayer = YoutubeGetterPlayer(id);
  if (isPlayerInfo(ytGetPlayer))
    return ytGetPlayer.playerInfo.currentTime;
  return 0;
};
const getYoutubeInLazyCurrentTimestamp = (props) => {
  const lastIframeInTheDom = getOnTopIframe(props);
  if (!lastIframeInTheDom)
    return void 0;
  const ytGetPlayerCurrentTime = maybeGetCurrentTimeFromYTGetPlayer(lastIframeInTheDom.id);
  if (ytGetPlayerCurrentTime)
    return ytGetPlayerCurrentTime;
  const player = getYoutubeInPlayer(lastIframeInTheDom);
  if (isYTPlayer(player))
    return player.getCurrentTime();
  return void 0;
};
const getYoutubePlayerByQueryString = (props) => {
  const iframe = getIframe(props);
  if (!iframe)
    return;
  const player = getYoutubeInPlayer(iframe);
  if (!isYTPlayer(player))
    return;
  return {
    getDuration: () => {
      try {
        return player.getDuration();
      } catch {
        return void 0;
      }
    },
    seekTo: (timestamp) => {
      try {
        return player.seekTo(timestamp, true);
      } catch {
        return void 0;
      }
    },
    pauseVideo: () => {
      try {
        return player.pauseVideo();
      } catch {
        return void 0;
      }
    }
  };
};
const loadYoutubeApi = (props) => {
  var _a;
  if (typeof YT == "undefined" || typeof YT.Player == "undefined") {
    var tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    var firstScriptTag = document.getElementsByTagName("script")[0];
    (_a = firstScriptTag.parentNode) == null ? void 0 : _a.insertBefore(tag, firstScriptTag);
    window.getYoutubeInLazyCurrentTimestamp = () => getYoutubeInLazyCurrentTimestamp(props);
  }
};
const getYoutubeVideoId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : void 0;
};
const removeDynamicQueryFromYoutubeUrl = (url) => {
  return url.replaceAll(/&t=\d+s/g, "").replace(/\?t=\d+s/g, "");
};
const TWITTER_REGEX = /^((?:https?:)?\/\/)?((?:www)\.)?((?:twitter\.com)).*$/;
const LINKEDIN_REGEX = /^((?:https?:)?\/\/)?((?:www)\.)?((?:linkedin\.com)).*$/;
const SPOTIFY_REGEX = /^((?:https?:)?\/\/)?((?:www)\.)?((?:open\.spotify\.com)).*$/;
const TWITTER_IFRAME_DOMAIN = (id, widgetId) => {
  return `https://platform.twitter.com/embed/Tweet.html?dnt=false&embedId=twitter-widget-${widgetId}&features=eyJ0ZndfZXhwZXJpbWVudHNfY29va2llX2V4cGlyYXRpb24iOnsiYnVja2V0IjoxMjA5NjAwLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X2hvcml6b25fdHdlZXRfZW1iZWRfOTU1NSI6eyJidWNrZXQiOiJodGUiLCJ2ZXJzaW9uIjpudWxsfSwidGZ3X3NwYWNlX2NhcmQiOnsiYnVja2V0Ijoib2ZmIiwidmVyc2lvbiI6bnVsbH19&frame=false&hideCard=false&hideThread=false&id=${id}&lang=en&origin=https%3A%2F%2Ftwitframe.com%2Fshow%3Furl%3Dhttps%3A%2F%2Ftwitter.com%2Famasad%2Fstatus%2F${id}&sessionId=8108ca39bc2eaa92622ec063ddae46e43b92288e&theme=dark&align=center&width=2000&widgetsVersion=1890d59c%3A1627936082797`;
};
const SPOTIFY_IFRAME_DOMAIN = (id) => `https://open.spotify.com/embed${id}`;
const LINKEDIN_IFRAME_DOMAIN = (id) => `https://www.linkedin.com/embed/feed/update/urn:li:share:${id}`;
const parseTwitterUrl = (url, widgetId) => {
  const id = url.pathname.split("/").pop();
  return TWITTER_IFRAME_DOMAIN(id || "", widgetId);
};
const parseLinkedinUrl = (url) => {
  const id = url.href.slice(url.href.lastIndexOf(":") + 1).replace("/", "");
  return LINKEDIN_IFRAME_DOMAIN(id);
};
const parseSpotifyUrl = (url) => {
  return SPOTIFY_IFRAME_DOMAIN(url.pathname);
};
const DOMAIN_PARSERS_CONFIG = {
  youtube: {
    regex: YOUTUBE_REGEX,
    getAttributes: parseYoutubeEmbedUrl
  },
  twitter: {
    regex: TWITTER_REGEX,
    getAttributes: parseTwitterUrl
  },
  linkedin: {
    regex: LINKEDIN_REGEX,
    getAttributes: parseLinkedinUrl
  },
  spotify: {
    regex: SPOTIFY_REGEX,
    getAttributes: parseSpotifyUrl
  }
};
const getDomainConfig = (url) => {
  var _a, _b, _c;
  return (_c = (_b = (_a = Object.entries(DOMAIN_PARSERS_CONFIG).filter(([_2, parser]) => {
    var _a2, _b2;
    const match = (_b2 = (_a2 = url == null ? void 0 : url.href) == null ? void 0 : _a2.match) == null ? void 0 : _b2.call(_a2, parser.regex);
    return match ? (match == null ? void 0 : match.length) > 0 : false;
  })) == null ? void 0 : _a.map(([domain, parser]) => {
    return {
      domain,
      ...parser
    };
  })) == null ? void 0 : _b[0]) != null ? _c : void 0;
};
const isUrlOfType = (type, src) => {
  if (!src)
    return false;
  try {
    let url = new URL(src);
    const domainConfig = getDomainConfig(url);
    return (domainConfig == null ? void 0 : domainConfig.domain) === type;
  } catch (e) {
    return false;
  }
};
const urlMediaParser = (src, id) => {
  try {
    if (!src || !isUrl(src))
      return src;
    let url = new URL(src);
    const domainConfig = getDomainConfig(url);
    return domainConfig ? domainConfig.getAttributes(url, id || "1") : src;
  } catch (e) {
    return src;
  }
};
const CATEGORY_ARTICLES = "Articles";
const CATEGORY_TWEETS = "Tweets";
const CATEGORY_BOOKS = "Books";
const CATEGORY_NEWSLETTERS = "Newsletters";
const CATEGORY_VIDEOS = "Videos";
const CATEGORY_EMAILS = "Emails";
const CATEGORY_CONVERSATIONS = "Conversations";
const CATEGORY_SLACK = "Slack";
const CATEGORY_PROFILES = "Profiles";
const CATEGORY_LINKEDIN_POSTS = "Linkedin Posts";
const CATEGORY_LINKEDIN_DM = "Linkedin DMs";
const CATEGORY_DOCUMENTS = "Documents";
const CATEGORY_PODCASTS = "Podcasts";
const CATEGORY_MUSICS = "Musics";
const CATEGORY_LINKS = "Links";
const CATEGORY_APPS = "Apps";
const CATEGORY_REDDIT = "Reddit";
const CATEGORY_TIKTOK = "TikTok";
const CATEGORY_PDF = "PDFs";
const CATEGORY_FILE = "Files";
const CATEGORY_SUBSTACK_NOTES = "Substack notes";
const CATEGORY_INSTAGRAM_POSTS = "Instagram Posts";
const CATEGORY_INSTAGRAM_REELS = "Instagram Reels";
const CATEGORY_NAMES = {
  CATEGORY_ARTICLES,
  CATEGORY_TWEETS,
  CATEGORY_BOOKS,
  CATEGORY_NEWSLETTERS,
  CATEGORY_VIDEOS,
  CATEGORY_EMAILS,
  CATEGORY_CONVERSATIONS,
  CATEGORY_PROFILES,
  CATEGORY_LINKEDIN_POSTS,
  CATEGORY_LINKEDIN_DM,
  CATEGORY_DOCUMENTS,
  CATEGORY_PODCASTS,
  CATEGORY_MUSICS,
  CATEGORY_SUBSTACK_NOTES,
  CATEGORY_LINKS,
  CATEGORY_APPS,
  CATEGORY_REDDIT,
  CATEGORY_TIKTOK,
  CATEGORY_PDF,
  CATEGORY_FILE,
  CATEGORY_SLACK,
  CATEGORY_INSTAGRAM_POSTS,
  CATEGORY_INSTAGRAM_REELS
};
const getCategoryFromUrl = (url) => {
  if (!url)
    return CATEGORY_APPS;
  try {
    const urlObject = new URL(url);
    const hostname = urlObject.hostname;
    const pathname = urlObject.pathname;
    if (isTweetUrl(url))
      return CATEGORY_TWEETS;
    if (isUrlOfType("youtube", url))
      return CATEGORY_VIDEOS;
    if (isUrlOfType("linkedin", url) && pathname.includes("/in"))
      return CATEGORY_PROFILES;
    if (isUrlOfType("spotify", url))
      return CATEGORY_PODCASTS;
    if (isUrlOfType("linkedin", url) && pathname.includes("/messaging/thread"))
      return CATEGORY_LINKEDIN_DM;
    if (isUrlOfType("linkedin", url) && (pathname.includes("/feed/update") || pathname.includes("/posts")))
      return CATEGORY_LINKEDIN_POSTS;
    if (hostname.includes("reddit.com"))
      return CATEGORY_REDDIT;
    if (hostname.includes("medium.com"))
      return CATEGORY_ARTICLES;
    if (hostname.includes("fortelabs.com"))
      return CATEGORY_ARTICLES;
    if (hostname.includes("books.google.com"))
      return CATEGORY_BOOKS;
    if (hostname.includes("news.ycombinator.com"))
      return CATEGORY_NEWSLETTERS;
    if (hostname.includes("substack.com") && pathname.includes("/note"))
      return CATEGORY_SUBSTACK_NOTES;
    if (hostname.includes("substack.com"))
      return CATEGORY_NEWSLETTERS;
    if (hostname.includes("mail.google.com"))
      return CATEGORY_EMAILS;
    if (hostname.includes("drive.google.com"))
      return CATEGORY_DOCUMENTS;
    if (hostname.includes("podcasts.apple.com"))
      return CATEGORY_PODCASTS;
    if (hostname.includes("whatsapp.com"))
      return CATEGORY_CONVERSATIONS;
    if (hostname.includes("superhuman.com"))
      return CATEGORY_EMAILS;
    if (hostname.includes("gmail.com"))
      return CATEGORY_EMAILS;
    if (hostname.includes("slack.com"))
      return CATEGORY_SLACK;
    if (hostname.includes("economist.com"))
      return CATEGORY_ARTICLES;
    if (hostname.includes("tiktok.com"))
      return CATEGORY_TIKTOK;
    if (url.endsWith(".pdf"))
      return CATEGORY_PDF;
    if (url.includes("instagram.com/reel/"))
      return CATEGORY_INSTAGRAM_REELS;
    if (url.includes("instagram.com/p/"))
      return CATEGORY_INSTAGRAM_POSTS;
    if (url.startsWith("/Users/"))
      return CATEGORY_FILE;
    return CATEGORY_LINKS;
  } catch (e) {
    return CATEGORY_LINKS;
  }
};
const getFromEmail = ({ email, emailSubject }) => {
  if (!email)
    return CATEGORY_EMAILS;
  if (email.includes("substack.com"))
    return CATEGORY_NEWSLETTERS;
  if (email.includes("the-ask.uk"))
    return CATEGORY_NEWSLETTERS;
  if (email.includes("hello@"))
    return CATEGORY_NEWSLETTERS;
  if (email.includes("stratechery.com"))
    return CATEGORY_NEWSLETTERS;
  if (email.includes("newsletter"))
    return CATEGORY_NEWSLETTERS;
  if (email.match(/@.*news/gm))
    return CATEGORY_NEWSLETTERS;
  if (emailSubject == null ? void 0 : emailSubject.toLocaleLowerCase().includes("newsletter"))
    return CATEGORY_NEWSLETTERS;
  return CATEGORY_EMAILS;
};
const defaultPromptsCategory = {
  OTHER: "Other",
  ADS: "Ads",
  BUSINESS: "Business",
  CHATBOT: "Chatbot",
  COACH: "Coach",
  CODE: "Code",
  COPY: "Copy",
  EMAIL: "Email",
  FINANCE: "Finance",
  WRITING: "Writing",
  FUNNY: "Funny",
  IDEAS: "Ideas",
  LANGUAGE: "Language",
  MARKETING: "Marketing",
  SEO: "SEO"
};
const AI_SHORTCUTS = {
  WITH_SOURCE: {
    keys: "meta+option+j",
    keysRender: ["\u2318", "\u2325", "J"]
  }
};
const AI_ENVIRONMENT = {
  "Selected Text": {
    icon: "CursorText",
    label: "Selected Text"
  },
  "Card Content": {
    icon: "Edit",
    label: "Card Content"
  },
  "Source Content": {
    icon: "Link",
    label: "Source Content"
  },
  "Block Content": {
    icon: "Text2",
    label: "Block Content"
  },
  Highlights: {
    icon: "Marker2",
    label: "Highlights"
  },
  Anything: {
    icon: "Web",
    label: "Anything",
    value: "Just ask AI"
  }
};
const FAKE_AI_ENVIRONMENT = {
  "Selected Text": {
    ...AI_ENVIRONMENT["Selected Text"],
    value: "Some selected Text",
    document: "Some random document for Icon"
  },
  "Card Content": {
    ...AI_ENVIRONMENT["Card Content"],
    value: "Some Card content",
    document: "Some random document for Icon"
  },
  "Block Content": {
    ...AI_ENVIRONMENT["Block Content"],
    value: "Some block",
    document: "Some random document for Icon"
  },
  "Source Content": {
    ...AI_ENVIRONMENT["Source Content"],
    value: "Some source",
    document: "Some random document for Icon"
  },
  Highlights: {
    ...AI_ENVIRONMENT["Highlights"],
    value: "Some source",
    document: "Some random document for Icon"
  },
  Anything: {
    ...AI_ENVIRONMENT["Anything"],
    document: "Some random document for Icon"
  }
};
const noClipNoAiApps = ["Slack", "Linear"];
const CardClipStatus = {
  LATER: "later",
  READ: "read",
  DEFAULT: void 0
};
const CARD_INBOX_TYPE = "inbox";
const CARD_TRASH_TYPE = "trash";
const CARD_CAPTURE_TYPE = "capture";
const CARD_NOTE_TYPE = "note";
const CARD_VIEW_TYPE = "view";
const DEFAULT_CARD_TYPE = CARD_NOTE_TYPE;
const DEFAULT_LAUNCHER_CARD_TYPE = CARD_CAPTURE_TYPE;
const CARD_TASK_TYPE = "task";
const CARD_SOURCES_TYPE = "sources";
const CARD_SOURCE_TYPE = "source";
const CARD_PEOPLE_TYPE = "people";
const CARD_PERSON_TYPE = "person";
const CARD_JOURNAL_TYPE = "journal";
const CARD_DAY_TYPE = "day";
const CARD_SPLIT_TYPE = "split";
const CARD_VIEW_DEFAULT = "default";
const CARD_VIEW_TASKS = "tasks";
const DEFAULT_CARD_TITLE = "Untitled";
const DEFAULT_CARD_TITLE_LENGTH = 46;
const BUCKET_SPECIFIC_CHILD_TYPE = ["meeting", CARD_PERSON_TYPE, CARD_DAY_TYPE, CARD_SOURCE_TYPE];
const BUCKET_TYPE = [
  "list",
  "meetings",
  CARD_VIEW_TYPE,
  CARD_INBOX_TYPE,
  CARD_PEOPLE_TYPE,
  CARD_JOURNAL_TYPE,
  CARD_SOURCES_TYPE
];
const METADATA_CARD_TYPE = ["meetings", CARD_PEOPLE_TYPE, CARD_SOURCES_TYPE];
const CARD_VIEW = [CARD_NOTE_TYPE, CARD_CAPTURE_TYPE, CARD_TASK_TYPE];
const CARD_WITH_CONTENT = [...CARD_VIEW, CARD_DAY_TYPE];
const CARD_WITHOUT_TITLE_IN_TEXT_EDITOR_CONTENT = [CARD_CAPTURE_TYPE, CARD_DAY_TYPE];
const CARD_EDITABLE_TYPE = CARD_VIEW;
const CARD_WITH_EDITOR_VIEW = [...CARD_EDITABLE_TYPE, ...BUCKET_SPECIFIC_CHILD_TYPE];
const CARD_TYPE = [...CARD_EDITABLE_TYPE, ...BUCKET_SPECIFIC_CHILD_TYPE, ...BUCKET_TYPE];
const CARD_WITH_TITLE_IN_TEXT_EDITOR_CONTENT = [
  CARD_NOTE_TYPE,
  CARD_TASK_TYPE,
  ...BUCKET_SPECIFIC_CHILD_TYPE
];
const MOVE_TO_TYPE = [
  ...CARD_EDITABLE_TYPE,
  ...BUCKET_SPECIFIC_CHILD_TYPE,
  CARD_PEOPLE_TYPE,
  "meetings",
  "list",
  CARD_INBOX_TYPE
];
const CARD_UNTITLED = "Untitled";
const STATUS_TRASH = "trash";
const STATUS_DONE = "done";
const STATUS_TODO = "todo";
const CARD_ALL_STATUS = [STATUS_TODO, void 0, null, STATUS_DONE, STATUS_TRASH];
const CARD_STATUS_NO_TRASH = [STATUS_TODO, void 0, STATUS_DONE, null];
const CARD_STATUS_NO_TRASH_NO_DONE = [STATUS_TODO, void 0, null];
const buildCardIRI = (uuid) => `/cards/${uuid}`;
const buildWorkspaceIRI = (uuid) => `/workspaces/${uuid}`;
const isCardType = (value) => CARD_TYPE.includes(value);
const isCardStatus = (value) => CARD_ALL_STATUS.includes(value);
const isLazyBlock = (type) => !isCardType(type);
const includesCardType = (cardTypes, cardType) => {
  return cardTypes.includes(cardType);
};
const DEFAULT_CARD_EMOJI = {
  EMAIL: "\u2709\uFE0F",
  VIDEO: "\u{1F3AC}",
  TWITTER_TWEET: "\u{1F426}",
  TWITTER_THREAD: "\u{1F9F5}",
  ARTICLE: "\u{1F4F0}",
  LINK: "\u{1F517}",
  LINKEDIN_POST: "\u{1F4BC}",
  PROFILE: "\u{1F464}",
  DM: "\u{1F4AC}",
  PDF: "\u{1F4C4}"
};
const NOTES_DEFAULT_VIEW_ID = "default-notes";
const NOTES_LATER_DEFAULT_SPLIT_ID = "default-notes-later";
const TASKS_DEFAULT_VIEW_ID = "default-tasks";
const isDefaultView = (card, id) => {
  var _a, _b;
  return ((_b = (_a = card.metadata) == null ? void 0 : _a.views.default) == null ? void 0 : _b.id) === id;
};
const isDefaultNotesView = (card) => {
  return isDefaultView(card, NOTES_DEFAULT_VIEW_ID);
};
const isDefaultTasksView = (card) => {
  return isDefaultView(card, TASKS_DEFAULT_VIEW_ID);
};
const findCardDefaultView = (cardType, defaultViews) => {
  const CARD_TYPE_TO_DEFAULT_VIEW_ID = {
    [CARD_NOTE_TYPE]: NOTES_DEFAULT_VIEW_ID,
    [CARD_CAPTURE_TYPE]: NOTES_DEFAULT_VIEW_ID,
    [CARD_TASK_TYPE]: TASKS_DEFAULT_VIEW_ID
  };
  const defaultViewId = CARD_TYPE_TO_DEFAULT_VIEW_ID[cardType];
  return defaultViews.find((defaultView) => isDefaultView(defaultView, defaultViewId));
};
const allCardsQuery = {
  queryTypes: [...CARD_EDITABLE_TYPE, ...BUCKET_SPECIFIC_CHILD_TYPE],
  queryStatus: CARD_STATUS_NO_TRASH,
  "sort[creationDate]": "desc"
};
const All_CARD_SHORTCUT = ["G", "E"];
const SUPERHUMAN_APP_NAME = "Superhuman";
const SLACK_APP_NAME = "Slack";
const LAZY_APP_NAME = "Lazy";
const ImageAttachmentTypesWhiteListObj = {
  "image/jpeg": 1,
  "image/jpg": 1,
  "image/png": 1,
  "image/gif": 1,
  "image/vnd.microsoft.icon": 1,
  "image/svg+xml": 1
};
const ImageAttachmentTypesWhiteListObjList = Object.keys(
  ImageAttachmentTypesWhiteListObj
);
const PDFAttachmentTypesWhiteListObj = {
  "application/pdf": 1
};
const AudioAttachmentTypesWhiteListObj = {
  "audio/mpeg": 1
};
const FileAttachmentTypesWhiteListObj = {
  "text/html": 1,
  "text/rtf": 1,
  "text/plain": 1,
  "application/zip": 1,
  "application/msword": 1,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": 1,
  "text/csv": 1,
  ...PDFAttachmentTypesWhiteListObj,
  "application/vnd.oasis.opendocument.text": 1,
  "application/vnd.oasis.opendocument.spreadsheet": 1,
  "application/vnd.oasis.opendocument.presentation": 1,
  "application/vnd.ms-excel": 1,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": 1,
  "application/vnd.ms-powerpoint": 1,
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": 1,
  "application/vnd.ms-access": 1,
  "application/vnd.ms-project": 1,
  "application/vnd.ms-works": 1,
  "application/vnd.ms-excel.sheet.macroEnabled.12": 1,
  "application/vnd.ms-excel.sheet.binary.macroEnabled.12": 1,
  "application/vnd.ms-powerpoint.presentation.macroEnabled.12": 1,
  "application/vnd.ms-powerpoint.slideshow.macroEnabled.12": 1,
  "application/vnd.ms-powerpoint.template.macroEnabled.12": 1,
  "application/vnd.ms-word.document.macroEnabled.12": 1,
  "application/vnd.ms-word.template.macroEnabled.12": 1,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template": 1,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template": 1,
  "application/x-text": 1,
  "application/vnd.apple.pages": 1,
  "application/vnd.apple.numbers": 1,
  "application/vnd.apple.keynote": 1,
  "application/vnd.google-apps.document": 1,
  "application/vnd.google-apps.spreadsheet": 1,
  "application/vnd.google-apps.presentation": 1,
  "application/vnd.google-apps.drawing": 1,
  "application/vnd.google-apps.script": 1,
  "application/vnd.google-apps.form": 1,
  "application/vnd.google-apps.fusiontable": 1,
  "application/vnd.wordperfect": 1,
  ...AudioAttachmentTypesWhiteListObj
};
const VideoAttachmentTypesWhiteListObj = {
  "video/mp4": 1,
  "video/mpeg": 1,
  "video/quicktime": 1,
  "video/webm": 1,
  "video/x-msvideo": 1,
  "video/x-flv": 1,
  "video/ogg": 1
};
const VideoAttachmentTypesWhiteListObjList = Object.keys(
  VideoAttachmentTypesWhiteListObj
);
const buildAttachmentIRI = (uuid) => `/attachments/${uuid}`;
const AttachmentTypesWhiteListObj = {
  ...FileAttachmentTypesWhiteListObj,
  ...ImageAttachmentTypesWhiteListObj,
  ...VideoAttachmentTypesWhiteListObj,
  ...AudioAttachmentTypesWhiteListObj
};
const AttachmentTypesWhiteList = Object.keys(AttachmentTypesWhiteListObj);
const AttachmentTypesBlackListObj = { "image/example": 1 };
const AttachmentTypesBlackList = Object.keys(AttachmentTypesBlackListObj);
const AttachmentMaxFileSize = 1e8;
const BlockTypes = {
  BLOCK_LINE: "blockLine",
  BLOCKQUOTE: "blockquote",
  BLOCK_COLUMN: "blockColumn",
  BULLETED_LIST: "bulletedList",
  CARD_CONTENT: "seshatCardContent",
  CARD_LINK: "seshatCardLink",
  CODE: "codeBlock",
  HEADING_1: "heading1",
  HEADING_2: "heading2",
  HEADING_3: "heading3",
  HEADING_4: "heading4",
  PARAGRAPH: "paragraph",
  HIGHLIGHT: "highlight",
  HIGHLIGHTS: "highlights",
  HIGHLIGHT_NOTE: "highlightNote",
  NODE_VIEW: "seshatNodeViewContent",
  NUMBERED_LIST: "numberedList",
  NOTE_EDITABLE: "seshatNoteEditable",
  TASK_EDITABLE: "seshatTaskEditable",
  TWEET: "tweet",
  CAPTURE: "capture",
  CLIP_CONTENT: "clipContent",
  CLIP_CONTENT_TITLE: "clipContentTitle"
};
const ClipBookmarksTypes = { PDF: "pdf" };
const NodeViewTypes = {
  CARD_LINK: "cardLinkComponent",
  CLIP_BOOK: "clipBook",
  CLIP_BOOKMARK: "clipBookmark",
  FILE: "file",
  IFRAME: "iframe",
  HORIZONTAL_RULE: "horizontalRule",
  TABLE_OF_CONTENTS: "tableOfContents",
  EXTERNAL_VIDEO: "externalVideo"
};
const BlockAttrsParams = {
  BLOCK_UUID: "block-id",
  CONTEXT_UUID: "context-uuid",
  HIGHLIGHT_UUID: "highlightId",
  HIGHLIGHT_NOTE: "highlightNote"
};
const breakpoints = {
  sm: 800,
  md: 1300,
  lg: 1800,
  xl: 2300
};
const mediaQueries = (key) => {
  return (style) => `@media (max-width: ${breakpoints[key]}px) { ${style} }`;
};
const browserAllowed = [
  "Google Chrome",
  "Google\xA0Chrome",
  "Microsoft Edge",
  "Opera",
  "Brave Browser",
  "Brave Browser Nightly",
  "Google Chrome Canary",
  "Chromium",
  "Tor Browser",
  "Vivaldi",
  "Google Chrome Dev",
  "Google Chrome Beta"
];
const safariBrowser = ["Safari Technology Preview", "Safari", "Webkit", "Orion", "Orion RC"];
const ArcBrowser = ["Arc", "SigmaOS"];
const browserList = [...browserAllowed, ...safariBrowser, ...ArcBrowser];
const BUCKETS_CHILDREN_OBJECT_TYPE = {
  meeting: { parentType: "meetings" },
  person: { parentType: "people" },
  source: { parentType: "sources" },
  day: { parentType: "journal" }
};
const BUCKETS = Object.keys(BUCKETS_CHILDREN_OBJECT_TYPE).reduce(
  (result, bucketKey) => ({
    ...result,
    [BUCKETS_CHILDREN_OBJECT_TYPE[bucketKey].parentType]: { childType: bucketKey }
  }),
  {}
);
function getChildTypeForCardType(cardType) {
  const bucket = BUCKETS[cardType];
  return bucket == null ? void 0 : bucket.childType;
}
const NOT_REAL_CARD_UUID = {
  ALL_CARDS: "allcards",
  TRASH: "trash",
  SEARCH: "search"
};
const NOT_REAL_CARD_UUIDS = Object.values(NOT_REAL_CARD_UUID);
const COLOR = {
  AUTHOR_LABEL: "#A17448"
};
const LAUNCHER_EDITOR_ID = "launcher_id";
const LazyPerformanceNames = {
  pageChange: "PAGE_CHANGE",
  accessVault: "ACCESS_VAULT"
};
const LOCAL_DATA_MIGRATION_VERSION = 5;
const MarkTypes = {
  HIGHLIGHT: "highlight",
  LINK: "link",
  TEXT_STYLE: "textStyle",
  BOLD: "bold",
  ITALIC: "italic",
  STRIKE: "strike",
  CODE: "code",
  TEXT_ALIGN: "textAlign",
  FONT_COLOR: "fontColor",
  SHINE: "shine",
  UNDERLINE: "underline"
};
const MarkAttrsParams = {
  HIGHLIGHT_UUID: BlockAttrsParams.HIGHLIGHT_UUID,
  HIGHLIGHT_NOTE: BlockAttrsParams.HIGHLIGHT_NOTE,
  HIGHLIGHT_COLOR: "color"
};
const arrayMove = ({ array, from, to }) => {
  array.splice(to, 0, array.splice(from, 1)[0]);
  return array;
};
const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
const isCoreCardView = (cardView) => {
  return !isTypeFilterCardView(cardView);
};
const debounce = (fn, delay2) => {
  let timeoutID = null;
  return function(...args) {
    if (timeoutID)
      clearTimeout(timeoutID);
    timeoutID = window.setTimeout(() => {
      var _a;
      return (_a = fn == null ? void 0 : fn.apply) == null ? void 0 : _a.call(fn, this, args);
    }, delay2 || 100);
  };
};
const debug = (namespace) => (formatter, args) => {
  const debug2 = npmDebug(namespace);
  debug2.log = console.log.bind(console);
  args ? debug2(formatter, args) : debug2(formatter);
};
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const escapeRegexMetaCharacter = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const isFigmaUrl = (href) => /https:\/\/([\w.-]+\.)?figma.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/.test(href);
const getFigmaEmbed = (href) => `https://www.figma.com/embed?embed_host=lazyapp&url=${href}`;
const isWhitespaceChar = (char) => new RegExp(/\s/).test(char);
const getWordPositions = (str) => {
  const strlen = str.length;
  const result = [];
  let lastIndex = 0;
  let currentIndex = 0;
  let whitespaceSequenceStarted = false;
  while (currentIndex < strlen) {
    const isAlphanumericChar = !isWhitespaceChar(str.charAt(currentIndex));
    if (!whitespaceSequenceStarted && isAlphanumericChar)
      currentIndex += 1;
    if (!whitespaceSequenceStarted && !isAlphanumericChar)
      whitespaceSequenceStarted = true;
    if (whitespaceSequenceStarted && !isAlphanumericChar)
      currentIndex += 1;
    if (whitespaceSequenceStarted && isAlphanumericChar) {
      result.push({
        start: lastIndex,
        end: currentIndex - 1,
        len: currentIndex - lastIndex - 1
      });
      lastIndex = currentIndex;
      whitespaceSequenceStarted = false;
    }
    if (currentIndex === strlen) {
      result.push({
        start: lastIndex,
        end: currentIndex,
        len: currentIndex - lastIndex
      });
    }
  }
  return result;
};
const getSimilarity = (str1, str2) => {
  if (str1 === str2)
    return 0;
  const str1len = str1.length;
  const str2len = str2.length;
  if (str1len === 0)
    return str2len;
  if (str2len === 0)
    return str1len;
  const prevRow = [];
  const str2Char = [];
  let curCol;
  let nextCol;
  let tmp;
  for (let i = 0; i < str2len; i += 1) {
    prevRow[i] = i;
    str2Char[i] = str2.charCodeAt(i);
  }
  prevRow[str2len] = str2len;
  let strCmp;
  let j;
  for (let i = 0; i < str1len; i += 1) {
    nextCol = i + 1;
    for (j = 0; j < str2len; j += 1) {
      curCol = nextCol;
      strCmp = str1.charCodeAt(i) === str2Char[j];
      nextCol = prevRow[j] + (strCmp ? 0 : 1);
      tmp = curCol + 1;
      if (nextCol > tmp) {
        nextCol = tmp;
      }
      tmp = prevRow[j + 1] + 1;
      if (nextCol > tmp) {
        nextCol = tmp;
      }
      prevRow[j] = curCol;
    }
    prevRow[j] = nextCol;
  }
  return nextCol;
};
const findSimilarSubstring = (string, substring) => {
  const str1len = string.length;
  const str2len = substring.length;
  if (str1len > 850)
    return void 0;
  if (str1len <= str2len) {
    const levenshteinDistance = getSimilarity(string, substring);
    return {
      start: 0,
      end: str1len - 1,
      len: str1len,
      levenshtein: levenshteinDistance,
      accuracy: levenshteinDistance ? (str2len - levenshteinDistance) / str2len : 0
    };
  }
  const wordPositions = getWordPositions(string);
  const wordPositionsLen = wordPositions.length;
  const best = {
    start: 0,
    end: 0,
    len: 0,
    levenshtein: void 0,
    accuracy: 0
  };
  for (let i = 0; i < wordPositionsLen; i += 1) {
    for (let j = i; j < wordPositionsLen; j += 1) {
      const partialString = string.substring(wordPositions[i].start, wordPositions[j].end + 1);
      const levenshtein = getSimilarity(partialString, substring);
      if (best.levenshtein === void 0 || levenshtein && levenshtein < best.levenshtein) {
        best.levenshtein = levenshtein;
        best.start = wordPositions[i].start;
        best.end = wordPositions[j].end;
        best.len = best.end - best.start;
      }
    }
  }
  best.accuracy = best.levenshtein ? (str2len - best.levenshtein) / str2len : 0;
  return best;
};
const normalizeString = (string) => string.replace(/\r\n|\r/g, "\n").replace(/\u2424/g, "\n").replace(/\u00a0/g, " ");
const findExactSubstring = (string, substring) => {
  const cleanedSubstring = normalizeString(substring);
  const cleanedString = normalizeString(string);
  const escapedSubstring = escapeRegexMetaCharacter(cleanedSubstring);
  const exactMatch = cleanedString.match(escapedSubstring);
  return (exactMatch == null ? void 0 : exactMatch.index) !== void 0 ? {
    start: exactMatch.index,
    end: exactMatch.index + exactMatch[0].length,
    len: exactMatch[0].length,
    accuracy: 1,
    levenshtein: 0
  } : void 0;
};
const findSubstringOffset = (string, substring) => {
  var _a;
  return (_a = findExactSubstring(string, substring)) != null ? _a : findSimilarSubstring(string, substring);
};
const uuidV5$2 = (workspaceUuid, type = "") => v5$1(workspaceUuid + type, v1$1());
const uuidInterface = uuidV5$2;
const uuidV1Interface = v1$1;
const uuidForBlockMigrationInterface = uuidV5$2;
const uuidV1$1 = uuidV1Interface;
const uuidV5$1 = uuidInterface;
const uuidForBlockMigration$1 = uuidForBlockMigrationInterface;
const generateRandomId = () => uuidV5$1(Math.floor(Math.random() * 4294967295).toString());
const isInstagramPost = (url) => {
  return /((?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel)\/([^/?#&]+)).*/g.test(url);
};
const getInstagramEmbed = (url) => {
  const id = url.match(
    /(?:https?:\/\/)?(?:www.)?instagram.com\/?([a-zA-Z0-9._-]+)?\/([p]+)?([reel]+)?([tv]+)?([stories]+)?\/([a-zA-Z0-9\-_.]+)\/?([0-9]+)?/
  );
  if (!id)
    return url;
  return `https://www.instagram.com/p/${id[6]}/embed`;
};
function isDefined(value) {
  return typeof value !== "undefined";
}
const isEqual = _.isEqual;
const isEmoji = (string) => {
  if (!string)
    return false;
  const regex = /^\p{Emoji}|\p{Extended_Pictographic}+$/gu;
  const regexWord = /\w/g;
  const match = regex.exec(string);
  const matchWord = regexWord.exec(string);
  return !!(match == null ? void 0 : match.length) && !(matchWord == null ? void 0 : matchWord.length) && (match == null ? void 0 : match.input.length) === string.length;
};
const isNoClipNoAiApp = (href) => noClipNoAiApps.some((app) => href.toLowerCase().includes(app.toLowerCase()));
const sanitizeSlackEmoji = (doc) => {
  let html = doc;
  if (typeof doc === "string") {
    html = document.createElement("div");
    html.innerHTML = doc;
  }
  if (typeof html === "string")
    return;
  [...html.querySelectorAll('img[src*="a.slack-edge.com"]')].forEach((child) => {
    var _a;
    const src = decodeURIComponent(child.getAttribute("src"));
    const emoji = src == null ? void 0 : src.split("/").pop();
    if ((child == null ? void 0 : child.tagName) === "IMG" && emoji) {
      (_a = child.parentElement) == null ? void 0 : _a.replaceChild(
        document.createTextNode(String.fromCodePoint(parseInt(emoji, 16)) + " "),
        child
      );
    }
  });
  return html.innerHTML;
};
const sanitizeFacebookEmoji = (doc) => {
  let html = doc;
  if (typeof doc === "string") {
    html = document.createElement("div");
    html.innerHTML = doc;
  }
  if (typeof html === "string")
    return;
  [...html.querySelectorAll('img[src*="static.xx.fbcdn.net/images/emoji.php"]')].forEach(
    (child) => {
      var _a;
      const emoji = child.getAttribute("alt");
      if ((child == null ? void 0 : child.tagName) === "IMG" && emoji) {
        (_a = child.parentElement) == null ? void 0 : _a.replaceChild(document.createTextNode(emoji), child);
      }
    }
  );
  return html.innerHTML;
};
const isEqualWithUndefinedValues = (a, b) => _.isEqualWith(a, b, comparisonFunc);
const comparisonFunc = (a, b) => {
  if (_.isArray(a) || _.isArray(b))
    return;
  if (!_.isObject(a) || !_.isObject(b))
    return;
  if (!_.includes(a, void 0) && !_.includes(b, void 0))
    return;
  return _.isEqualWith(
    _.omitBy(a, (value) => value === void 0),
    _.omitBy(b, (value) => value === void 0),
    comparisonFunc
  );
};
const KNOWN_PAYWALL_SITES = [
  "adelaidenow.com.au",
  "barrons.com",
  "couriermail.com.au",
  "fd.nl",
  "genomeweb.com",
  "heraldsun.com.au",
  "lavoixdunord.fr",
  "ntnews.com.au",
  "quora.com",
  "seekingalpha.com",
  "telegraph.co.uk",
  "theaustralian.com.au",
  "themercury.com.au",
  "thenational.scot",
  "wsj.com",
  "kansascity.com",
  "republic.ru",
  "nzz.ch",
  "handelsblatt.com",
  "df.cl",
  "wired.com",
  "zeit.de",
  "ad.nl",
  "bd.nl",
  "bloombergquint.com",
  "bndestem.nl",
  "brisbanetimes.com.au",
  "canberratimes.com.au",
  "cen.acs.org",
  "demorgen.be",
  "denverpost.com",
  "destentor.nl",
  "ed.nl",
  "examiner.com.au",
  "gelderlander.nl",
  "globes.co.il",
  "grubstreet.com",
  "harpers.org",
  "hbr.org",
  "humo.be",
  "lesechos.fr",
  "mercurynews.com",
  "newstatesman.com",
  "nrc.nl",
  "nymag.com",
  "ocregister.com",
  "shz.de",
  "pzc.nl",
  "qz.com",
  "scientificamerican.com",
  "seattletimes.com",
  "sofrep.com",
  "spectator.co.uk",
  "speld.nl",
  "telegraaf.nl",
  "theadvocate.com.au",
  "theage.com.au",
  "theatlantic.com",
  "thecut.com",
  "thediplomat.com",
  "tubantia.nl",
  "vn.nl",
  "vulture.com",
  "gelocal.it",
  "lrb.co.uk",
  "parool.nl",
  "techinasia.com",
  "the-american-interest.com",
  "trouw.nl",
  "volkskrant.nl",
  "thehindu.com",
  "financialpost.com",
  "haaretz.co.il",
  "haaretz.com",
  "themarker.com",
  "sueddeutsche.de",
  "elmundo.es",
  "time.com",
  "expansion.com",
  "dailytelegraph.com.au"
];
const PAYWALL_RELATED_TERMS = ["paywall", "roadblock", "gateway"];
const isPaywallUrl = (url) => {
  if (!url)
    return void 0;
  try {
    const _url = new URL(url);
    return KNOWN_PAYWALL_SITES.some((SITE_URL) => _url.host.includes(SITE_URL));
  } catch (e) {
    return void 0;
  }
};
const containsPaywallRelatedHTML = (html) => {
  if (!html)
    return void 0;
  const doc = new DOMParser().parseFromString(html, "text/html");
  if (doc.querySelector('a[href*="------post_regwall-----------"]'))
    return true;
  if (doc.getElementById("article-trial-barrier"))
    return true;
  return PAYWALL_RELATED_TERMS.some((term) => {
    var _a, _b, _c;
    if ((_a = doc.querySelector(`[class*="${term}"]`)) == null ? void 0 : _a.textContent)
      return true;
    if ((_b = doc.querySelector(`[id*="${term}"]:not(svg)`)) == null ? void 0 : _b.textContent)
      return true;
    if ((_c = doc.querySelector(`[data-target-id*="${term}"]`)) == null ? void 0 : _c.textContent)
      return true;
    return false;
  });
};
const isPaywallContent = ({ url, html }) => {
  if (!html || !url)
    return void 0;
  return isPaywallUrl(url) || containsPaywallRelatedHTML(html);
};
const isSlackUrl = (href) => /^https?:\/\/(app\.slack)\.com/i.test(href);
const maybeConvertAlphabeticKey = (e) => {
  const code = e.code;
  return code.startsWith("Key") ? code.replace("Key", "").toLowerCase() : e.key;
};
const keyboardEventToString = (e) => {
  let string = "";
  if (e.metaKey)
    string = string.concat("mod");
  if (e.shiftKey)
    string = string.concat(!string ? "shift" : "-shift");
  if (e.altKey)
    string = string.concat(!string ? "alt" : "-alt");
  if (e.code) {
    const key = maybeConvertAlphabeticKey(e);
    string = string.concat(!string ? key : `-${key}`);
  }
  return string;
};
const generateMarkText = (text) => {
  return { type: "text", text };
};
const generateText = (text) => {
  if (!text)
    return [];
  return [generateMarkText(text)];
};
const pipe = (...fns) => (value) => fns.reduce((acc, fn) => fn(acc), value);
const preStringToHtml = (string) => {
  return string.split(/\r\n|\r|\n/gi).map((line) => line ? "<p>" + line + "</p>" : "</br>").join("");
};
const isYoutubeAPI = (url) => url.includes("youtube.com/api");
const isYoutubeVideo = (url) => url.includes("youtube.com");
const isTwitter = (url) => url.includes("twitter.com");
const isLinkedinDM = (url) => url.includes("linkedin.com/messaging/thread");
const isWhatsappWeb = (url) => url.includes("web.whatsapp");
const isSuperhuman = (url) => url.includes("mail.superhuman");
const isGmail = (url) => url.includes("mail.google.com");
const isLinkedinUrl = (url) => url.includes("linkedin.com");
const isGithubUrl = (url) => url.includes("github.com");
const isRedditUrl = (url) => url.includes("reddit.com");
const isInstagramVideoOrPicture = (url) => url.includes("instagram.com/p");
const isIMDBVideo = (url) => url.includes("imdb.com/video");
const isVimeoVideo = (url) => url.match(
  /(http|https)?:\/\/(www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/
);
const isTiktokVideo = (url) => url.match(/tiktok.com\/@([^/]+)\/video\/(\d{19})/);
const isDailymotionVideo = (url) => url.includes("dailymotion.com/video");
const isFacebookVideo = (url) => url.match(/facebook.com\/(?:\/(\d+))?\/videos(?:\/.*?)?\/(\d+)/i);
const isNetflixVideo = (url) => url.includes("netflix.com/watch");
const isFacebookMessenger = (url) => url.includes("facebook.com/messages/t");
const illegalRe = /[\/\?<>\\:\*\|":]/g;
const controlRe = /[\x00-\x1f\x80-\x9f]/g;
const reservedRe = /^\.+$/;
const windowsReservedRe = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(\..*)?$/i;
const sanitizeForFileName = (string, replacement = "") => {
  return string.replace(illegalRe, replacement).replace(controlRe, replacement).replace(reservedRe, replacement).replace(windowsReservedRe, replacement).substring(0, 200);
};
const getNameAndExtFromFileName = (string) => {
  var _a;
  const file = (_a = string.split("/").pop()) != null ? _a : string;
  return {
    name: file.substring(0, file.lastIndexOf(".")),
    ext: file.substring(file.lastIndexOf(".") + 1, file.length)
  };
};
const tryResolvingPromiseWithinMs = (promise, ms) => {
  const timeout = new Promise((_2, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject("Timed out in " + ms + "ms.");
    }, ms);
  });
  return Promise.race([promise, timeout]);
};
const isViewHeaderMultiSelectFilter = (filter) => {
  return filter.type === "multiSelect";
};
const isViewHeaderDateFilter = (filter) => {
  return filter.type === "date";
};
const interpolatePromptParameters = ({
  promptEnv,
  parametersLabel
}) => !!(promptEnv == null ? void 0 : promptEnv.find((env) => env === parametersLabel));
const isAppleBookUrl = (url) => url.startsWith("file:///System/Applications/Books.app") || url.startsWith("https://books.apple.com/");
var lazyCommandGroups = /* @__PURE__ */ ((lazyCommandGroups2) => {
  lazyCommandGroups2["RECOMMENDED"] = "Recommended";
  lazyCommandGroups2["SOCIAL"] = "Social";
  lazyCommandGroups2["NAVIGATION"] = "Navigation";
  return lazyCommandGroups2;
})(lazyCommandGroups || {});
const CommandGroups = {
  ["Recommended"]: {
    title: "Recommended",
    position: 2e3
  },
  ["Social"]: {
    title: "Socials",
    position: 3e3
  },
  ["Navigation"]: {
    title: "Navigation",
    position: 4e3
  }
};
const contactCommand = {
  twitter: {
    id: "twitter",
    name: "Visit Twitter",
    search: "visit twitter",
    icon: "\u{1F426}",
    group: CommandGroups["Social"].title,
    position: CommandGroups["Social"].position + 50,
    callback: (entry) => {
      if (isTwitterUrl(entry.value))
        return window.open(entry.value, "_blank");
      return window.open(`https://www.x.com/${entry.value}`, "_blank");
    }
  },
  youtube: {
    id: "youtube",
    name: "Visit Youtube",
    search: "visit Youtube",
    icon: "\u{1F3AC}",
    group: CommandGroups["Social"].title,
    position: CommandGroups["Social"].position + 50,
    callback: (entry) => {
      if (isYoutubeUrl(entry.value))
        return window.open(entry.value, "_blank");
      const formattedYoutubeHandler = _.deburr(entry.value).replace(/ /g, "");
      return window.open(`https://www.youtube.com/@${formattedYoutubeHandler}`, "_blank");
    }
  },
  linkedin: {
    id: "linkedin",
    name: "Visit Linkedin",
    search: "visit Linkedin",
    icon: "\u{1F4BC}",
    group: CommandGroups["Social"].title,
    position: CommandGroups["Social"].position + 50,
    callback: (entry) => {
      if (isLinkedinUrl(entry.value))
        return window.open(entry.value, "_blank");
      return window.open(`https://www.linkedin.com/in/${entry.value}`, "_blank");
    }
  },
  github: {
    id: "github",
    name: "Visit GitHub",
    search: "visit GitHub",
    icon: "\u{1F419}",
    group: CommandGroups["Social"].title,
    position: CommandGroups["Social"].position + 50,
    callback: (entry) => {
      if (isGithubUrl(entry.value))
        return window.open(entry.value, "_blank");
      return window.open(`https://www.github.com/${entry.value}`, "_blank");
    }
  },
  custom: {
    id: "custom",
    name: "Visit personal website",
    search: "visit personal website",
    icon: "\u{1F517}",
    group: CommandGroups["Social"].title,
    position: CommandGroups["Social"].position + 50,
    callback: (entry) => {
      const formattedUrl = !/^https?:\/\//i.test(entry.value) ? `https://${entry.value}` : entry.value;
      window.open(formattedUrl, "_blank");
    }
  }
};
const REFERENCE_HASH_PLACEHOLDER = "reference_hash";
const REFERENCE_BRACKET_PLACEHOLDER = "reference_bracket";
const REFERENCE_TE_ITEM = { name: "reference", attrsId: "cardUuid" };
const MENTION_PLACEHOLDER = " Link to card";
const DEFAULT_REFERENCE = {
  char: "[[",
  name: REFERENCE_BRACKET_PLACEHOLDER,
  placeholder: MENTION_PLACEHOLDER,
  icon: "SEARCH",
  searchField: "title",
  fieldMentionId: "uuid"
};
const HASH_REFERENCE = {
  char: "#",
  name: REFERENCE_HASH_PLACEHOLDER,
  placeholder: MENTION_PLACEHOLDER,
  icon: "HASHTAG",
  searchField: "title",
  fieldMentionId: "uuid"
};
const SEARCH_PERSON_BY_DESCRIPTOR_ID = [
  "name",
  "email",
  "twitter",
  "youtube",
  "phone",
  "github",
  "skype",
  "instagram",
  "spotify",
  "linkedin",
  "urls",
  "whatsApp"
];
const SEARCH_SOURCE_BY_DESCRIPTOR_ID = ["link", "internalLink"];
const extractAlwaysShowProperties = (propertiesDescriptor) => Object.keys(propertiesDescriptor).filter(
  (id) => propertiesDescriptor[id]
);
const defaultPropertiesDescriptorPerson = {
  whatsApp: { id: "whatsApp", name: "WhatsApp", type: "multi_text", position: 50 },
  name: { id: "name", name: "Name", type: "multi_text", isHidden: () => true },
  picture: { id: "picture", name: "Picture", type: "multi_text", isHidden: () => true },
  twitter: {
    id: "twitter",
    name: "Twitter",
    type: "multi_link",
    generateLink: ({ value }) => getTwitterUrl(value),
    position: 20
  },
  instagram: {
    id: "instagram",
    name: "Instagram",
    type: "multi_link",
    generateLink: ({ value }) => "https://www.instagram.com/" + value,
    position: 20
  },
  youtube: {
    id: "youtube",
    name: "Youtube",
    type: "multi_link",
    generateLink: ({ metadata }) => {
      var _a, _b, _c, _d, _e, _f;
      return (_f = (_e = (_b = (_a = metadata == null ? void 0 : metadata.data) == null ? void 0 : _a.youtube) == null ? void 0 : _b.url) != null ? _e : (_d = (_c = metadata == null ? void 0 : metadata.propertiesValues.youtube) == null ? void 0 : _c.values) == null ? void 0 : _d[0]) != null ? _f : void 0;
    },
    position: 40
  },
  github: {
    id: "github",
    name: "Github",
    type: "multi_link",
    generateLink: ({ metadata }) => {
      var _a, _b, _c, _d, _e, _f;
      return (_f = (_e = (_b = (_a = metadata == null ? void 0 : metadata.data) == null ? void 0 : _a.github) == null ? void 0 : _b.url) != null ? _e : (_d = (_c = metadata == null ? void 0 : metadata.propertiesValues.github) == null ? void 0 : _c.values) == null ? void 0 : _d[0]) != null ? _f : void 0;
    },
    position: 45
  },
  slack: {
    id: "slack",
    name: "Slack",
    type: "multi_link",
    generateLink: ({ metadata, value }) => {
      var _a, _b, _c, _d, _e, _f, _g;
      return (_g = (_f = (_c = (_b = (_a = metadata == null ? void 0 : metadata.data) == null ? void 0 : _a.slack) == null ? void 0 : _b[value]) == null ? void 0 : _c.url) != null ? _f : (_e = (_d = metadata == null ? void 0 : metadata.propertiesValues.slack) == null ? void 0 : _d.values) == null ? void 0 : _e[0]) != null ? _g : void 0;
    },
    position: 60
  },
  urls: {
    id: "urls",
    name: "Urls",
    type: "multi_link",
    generateLink: ({ value }) => value
  },
  phone: { id: "phone", name: "Phone", type: "multi_text", position: 3 },
  skype: { id: "skype", name: "Skype", type: "multi_text", position: 60 },
  spotify: { id: "spotify", name: "Spotify", type: "multi_text", position: 70 },
  linkedin: {
    id: "linkedin",
    name: "Linkedin",
    type: "multi_link",
    generateLink: ({ metadata }) => {
      var _a, _b, _c, _d, _e, _f;
      return (_f = (_e = (_b = (_a = metadata == null ? void 0 : metadata.data) == null ? void 0 : _a.linkedin) == null ? void 0 : _b.url) != null ? _e : (_d = (_c = metadata == null ? void 0 : metadata.propertiesValues.linkedin) == null ? void 0 : _c.values) == null ? void 0 : _d[0]) != null ? _f : void 0;
    },
    position: 30
  },
  email: {
    id: "email",
    name: "Email",
    type: "multi_link",
    generateLink: ({ value }) => emailToUrl(value),
    position: 2
  },
  circleSo: {
    id: "circleSo",
    name: "Circle.so",
    type: "multi_link",
    generateLink: ({ value }) => value,
    position: 3
  },
  groups: { id: "groups", name: "Group", type: "multi_select", alwaysShow: true, position: 1 },
  publication: { id: "publication", name: "Publication", type: "multiple_connection" }
};
const defaultPropertiesDescriptorPersonToAlwaysShow = extractAlwaysShowProperties(defaultPropertiesDescriptorPerson);
const defaultPropertiesDescriptorSource = {
  link: {
    id: "link",
    name: "Link",
    type: "uniq_link",
    generateLink: ({ value }) => value,
    position: 3,
    isHidden: ({ id, values }) => {
      if (!values)
        return false;
      return id === "link" && !!(values == null ? void 0 : values.find((value) => isPDFUrl(value) && (value.startsWith("file://") || value.startsWith("/"))));
    }
  },
  internalLink: {
    id: "internalLink",
    name: "Internal link",
    type: "uniq_link",
    generateLink: ({ value }) => value,
    position: 3
  },
  linkAnchor: {
    id: "linkAnchor",
    name: "LinkAnchor",
    type: "multi_link",
    isHidden: () => true,
    position: 4
  },
  image: {
    id: "image",
    name: "Image",
    type: "uniq_link",
    isHidden: () => true,
    generateLink: ({ value }) => value,
    position: 5
  },
  author: {
    id: "author",
    name: "Author",
    type: "multiple_connection",
    cardTypes: ["person"],
    alwaysShow: true,
    position: 6
  },
  category: {
    id: "category",
    name: "Category",
    type: "multi_select",
    alwaysShow: true,
    position: 2,
    options: Object.values(CATEGORY_NAMES).map((category) => ({ id: category, name: category }))
  },
  publishedDate: {
    id: "publishedDate",
    name: "Published",
    type: "date",
    position: 1
  }
};
const defaultPropertiesDescriptorSourceToAlwaysShow = extractAlwaysShowProperties(defaultPropertiesDescriptorSource);
const defaultPropertiesDescriptorMeeting = {
  attendee: { id: "attendee", name: "Attendee", type: "multiple_connection" }
};
const defaultPropertiesDescriptorCard = {
  person: {
    id: "person",
    name: "Person",
    type: "uniq_connection",
    cardTypes: ["person"]
  },
  source: {
    id: "source",
    name: "Source",
    type: "uniq_connection",
    cardTypes: ["source"]
  },
  meeting: {
    id: "meeting",
    name: "Meeting",
    type: "uniq_connection",
    cardTypes: ["meeting"]
  }
};
const getDefaultPropsByCardType = (cardType) => {
  if (cardType === "source")
    return defaultPropertiesDescriptorSourceToAlwaysShow;
  if (cardType === "person")
    return defaultPropertiesDescriptorPersonToAlwaysShow;
  return [];
};
const isDescriptorLinkToACard = (type) => type === "uniq_connection" || type === "multiple_connection";
const isDescriptorLinkToExternal = (type) => type === "multi_link" || type === "uniq_link";
const isMultipleValue = (type) => type === "multi_link" || type === "multiple_connection";
const allDefaultPropertiesDescriptors = {
  ...defaultPropertiesDescriptorPerson,
  ...defaultPropertiesDescriptorSource,
  ...defaultPropertiesDescriptorMeeting,
  ...defaultPropertiesDescriptorCard
};
const getDefaultDescriptors = (type) => {
  if (type === "person") {
    return Object.values(defaultPropertiesDescriptorPerson);
  }
  if (type === "source") {
    return Object.values(defaultPropertiesDescriptorSource);
  }
  if (isCardTypeWithContext(type)) {
    return Object.values(defaultPropertiesDescriptorCard);
  }
  return [];
};
const isCardTypeWithContext = (type) => type === "task" || type === "note" || type === "capture" || type === "day";
const isCardTypeWithProperties = (type) => type === "person" || type === "source";
function getCardDescriptors(cards, uuid, initialDescriptors = [], initialTraversedUuids = []) {
  var _a, _b;
  if (initialTraversedUuids.includes(uuid)) {
    return initialDescriptors;
  }
  const card = cards[uuid];
  if (!card) {
    return initialDescriptors;
  }
  const traversedUuids = initialTraversedUuids.concat([uuid]);
  const cardDescriptors = Object.values((_b = (_a = card.metadata) == null ? void 0 : _a.propertiesDescriptors) != null ? _b : {}).filter((descriptor) => !!descriptor).concat(getDefaultDescriptors(card.type));
  const allDescriptors = uniqBy(initialDescriptors.concat(cardDescriptors), (descriptor) => descriptor.id);
  if (card.parentCardUuid) {
    return getCardDescriptors(cards, card.parentCardUuid, allDescriptors, traversedUuids);
  }
  return allDescriptors;
}
const getReferencePrefix = (char) => char === "#" ? "#" : void 0;
const DEFAULT_TRUE = true;
const DEFAULT_FALSE = false;
const DEFAULT_ARRAY = [];
const DEFAULT_OBJECT = {};
const DEFAULT_UNDEFINED = void 0;
const DEFAULT_NOOP = () => {
};
const SeshatCssClass = {
  BLOCK: "seshat_block",
  BLOCK_WRAPPER: "seshat_block_wrapper",
  BLOCK_CONTENT: "seshat_block_content",
  CONTENT: "seshat_content",
  CONTENT_NODE_VIEW: "seshat_node_view_content",
  SELECTED_BLOCK: "selected",
  DROPCURSOR: "seshat_dropcursor",
  DRAG_IMAGE: "drag-image"
};
const SeshatNodes = {
  BLOCK: "block",
  WRAPPING_BLOCK: "wrappingBlock",
  BLOCK_CONTENT: "seshatBlockContent",
  CONTENT: "seshatContent",
  CONTENT_NODE_VIEW: "seshatNodeViewContent"
};
const SeshatInlineNodes = {
  EXTERNAL_IMAGE: "externalImage",
  TEXT: "text",
  REFERENCE: "reference"
};
const SeshatNodeGroups = {
  BLOCK: "seshatBlock",
  CONTENT: "seshatContent",
  NODE_VIEW: "nodeView"
};
const SeshatWrappingBlocks = [
  BlockTypes.BLOCKQUOTE,
  BlockTypes.TWEET,
  BlockTypes.BLOCK_LINE,
  BlockTypes.CAPTURE,
  BlockTypes.CLIP_CONTENT,
  BlockTypes.BLOCK_COLUMN,
  BlockTypes.HIGHLIGHTS,
  BlockTypes.HIGHLIGHT,
  BlockTypes.HIGHLIGHT_NOTE
];
const LazyBlockTypes = { ...BlockTypes, ...NodeViewTypes };
const SESHAT_JSON_MIME = "application/x-seshat-json";
const RECALCULATE_PLACEHOLDER = "RECALCULATE_PLACEHOLDER";
const DRAG_HANDLE_ID = "drag-handle";
const BLOCK_MENU_ID = "block-menu";
const EDIT_LINK_MENU_CONTENT_INPUT_ID = "edit-link-menu-content-input";
const MENTION_PLACEHOLDER_INPUT_ID = "autocomplete_placeholder_mention";
const MENTION_PLACEHOLDER_POPOVER_ID = "autocomplete_placeholder_mention_popover";
const CONVERT_LINK_MENU_ID = "convert-link-menu";
const TRAILING_MENU_POPOVER_ID = "trailing-menu-popover";
const BALLON_MENU_POPOVER_ID = "ballon_seshat";
const COMMAND_J_DIALOG_ID = "command-j-dialog";
const EDITOR_RELATED_ELEMENT_IDS = [
  BALLON_MENU_POPOVER_ID,
  BLOCK_MENU_ID,
  DRAG_HANDLE_ID,
  EDIT_LINK_MENU_CONTENT_INPUT_ID,
  MENTION_PLACEHOLDER_INPUT_ID,
  MENTION_PLACEHOLDER_POPOVER_ID,
  CONVERT_LINK_MENU_ID,
  TRAILING_MENU_POPOVER_ID,
  COMMAND_J_DIALOG_ID
];
const LINKEDIN_FETCH_CATEGORY = {
  POST: "post",
  DM: "dm",
  PROFILE: "profile"
};
const INBOX_DEFAULT_OPTION = {
  name: "Inbox",
  id: CARD_VIEW_DEFAULT,
  color: "inherit",
  isDefault: true,
  noFilter: true
};
const LATER_DEFAULT_OPTION = {
  name: "Later",
  id: CardClipStatus.LATER,
  color: "inherit",
  isDefault: true,
  filters: [{ fields: "status", includes: [CardClipStatus.LATER] }]
};
const ALL_CARD_DEFAULT_OPTION = {
  name: "All Cards",
  id: CARD_VIEW_DEFAULT,
  color: "inherit",
  isDefault: true,
  noFilter: true
};
const ALL_CARD_TRASH_DEFAULT_OPTION = {
  name: "Trash",
  id: "trash",
  color: "inherit",
  isDefault: true,
  noFilter: true
};
const METADATA_DEFAULT_OPTION = {
  name: "All",
  id: CARD_VIEW_DEFAULT,
  color: "inherit",
  isDefault: true,
  noFilter: true
};
const TRASH_DEFAULT_OPTION = [
  {
    name: "Cards",
    id: "cards",
    color: "inherit",
    isDefault: true,
    noFilter: true
  },
  {
    name: "People",
    id: "people",
    color: "inherit",
    isDefault: true,
    noFilter: true
  },
  {
    name: "Sources",
    id: "sources",
    color: "inherit",
    isDefault: true,
    noFilter: true
  }
];
const ADD_CONTEXT_ANCHOR = "+";
const CAPTURE_TO_ANCHOR = ">";
const WS_TYPE = {
  GET_WEB_PERMISSION: "getWebAutomationPermissions",
  PROMPT_PERMISSIONS: "promptPermission"
};
const COMMAND_J_ID = "commandJ";
const EXPORT_WINDOW_EVENT = {
  SET_CONTENT: "set-content"
};
const ArrayOperation = {
  move: (arr, oldIndex, newIndex) => {
    if (newIndex >= arr.length) {
      var k = newIndex - arr.length + 1;
      while (k--) {
        arr.push(void 0);
      }
    }
    arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
    return arr;
  },
  includes(arr, elem) {
    return arr.includes(elem);
  }
};
const asyncMap = async (array, iterator) => {
  let results = [];
  for (let i = 0; i < array.length; i++) {
    const value = array[i];
    const result = await iterator(value, i, array);
    if (result)
      results[i] = result;
  }
  return results;
};
const DONE = 2;
const mapBlobToBuffer = (blob) => {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = function() {
      if (reader.readyState === DONE) {
        if (reader.result) {
          const buffer = new Uint8Array(reader.result);
          resolve(buffer);
        } else {
          reject("Error in Buffer");
        }
      }
    };
    reader.readAsArrayBuffer(blob);
  });
};
const mapBase64ToFile = (dataUrlBase64, filename) => {
  var _a, _b;
  const arr = dataUrlBase64.split(",");
  const mime = (_b = (_a = arr == null ? void 0 : arr[0]) == null ? void 0 : _a.match(/:(.*?);/)) == null ? void 0 : _b[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  let u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};
const bufferToFile = ({
  buffer,
  fileName,
  type
}) => {
  const blob = new Blob([buffer], { type });
  blob.lastModifiedDate = new Date();
  blob.name = fileName;
  const fileOfBlob = new File([blob], fileName, {
    type
  });
  return fileOfBlob;
};
const Uint8ArrayToBuffer = (ab) => {
  var buf = Buffer.alloc(ab.byteLength);
  var view = new Uint8Array(ab);
  for (var i = 0; i < buf.length; ++i) {
    buf[i] = view[i];
  }
  return buf;
};
const turnFileIntoLocalUrl = (data, type) => window.URL.createObjectURL(new Blob([data], { type }));
const concatBuffers = (a, b) => Buffer.concat([Buffer.from(a), Buffer.from(b)]);
const stringToBuffer = (text) => Buffer.from(text, "utf8");
const bufferToString = (buffer) => Buffer.from(buffer).toString("utf8");
const bufferToHex = (buffer) => {
  return Buffer.from(buffer).toString("hex");
};
const hexToBuffer = (hex) => Buffer.from(hex, "hex");
const customize = (objValue, srcValue) => {
  if (_.isArray(objValue)) {
    return objValue;
  } else if (_.isObject(objValue) && !(objValue == null ? void 0 : objValue.type)) {
    return _.mergeWith({ ...objValue }, { ...srcValue }, customize);
  } else {
    return objValue;
  }
};
const customizeWithConcat = (objValue, srcValue) => {
  if (_.isArray(objValue) || _.isArray(srcValue)) {
    return _.uniq((_.isArray(objValue) ? [...objValue] : []).concat(_.isArray(srcValue) ? [...srcValue] : []));
  } else if (_.isObject(objValue) || _.isObject(srcValue)) {
    return _.mergeWith({ ...objValue }, { ...srcValue }, customizeWithConcat);
  } else {
    return objValue;
  }
};
const deepMerge = (object, objectInto) => _.mergeWith(object, objectInto, customize);
const deepMergeWithConcat = (object, objectInto) => _.mergeWith({ ...object }, { ...objectInto }, customizeWithConcat);
const transformEmailString = (email) => {
  const regex = /(.*).*<(.*)>/gm;
  const emailFormatted = email == null ? void 0 : email.replace(/"/g, "");
  return {
    address: emailFormatted == null ? void 0 : emailFormatted.replace(regex, `$2`),
    name: emailFormatted == null ? void 0 : emailFormatted.replace(regex, `$1`).trim()
  };
};
const transformContent = (content) => {
  [...content.querySelectorAll("blockquote") || []].forEach((b) => b.remove());
  [...content.querySelectorAll(".gmail_quote") || []].forEach((b) => b.remove());
  [...content.querySelectorAll(".im") || []].forEach((b) => b.remove());
  [...content.querySelectorAll("div") || []].forEach((b) => {
    if (b.textContent === "")
      b.remove();
  });
  [...content.querySelectorAll('*[style="display:none"]') || []].forEach((b) => b.remove());
  [...content.querySelectorAll(".CToWUd") || []].forEach((b) => b.remove());
  [...content.querySelectorAll(".adL") || []].forEach((b) => b.remove());
  [...content.querySelectorAll(".hi") || []].forEach((b) => b.remove());
  [
    ...content.querySelectorAll('*[style="display:none;border:0px;width:0px;height:0px;overflow:hidden"]') || []
  ].forEach((b) => {
    var _a;
    return (_a = b.parentElement) == null ? void 0 : _a.remove();
  });
  [...content.querySelectorAll('*[tabindex="-1"]') || []].forEach((b) => b.remove());
};
const reformatSubject = (subject) => {
  return subject == null ? void 0 : subject.replace(/Re:/g, "").replace(/Fwd:/g, "").trim();
};
const isEmail = (email) => {
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email);
};
const EXCERPT_LENGTH_BEFORE_MATCH = 15;
const EXCERPT_LENGTH_AFTER_MATCH = 15;
const ADD_LAST_CHARACTERS_TO_SLICE_END = 1;
const getExcerptEnd = ({ end, excerptLengthAfterEnd, excerptLengthBeforeStart, valueLength }) => {
  const isSmallerThanValueLength = end - excerptLengthBeforeStart < valueLength;
  const shouldAddLengthAtTheEnd = excerptLengthBeforeStart < 0 && excerptLengthAfterEnd > 0;
  if (shouldAddLengthAtTheEnd) {
    return isSmallerThanValueLength ? end - excerptLengthBeforeStart : valueLength;
  }
  return end;
};
const getExcerptStart = ({ start, excerptLengthAfterEnd, excerptLengthBeforeStart }) => {
  if (start === 0)
    return start;
  const isStartHigherThanZero = start - excerptLengthAfterEnd > 0;
  const shouldAddLengthAtStart = excerptLengthAfterEnd < 0 && excerptLengthBeforeStart > 0;
  if (shouldAddLengthAtStart) {
    return isStartHigherThanZero ? start - Math.abs(excerptLengthAfterEnd) : 0;
  }
  return start;
};
const extractExcerptFromValue = ({ value, start, end }) => {
  if (!value)
    return "";
  const excerptLengthBeforeStart = start - EXCERPT_LENGTH_BEFORE_MATCH;
  const temporaryEnd = end + EXCERPT_LENGTH_AFTER_MATCH;
  const excerptLengthAfterEnd = value.length - temporaryEnd;
  const temporaryStart = excerptLengthBeforeStart > EXCERPT_LENGTH_BEFORE_MATCH ? excerptLengthBeforeStart : 0;
  let finalEnd = getExcerptEnd({
    end: temporaryEnd,
    excerptLengthAfterEnd,
    excerptLengthBeforeStart,
    valueLength: value.length
  });
  let finalStart = getExcerptStart({
    start: temporaryStart,
    excerptLengthAfterEnd,
    excerptLengthBeforeStart
  });
  return value == null ? void 0 : value.slice(finalStart, finalEnd + ADD_LAST_CHARACTERS_TO_SLICE_END);
};
const objectToFormData = (obj) => {
  const formData = new FormData();
  for (var key in obj) {
    formData.append(key, obj[key]);
  }
  return formData;
};
const getDoneTaskPercentage = (childTask) => {
  if (childTask.length === 0)
    return 0;
  const doneChildTask = childTask.filter((t) => t.status === "done");
  return doneChildTask.length / childTask.length * 100;
};
const getTextFromCard = (card, cards) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
  try {
    if ((_a = card.text) == null ? void 0 : _a.length)
      return concatenateText(card, cards);
    if (card.type === LazyBlockTypes.CLIP_BOOKMARK)
      return (_e = (_d = (_b = card.attrs) == null ? void 0 : _b.title) != null ? _d : (_c = card.attrs) == null ? void 0 : _c.href) != null ? _e : "";
    if (card.type === LazyBlockTypes.CLIP_BOOK)
      return (_g = (_f = card.attrs) == null ? void 0 : _f.title) != null ? _g : "";
    if (card.type === LazyBlockTypes.FILE)
      return (_i = (_h = card.attrs) == null ? void 0 : _h.fileName) != null ? _i : "";
    if (card.type === LazyBlockTypes.IFRAME)
      return (_k = (_j = card.attrs) == null ? void 0 : _j.src) != null ? _k : "";
    if (card.type === LazyBlockTypes.HORIZONTAL_RULE)
      return "---";
    return "";
  } catch (err) {
    return "";
  }
};
const concatenateText = (card, cards) => {
  var _a, _b;
  return (_b = (_a = card.text) == null ? void 0 : _a.reduce((acc, section) => {
    var _a2, _b2, _c, _d, _e;
    if (section.type === "reference" && ((_a2 = section.attrs) == null ? void 0 : _a2.cardUuid)) {
      const targetCard = cards[(_b2 = section.attrs) == null ? void 0 : _b2.cardUuid];
      if (!targetCard)
        return (_d = (_c = section.attrs) == null ? void 0 : _c.value) != null ? _d : "";
      return acc + ` ${((_e = section.attrs) == null ? void 0 : _e.char) === "#" ? "#" : ""}` + getTextFromCard(targetCard, cards);
    }
    return acc + section.text;
  }, "")) != null ? _b : "";
};
const getNestedTextFromCard = ({
  card,
  cards
}) => {
  return getChildrenText({
    card,
    cards
  });
};
const getChildrenText = ({
  card,
  cards
}) => {
  if (!card.childrenIds)
    return "";
  return card.childrenIds.reduce((acc, uuid) => {
    const child = cards[uuid];
    if (!child)
      return acc;
    const text = getParentAndChildrenText({ card: child, cards });
    return text ? acc + text + " " : acc;
  }, "").trim();
};
const getParentAndChildrenText = ({
  card,
  cards
}) => {
  return (getTextFromCard(card, cards) + " " + getChildrenText({
    card,
    cards
  })).trim();
};
const SPECIAL_VALUES = {
  undefined: void 0,
  null: null
};
const decodeValue = (value) => value in SPECIAL_VALUES ? SPECIAL_VALUES[value] : decodeURIComponent(value);
function queryParser(query) {
  const result = query.replace("?", "").split("&");
  return result.reduce(parseComponent, {});
}
function parseComponent(acc, next) {
  if (!next)
    return acc;
  const [key, v] = next.split("=");
  const value = decodeValue(v);
  const strategies = [maybeParseArrayLike, maybeParseObjectLike, maybeParsePrimitiveLike];
  for (const strategy of strategies) {
    const handled = strategy(acc, key, value);
    if (handled)
      break;
  }
  return acc;
}
const OBJECT_REGEXP = new RegExp("\\w+\\[\\w+\\]");
function isObjectLike(key) {
  return OBJECT_REGEXP.test(key);
}
function maybeParseObjectLike(store, key, value) {
  var _a;
  if (!isObjectLike(key))
    return false;
  const [beforeClosingBracket] = key.split("]");
  const [field, subfield] = beforeClosingBracket.split("[");
  if (!subfield)
    return false;
  const obj = (_a = store[field]) != null ? _a : {};
  obj[subfield] = value;
  store[field] = obj;
  return true;
}
function isArrayLike(key) {
  return key.endsWith("[]");
}
function maybeParseArrayLike(store, key, value) {
  var _a;
  if (!isArrayLike(key))
    return false;
  const [field] = key.split("[]");
  const a = (_a = store[field]) != null ? _a : [];
  a.push(value);
  store[field] = a;
  return true;
}
function maybeParsePrimitiveLike(store, key, value) {
  store[key] = value;
  return true;
}
const defaultLength = 150;
const summarize = (substring, length = defaultLength) => {
  let escapedContent = substring;
  if (length !== 0)
    escapedContent = escapedContent.substring(0, length);
  return escapedContent;
};
const NUMBER_OF_WORD_READ_PRE_MINUTE = 296;
const calculateReadingTime = (content) => {
  return Math.round(content.split(" ").length / NUMBER_OF_WORD_READ_PRE_MINUTE);
};
const setLastUpdate$2 = () => new Date().toISOString();
const toIsoDate$1 = (date) => {
  return date.toISOString();
};
const cloneDate = (date) => {
  return new Date(date.getTime());
};
const getDayCardDueDate = (date) => {
  return date.toISOString();
};
const turnIntoOrdinalNoun = (date) => date.map((d) => {
  if (d.type === "day") {
    if (d.value === "1" || d.value === "21" || d.value === "31")
      return `${d.value}st`;
    if (d.value === "2" || d.value === "22")
      return `${d.value}nd`;
    if (d.value === "3" || d.value === "23")
      return `${d.value}rd`;
    return `${d.value}th`;
  }
  return d.value;
}).join("");
const turnOrdinalNounIntoDate = (date) => {
  const splitDate = date.replace(/st|nd|rd|th/gm, "");
  return new Date(splitDate);
};
const getValidDate = (date) => {
  try {
    if (!date)
      return new Date();
    const d = turnOrdinalNounIntoDate(date);
    if (d.toString() === "Invalid Date")
      return new Date();
    return d;
  } catch (e) {
    return new Date();
  }
};
const convertDateToLocal = ({
  date,
  local = "en-US",
  dateOnly
}) => {
  try {
    const validDate = getValidDate(date);
    const intlDate = new Intl.DateTimeFormat(local, {
      year: "numeric",
      month: "short",
      day: "numeric",
      ...!dateOnly && {
        hour: "numeric",
        minute: "numeric",
        second: void 0
      }
    }).formatToParts(validDate);
    return turnIntoOrdinalNoun(intlDate);
  } catch (_2) {
    return date;
  }
};
const convertDateToDayMonth = ({ date: dateStr, local = "en-US" }) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return "";
  }
  const month = date.toLocaleDateString(local, {
    month: "short"
  });
  const day = date.toLocaleDateString(local, {
    day: "numeric"
  });
  return `${month} ${day}`;
};
const convertDateToDayMonthYear = ({ date: dateStr, local = "en-US" }) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return "";
  }
  const year = date.toLocaleDateString(local, {
    year: "numeric"
  });
  return `${convertDateToDayMonth({ date: dateStr, local })} ${year}`;
};
const isToday = (date) => {
  try {
    if (!date)
      return false;
    return getFormattedDate(getValidDate(date)) === getFormattedDate(new Date());
  } catch {
    return false;
  }
};
const isYesterday = (date) => {
  if (!date)
    return false;
  var yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return getFormattedDate(getValidDate(date)) === getFormattedDate(yesterday);
};
const isDateEqual = ({ date1, date2 }) => {
  var _a, _b;
  if (!date1 || !date2)
    return false;
  return ((_a = getValidDate(date1)) == null ? void 0 : _a.toLocaleDateString()) === ((_b = getValidDate(date2)) == null ? void 0 : _b.toLocaleDateString());
};
const getSecondDeltaBetweenDate = ({ baseDate, diffDate }) => {
  const t1 = new Date(getValidDate(baseDate));
  const t2 = new Date(getValidDate(diffDate));
  const dif = t1.getTime() - t2.getTime();
  return dif / 1e3;
};
const getDayDeltaBetweenDate = ({ baseDate, diffDate }) => {
  const t1 = new Date(getValidDate(baseDate));
  const t2 = new Date(getValidDate(diffDate));
  const utc1 = Date.UTC(t1.getFullYear(), t1.getMonth(), t1.getDate());
  const utc2 = Date.UTC(t2.getFullYear(), t2.getMonth(), t2.getDate());
  const dif = utc1 - utc2;
  return Math.floor(dif / (1e3 * 3600 * 24));
};
const getFormattedDate = (today) => {
  const date = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: void 0
  }).formatToParts(today);
  return turnIntoOrdinalNoun(date);
};
const formatRelativeDate = (date) => {
  const differenceInDays = Math.abs(
    getDayDeltaBetweenDate({ baseDate: date.toISOString(), diffDate: new Date().toISOString() })
  );
  const rtf = new Intl.RelativeTimeFormat("en", {
    numeric: "auto"
  });
  const differenceInYears = Math.floor(differenceInDays / 365);
  if (differenceInYears >= 1) {
    return rtf.format(-differenceInYears, "years");
  }
  const differenceInMonths = Math.floor(differenceInDays / 30);
  if (differenceInMonths >= 1) {
    return rtf.format(-differenceInMonths, "months");
  }
  return rtf.format(-differenceInDays, "days");
};
const isStringDateBetweenOtherStringDates = ({
  date,
  startDate,
  endDate
}) => {
  const dateToCompare = date ? new Date(date) : new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  return dateToCompare >= start && dateToCompare <= end;
};
const SECOND_IN_MINUTE = 60;
const SECOND_IN_HOURS = 60 * 60;
const timestampToMinute = (timestamp) => {
  const hours = Math.floor(timestamp / SECOND_IN_HOURS);
  const timestampMinusHours = timestamp - hours * SECOND_IN_HOURS;
  const minutesNumber = Math.floor(timestampMinusHours / SECOND_IN_MINUTE);
  const minutes = String(minutesNumber).padStart(2, "0");
  const second = String(Math.floor(timestampMinusHours - minutesNumber * SECOND_IN_MINUTE)).padStart(2, "0");
  if (!hours)
    return `${minutes}:${second}`;
  return `${hours}:${minutes}:${second}`;
};
const minuteToTimestamp = (time) => {
  const times = time.split(":");
  let hours = "0";
  let minutes = "0";
  let seconds = "0";
  if (times.length === 2)
    [minutes, seconds] = times;
  else
    [hours, minutes, seconds] = times;
  const hoursNumber = parseInt(hours);
  const minutesNumber = parseInt(minutes);
  const secondsNumber = parseInt(seconds);
  return hoursNumber * SECOND_IN_HOURS + minutesNumber * SECOND_IN_MINUTE + secondsNumber;
};
const endOfDay = (initialDate) => {
  const date = cloneDate(initialDate);
  date.setHours(23, 59, 59, 999);
  return date;
};
const isDateBefore = (date, dateToCompare) => {
  return date.getTime() < dateToCompare.getTime();
};
const isDateAfter = (date, dateToCompare) => {
  return date.getTime() > dateToCompare.getTime();
};
const addDays = (initialDate, daysToAdd) => {
  const date = cloneDate(initialDate);
  date.setDate(date.getDate() + daysToAdd);
  return date;
};
const subDays = (initialDate, daysToSubtract) => {
  return addDays(initialDate, -daysToSubtract);
};
const isSameDay = (firstDate, secondDate) => {
  return getFormattedDate(firstDate) === getFormattedDate(secondDate);
};
const isSameMonth = (firstDate, secondDate) => {
  return firstDate.getFullYear() === secondDate.getFullYear() && firstDate.getMonth() === secondDate.getMonth();
};
const FIRST_DAY_OF_WEEK = 1;
const firstDayOfWeek = (initialDate) => {
  const date = cloneDate(initialDate);
  const day = date.getDay();
  const diff = day < FIRST_DAY_OF_WEEK ? -7 + (FIRST_DAY_OF_WEEK - day) : -(day - FIRST_DAY_OF_WEEK);
  date.setDate(date.getDate() + diff);
  return date;
};
const lastDayOfWeek = (initialDate) => {
  const date = cloneDate(initialDate);
  const day = date.getDay();
  const diff = (day < FIRST_DAY_OF_WEEK ? -7 : 0) + 6 - (day - FIRST_DAY_OF_WEEK);
  date.setDate(date.getDate() + diff);
  return date;
};
const maxDate = (dates) => {
  let result;
  dates.forEach(function(date) {
    if (result === void 0 || isDateAfter(date, result)) {
      result = date;
    }
  });
  return result || new Date(NaN);
};
const convertContentToTitle = (title) => {
  return title.replace(/<p>&nbsp;<\/p>$/g, "").replace(/<p><\/p>$/g, "");
};
const toTitleCase = (str) => {
  return str.split(/\s+/).map((s) => s.charAt(0).toUpperCase() + s.substring(1).toLowerCase()).join(" ");
};
const transformEmailToTitle = (title) => {
  var _a;
  return toTitleCase(((_a = title == null ? void 0 : title.split("@")[0]) == null ? void 0 : _a.replace(".", " ").replace("_", " ").replace("-", " ")) || "");
};
const transformNameToTitle = (name) => {
  return toTitleCase((name == null ? void 0 : name.replace("@", "")) || "");
};
const substringTitle = (title, length = DEFAULT_CARD_TITLE_LENGTH) => {
  if (title.length <= length)
    return title.trim();
  const trimmedStringBySpace = title.replaceAll("\n\r", " ").replaceAll("\n", " ").replaceAll(/\s+/gm, " ").split(" ").reduce((acc, word, _2, arr) => {
    if (acc.length + word.length < length) {
      return `${acc} ${word}`;
    }
    arr.splice(1);
    return acc;
  }, "");
  return `${!trimmedStringBySpace ? title.substring(0, length) : trimmedStringBySpace} ...`.trim();
};
const xmlToJson = (xmlString, convert = false) => {
  var xml = convert ? new DOMParser().parseFromString(xmlString, "text/xml") : xmlString;
  var obj = {};
  if (xml.nodeType === 1) {
    if (xml.attributes.length > 0) {
      obj["@attributes"] = {};
      for (var j = 0; j < xml.attributes.length; j++) {
        var attribute = xml.attributes.item(j);
        obj["@attributes"][attribute.nodeName] = attribute.nodeValue;
      }
    }
  } else if (xml.nodeType === 3) {
    obj = xml.nodeValue;
  }
  if (xml.hasChildNodes()) {
    for (var i = 0; i < xml.childNodes.length; i++) {
      var item = xml.childNodes.item(i);
      var nodeName = item.nodeName;
      if (obj !== null && typeof (obj == null ? void 0 : obj[nodeName]) === "undefined") {
        obj[nodeName] = xmlToJson(item);
      } else {
        if (obj !== null && typeof (obj == null ? void 0 : obj[nodeName].push) === "undefined") {
          var old = obj[nodeName];
          obj[nodeName] = [];
          obj[nodeName].push(old);
        }
        obj == null ? void 0 : obj[nodeName].push(xmlToJson(item));
      }
    }
  }
  return obj;
};
const generateNewCard$2 = (workspaceUuid, card) => {
  const isCapture = !card.title && !card.text && !card.type;
  const cardType = isCapture ? "capture" : card.type || DEFAULT_CARD_TYPE;
  const text = typeof card.title === "string" && !card.text ? [{ text: card.title, type: "text" }] : void 0;
  return {
    text,
    ...card,
    workspaceUuid,
    type: cardType,
    creationDate: setLastUpdate$2(),
    lastUpdate: setLastUpdate$2()
  };
};
const maybeSetDefaultWorkspaceMetadata = (metadata) => {
  var _a, _b, _c;
  return {
    ...metadata,
    pinnedCards: (_a = metadata == null ? void 0 : metadata.pinnedCards) != null ? _a : [],
    drawersWidth: (_b = metadata == null ? void 0 : metadata.drawersWidth) != null ? _b : {},
    viewsOrder: (_c = metadata == null ? void 0 : metadata.viewsOrder) != null ? _c : []
  };
};
function mergeCards({
  fromCard,
  intoCard,
  cards
}) {
  var _a;
  const metadata = fromCard.type === "capture" ? maybeMakeMetadataReady(intoCard.metadata || {}) : maybeMakeMetadataReady(
    deepMergeWithConcat(
      maybeMakeMetadataReady(fromCard.metadata || {}),
      maybeMakeMetadataReady(intoCard.metadata || {})
    )
  );
  const commonMergeProps = {
    ...intoCard,
    uuid: intoCard.uuid,
    workspaceUuid: intoCard.workspaceUuid,
    pictureUrl: !!fromCard.pictureUrl ? fromCard.pictureUrl : intoCard.pictureUrl,
    emoji: fromCard.emoji ? fromCard.emoji : intoCard.emoji,
    childrenCardsUuid: _.uniq([...intoCard.childrenCardsUuid || [], ...fromCard.childrenCardsUuid || []]),
    attachments: _.uniq([...intoCard.attachments || [], ...fromCard.attachments || []]),
    metadata,
    lastUpdate: setLastUpdate$2()
  };
  if (CARD_NOTE_TYPE === fromCard.type || CARD_TASK_TYPE === fromCard.type) {
    const isPreviousCapture = cards && isCaptureTurnedIntoNote(cards, fromCard);
    return isPreviousCapture ? {
      ...commonMergeProps,
      parentCardUuid: fromCard.parentCardUuid || intoCard.parentCardUuid,
      type: intoCard.type || fromCard.type,
      text: intoCard.text || fromCard.text,
      title: intoCard.title || fromCard.title
    } : {
      ...commonMergeProps,
      parentCardUuid: fromCard.parentCardUuid || intoCard.parentCardUuid,
      type: fromCard.type || intoCard.type,
      text: fromCard.text || intoCard.text,
      title: fromCard.title || intoCard.title
    };
  }
  if (CARD_PERSON_TYPE === fromCard.type) {
    const PROPS_TO_CONVERT_TO_METADATA = [
      {
        descriptorId: allDefaultPropertiesDescriptors.name.id,
        name: "title"
      },
      {
        descriptorId: allDefaultPropertiesDescriptors.picture.id,
        name: "pictureUrl"
      }
    ];
    for (const property of PROPS_TO_CONVERT_TO_METADATA) {
      if (commonMergeProps.metadata.propertiesValues[property.descriptorId] == null) {
        commonMergeProps.metadata.propertiesValues[property.descriptorId] = { values: [] };
      }
      commonMergeProps.metadata.propertiesValues[property.descriptorId].values = _.uniq(
        commonMergeProps.metadata.propertiesValues[property.descriptorId].values.concat(
          [intoCard[property.name], fromCard[property.name]].filter((value) => value != null)
        )
      );
    }
    return {
      ...commonMergeProps,
      pictureUrl: (_a = intoCard.pictureUrl) != null ? _a : fromCard.pictureUrl
    };
  }
  return {
    ...commonMergeProps,
    type: intoCard.type || fromCard.type,
    text: intoCard.text || fromCard.text,
    title: intoCard.title || fromCard.title
  };
}
const isCaptureTurnedIntoNote = (cards, card) => {
  var _a, _b;
  const clipUuid = ((card == null ? void 0 : card.childrenIds) || []).find((child) => {
    const card2 = cards[child];
    return (card2 == null ? void 0 : card2.type) === LazyBlockTypes.CLIP_CONTENT || (card2 == null ? void 0 : card2.type) === LazyBlockTypes.CLIP_BOOKMARK;
  });
  if (!clipUuid)
    return false;
  return ((_a = cards[clipUuid].attrs) == null ? void 0 : _a.title) && ((_b = cards[clipUuid].attrs) == null ? void 0 : _b.title) === card.title;
};
const CLIP_EMOJI_MATCHERS = {
  [DEFAULT_CARD_EMOJI.EMAIL]: ({ attrs, categories }) => !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_EMAILS)) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_NEWSLETTERS)) || !!(attrs == null ? void 0 : attrs.email),
  [DEFAULT_CARD_EMOJI.VIDEO]: ({ attrs, url, categories }) => !!(attrs == null ? void 0 : attrs.video) || isUrlOfType("youtube", url) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_VIDEOS)),
  [DEFAULT_CARD_EMOJI.DM]: ({ url, categories }) => !!(url == null ? void 0 : url.includes("linkedin.com/messaging/thread")) || !!(url == null ? void 0 : url.includes("slack.com")) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_SLACK)) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_CONVERSATIONS)) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_LINKEDIN_DM)),
  [DEFAULT_CARD_EMOJI.PROFILE]: ({ url, categories }) => !!(url == null ? void 0 : url.includes("linkedin.com/in")) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_PROFILES)),
  [DEFAULT_CARD_EMOJI.LINKEDIN_POST]: ({ url, categories }) => isUrlOfType("linkedin", url) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_LINKEDIN_POSTS)),
  [DEFAULT_CARD_EMOJI.PDF]: ({ url, categories }) => !!(url == null ? void 0 : url.endsWith(".pdf")) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_PDF)),
  [DEFAULT_CARD_EMOJI.TWITTER_TWEET]: ({ attrs, url, nbOfChildren, categories }) => (!!(attrs == null ? void 0 : attrs.twitter) || !!url && !!isTweetUrl(url) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_TWEETS))) && nbOfChildren <= 1,
  [DEFAULT_CARD_EMOJI.TWITTER_THREAD]: ({ attrs, url, nbOfChildren, categories }) => (!!(attrs == null ? void 0 : attrs.twitter) || !!url && !!isTweetUrl(url) || !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_TWEETS))) && nbOfChildren > 1,
  [DEFAULT_CARD_EMOJI.LINK]: ({ categories }) => !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_LINKS)),
  [DEFAULT_CARD_EMOJI.ARTICLE]: ({ categories }) => !!(categories == null ? void 0 : categories.includes(CATEGORY_NAMES.CATEGORY_ARTICLES))
};
const getClipEmoji = (args) => {
  for (const [emoji, matches] of Object.entries(CLIP_EMOJI_MATCHERS)) {
    if (matches(args))
      return emoji;
  }
  return void 0;
};
const cleanAuthorName = (name) => {
  if (!name)
    return "";
  const username = name.startsWith("@") ? name.replace(/@+/g, "").replaceAll(/\s+/g, " ") : name.split("@")[0];
  const regex = new RegExp("[_-]|(?<!^)([A-Z])(?=[a-z])", "gm");
  const subst = ` $1`;
  const words = username.replaceAll(regex, subst).split(" ");
  const formattedName = words.filter((n) => !!n).map((word) => {
    if (word.charAt(0) === word.charAt(0).toLowerCase())
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    return word;
  }).join(" ");
  return formattedName;
};
const getAuthorNameFromMetadata = (jsonLD, metadata) => {
  var _a, _b, _c, _d, _e;
  const jsonLDAuthor = Array.isArray(jsonLD == null ? void 0 : jsonLD.author) ? (_b = (_a = jsonLD == null ? void 0 : jsonLD.author) == null ? void 0 : _a[0]) == null ? void 0 : _b.name : (_c = jsonLD == null ? void 0 : jsonLD.author) == null ? void 0 : _c.name;
  const metadataAuthor = metadata == null ? void 0 : metadata.author;
  const authorsPossibilities = [parseAuthor(jsonLDAuthor), parseAuthor(metadataAuthor)];
  const personName = authorsPossibilities.find((author) => (author == null ? void 0 : author.type) === "name");
  if (personName)
    return personName.author;
  const twitterHandle = authorsPossibilities.find((author) => (author == null ? void 0 : author.type) === "twitter");
  if (twitterHandle)
    return twitterHandle.author;
  const urlName = authorsPossibilities.find((author) => (author == null ? void 0 : author.type) === "url");
  if (urlName)
    return urlName.author;
  const nameItemListElement = (_e = (_d = jsonLD == null ? void 0 : jsonLD.itemListElement) == null ? void 0 : _d[0].item) == null ? void 0 : _e.name;
  if (nameItemListElement)
    return nameItemListElement;
  return;
};
const parseAuthor = (author) => {
  var _a;
  if (typeof author !== "string")
    return;
  if (author.startsWith("@")) {
    const authorWithoutAt = author.slice(1);
    const isAuthorUrl2 = isUrl(authorWithoutAt);
    if (isAuthorUrl2) {
      const parsedAuthor = (_a = authorWithoutAt.split("/").pop()) == null ? void 0 : _a.toLowerCase();
      if (parsedAuthor) {
        return { type: "twitter", author: parsedAuthor.startsWith("@") ? parsedAuthor : `@${parsedAuthor}` };
      }
    }
    return { type: "twitter", author: author.toLocaleLowerCase() };
  }
  const isAuthorUrl = author ? isUrl(author) : false;
  if (author && !isAuthorUrl) {
    return { type: "name", author };
  }
  if (author && isAuthorUrl) {
    return { type: "url", author: author.split("/").pop() };
  }
};
const summarizeAuthorName = (authorName) => {
  if (!authorName)
    return;
  return authorName.split(" ", 2).map((s) => s.charAt(0).toUpperCase()).join("");
};
const extractTextFromHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent;
};
const getUserPublicName = (user) => {
  var _a, _b, _c;
  return (_c = (_b = (_a = user.metadata) == null ? void 0 : _a.public) == null ? void 0 : _b.name) != null ? _c : user.name;
};
const traverseProsemirrorDoc = (doc, callback) => {
  const traverse = (node, parent = null, i) => {
    const shouldRemoveNode = !callback({ node, parent, index: i });
    if (shouldRemoveNode) {
      if (parent && Array.isArray(parent.content))
        parent.content.splice(i, 1);
      return;
    }
    if (node.content && Array.isArray(node.content)) {
      for (let i2 = node.content.length - 1; i2 >= 0; i2--) {
        traverse(node.content[i2], node, i2);
      }
    }
  };
  traverse(doc, null, -1);
};
const normalizeShortcutForGlobalShortcut = (shortcut) => shortcut.replace("\u21E7", "shift").replace("\u2318", "meta").replace("\u2303", "ctrl").replace("\u2325", "alt");
const convertKeyEventToString = (event) => {
  const modifierKeys = {
    metaKey: "meta",
    ctrlKey: "ctrl",
    altKey: "alt",
    shiftKey: "shift"
  };
  let keyString = "";
  for (const key in modifierKeys) {
    if (modifierKeys.hasOwnProperty(key) && event[key]) {
      keyString += modifierKeys[key] + "+";
    }
  }
  if (event.key) {
    keyString += event.code.replace("Key", "");
  }
  return keyString.toLowerCase();
};
function makePropertyDescriptorId(value) {
  return value;
}
function makeMultiSelectDescriptor({
  props,
  generateId = () => uuidV5$1("", "")
}) {
  return {
    id: makePropertyDescriptorId(generateId()),
    type: "multi_select",
    ...omit(props, "id")
  };
}
const getDescriptor = ({
  metadata,
  descriptorId
}) => {
  var _a, _b, _c, _d, _e, _f;
  const descriptor = {
    ...(_a = allDefaultPropertiesDescriptors[descriptorId]) != null ? _a : {},
    ...(_b = metadata == null ? void 0 : metadata.propertiesDescriptors[descriptorId]) != null ? _b : {}
  };
  if (Object.keys(descriptor).length === 0)
    return makeMultiSelectDescriptor({
      generateId: () => descriptorId,
      props: { name: descriptorId }
    });
  return {
    ...descriptor,
    options: (descriptor == null ? void 0 : descriptor.options) ? _.uniqBy(
      [
        ...(_d = (_c = metadata == null ? void 0 : metadata.propertiesDescriptors[descriptorId]) == null ? void 0 : _c.options) != null ? _d : [],
        ...(_f = (_e = allDefaultPropertiesDescriptors[descriptorId]) == null ? void 0 : _e.options) != null ? _f : []
      ],
      "name"
    ) : void 0
  };
};
function addDescriptorOptionsToMetadata({
  descriptorId,
  options,
  metadata
}) {
  var _a;
  let descriptor = getDescriptor({ metadata, descriptorId });
  const descriptorOptions = (_a = descriptor.options) != null ? _a : [];
  options.forEach((option) => {
    const findIndex = descriptorOptions.findIndex((existingOption) => existingOption.id === option.id);
    if (findIndex > -1)
      descriptorOptions[findIndex] = option;
    else
      descriptorOptions.push(option);
  });
  return {
    ...metadata,
    propertiesDescriptors: {
      ...metadata.propertiesDescriptors,
      [descriptorId]: {
        ...descriptor,
        options: descriptorOptions
      }
    }
  };
}
function removeDescriptorOptionsToMetadata({
  descriptorId,
  metadata,
  viewIds
}) {
  var _a, _b, _c, _d;
  const descriptor = (_a = metadata.propertiesDescriptors[descriptorId]) != null ? _a : allDefaultPropertiesDescriptors[descriptorId];
  return {
    ...metadata,
    propertiesDescriptors: {
      ...metadata.propertiesDescriptors,
      [descriptorId]: {
        ...descriptor,
        options: ((_d = (_c = (_b = metadata == null ? void 0 : metadata.propertiesDescriptors) == null ? void 0 : _b[descriptorId]) == null ? void 0 : _c.options) == null ? void 0 : _d.filter((option) => {
          return !viewIds.includes(option.id);
        })) || []
      }
    }
  };
}
function addDescriptorToMetadataIfMissing(descriptor, metadata) {
  const { propertiesValues } = metadata;
  if (propertiesValues[descriptor.id])
    return metadata;
  return {
    ...metadata,
    propertiesDescriptors: {
      ...metadata.propertiesDescriptors,
      [descriptor.id]: descriptor
    }
  };
}
function addValueToMetadata({ propertyDescriptorId, value, metadata }) {
  const { propertiesValues } = metadata;
  return {
    ...metadata,
    propertiesValues: {
      ...propertiesValues,
      [propertyDescriptorId]: value
    }
  };
}
const makeEmptyCardView = () => ({ currentDescriptor: void 0 });
function maybeAugmentWithViews(data) {
  var _a;
  const hasViews = typeof data.views !== "undefined";
  const hasDefaultView = typeof ((_a = data == null ? void 0 : data.views) == null ? void 0 : _a.default) !== "undefined";
  if (hasViews && hasDefaultView) {
    return data;
  }
  if (hasViews && !hasDefaultView) {
    return {
      ...data,
      views: {
        ...data.views,
        default: makeEmptyCardView()
      }
    };
  }
  return { ...data, views: { ...data.views, default: makeEmptyCardView() } };
}
function maybeAugmentWithPropertiesValues(data) {
  if (typeof data.propertiesValues !== "undefined") {
    return data;
  }
  return {
    ...data,
    propertiesValues: {}
  };
}
function maybeAugmentWithPropertiesDescriptors(data) {
  if (typeof data.propertiesDescriptors !== "undefined") {
    return data;
  }
  return {
    ...data,
    propertiesDescriptors: {}
  };
}
const maybeMakeMetadataReady = (data) => {
  const d1 = maybeAugmentWithViews(data != null ? data : {});
  const d2 = maybeAugmentWithPropertiesDescriptors(d1);
  const d3 = maybeAugmentWithPropertiesValues(d2);
  return d3;
};
const addValueToPropertyValueMetadata = ({ propertyDescriptorId, metadata, value }) => addValueToMetadata({
  propertyDescriptorId,
  value: addToPropertyValue({
    propertyDescriptorId,
    values: [value],
    metadata: maybeMakeMetadataReady(metadata)
  }),
  metadata: maybeMakeMetadataReady(metadata)
});
function hasSplit({ views, propertiesDescriptors }, viewId = "default") {
  var _a;
  const descriptorId = (_a = views == null ? void 0 : views[viewId]) == null ? void 0 : _a.currentDescriptor;
  if (!descriptorId)
    return false;
  return !!propertiesDescriptors[descriptorId];
}
function isEmptyMetadata(value) {
  return !isDefined(value) || isEqual$1(value, maybeMakeMetadataReady({}));
}
function isMultiSelectPropertyDescriptor(descriptor) {
  return descriptor.type === "multi_select";
}
function makeCardView(props) {
  if (!(props == null ? void 0 : props.currentDescriptor))
    return makeEmptyCardView();
  return { currentDescriptor: props.currentDescriptor };
}
function makeDefaultDescriptorFilter(metadata, filters) {
  var _a, _b;
  if (!metadata)
    return {};
  const view = metadata.views[(_a = filters.view) != null ? _a : "default"];
  const { currentDescriptor } = view != null ? view : {};
  if (!currentDescriptor)
    return {};
  const descriptor = metadata.propertiesDescriptors[currentDescriptor];
  if (!descriptor)
    return {};
  const hasDefaultOption = (_b = descriptor.options) == null ? void 0 : _b.some((o) => o.isDefault);
  if (!hasDefaultOption)
    return {};
  return {
    split: {
      descriptor: descriptor.id,
      currentOption: void 0
    }
  };
}
function makeMultiSelectOptionId(value) {
  return value;
}
function makeMultiSelectOption(props, generateId = () => uuidV5$1("", "")) {
  return {
    id: makeMultiSelectOptionId(generateId()),
    ...omit(props, "id")
  };
}
function makeMultiSelectPropertyValue(values) {
  return {
    type: "multi_select",
    values
  };
}
const makePropertyValue = (values) => {
  return {
    values
  };
};
function makeSplit(descriptor, currentOption) {
  return { descriptor, currentOption };
}
function makeViewId(value) {
  return value;
}
function makeAuthorOption() {
  return makeMultiSelectOption({ name: "Authors", color: "red", isDefault: false }, () => "author");
}
function makeGroupsDescriptorDefaultOptions() {
  return [{ id: "all", name: "All", color: "black", isDefault: true }, makeAuthorOption()];
}
function makeGroupsDescriptor() {
  return makeMultiSelectDescriptor({
    props: {
      name: "Groups",
      options: makeGroupsDescriptorDefaultOptions()
    },
    generateId: () => "groups"
  });
}
function makePropertyValueWithAuthorOption() {
  return makePropertyValue([makeAuthorOption().id]);
}
function makePropertyValueGroupWithAuthorValue() {
  return { groups: makePropertyValueWithAuthorOption() };
}
function makeLaterOption() {
  return makeMultiSelectOption({ name: "Later", color: "black", isDefault: false }, () => CardClipStatus.LATER);
}
function makeNowOption() {
  return makeMultiSelectOption({ name: "Inbox", color: "black", isDefault: true }, () => "inbox");
}
function makeReadLaterDescriptor() {
  return makeMultiSelectDescriptor({
    props: {
      name: "Read Later",
      options: [makeNowOption(), makeLaterOption()]
    },
    generateId: () => "readLater"
  });
}
function isCardInLaterSplit(card) {
  var _a, _b, _c;
  if (!card.metadata) {
    return false;
  }
  const { id: readLaterId } = makeReadLaterDescriptor();
  const laterId = makeLaterOption().id;
  return !!((_c = (_b = (_a = card.metadata.propertiesValues) == null ? void 0 : _a[readLaterId]) == null ? void 0 : _b.values) == null ? void 0 : _c.includes(laterId));
}
function makeReadOption() {
  return makeMultiSelectOption({ name: "Read", color: "black", isDefault: false }, () => CardClipStatus.READ);
}
function isCardInReadSplit(card) {
  var _a, _b, _c;
  if (!card.metadata) {
    return false;
  }
  const { id: readLaterId } = makeReadLaterDescriptor();
  const readValue = makeReadOption().id;
  return !!((_c = (_b = (_a = card.metadata.propertiesValues) == null ? void 0 : _a[readLaterId]) == null ? void 0 : _b.values) == null ? void 0 : _c.includes(readValue));
}
const isReadLaterSplit = (optionId) => {
  const laterId = makeLaterOption().id;
  if (optionId === laterId)
    return true;
  return false;
};
function makeReadLaterValue() {
  return makeMultiSelectPropertyValue([makeLaterOption().id]);
}
function makePropertyValueReadLaterWithLaterValue() {
  const { id: readLaterId } = makeReadLaterDescriptor();
  return { [readLaterId]: makeReadLaterValue() };
}
function shouldShowLaterStatusInSplit(split, cardUuid) {
  return isReadLaterSplit(split) || cardUuid === NOT_REAL_CARD_UUID.ALL_CARDS || split === "default";
}
const setLastUpdate$1 = () => "2021-01-06T17:22:52Z";
const toIsoDate = (date) => date == null ? void 0 : date.toISOString();
var index$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  getFormattedDate,
  getSecondDeltaBetweenDate,
  setLastUpdate: setLastUpdate$1,
  toIsoDate
}, Symbol.toStringTag, { value: "Module" }));
const uuidV5 = (workspaceUuid, type = "") => type + "123";
let counter = 123;
const v1 = () => {
  return `${counter}`;
};
const v5 = (type) => {
  return `${type}-${counter}`;
};
const uuidV1 = () => v1();
const uuidForBlockMigration = (workspaceUuid, type = "") => `${type}${Math.random().toString(5).substr(2, 9)}`;
var index = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  "default": uuidV5,
  uuidV5,
  v1,
  v5,
  uuidV1,
  uuidForBlockMigration
}, Symbol.toStringTag, { value: "Module" }));
const generateNewCard = (workspaceUuid, card) => {
  const isCapture = !card.title && !card.text && !card.type;
  const cardType = isCapture ? "capture" : card.type || DEFAULT_CARD_TYPE;
  const text = typeof card.title === "string" && !card.text ? [{ text: card.title, type: "text" }] : void 0;
  return {
    text,
    ...card,
    workspaceUuid,
    type: cardType,
    creationDate: setLastUpdate$1(),
    lastUpdate: setLastUpdate$1()
  };
};
var generateNewCard$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  generateNewCard
}, Symbol.toStringTag, { value: "Module" }));
var mock = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  time: index$1,
  uuid: index,
  generateNewCard: generateNewCard$1
}, Symbol.toStringTag, { value: "Module" }));
const isPersonCard = (card) => (card == null ? void 0 : card.type) === "person" && (card == null ? void 0 : card.status) !== STATUS_TRASH;
const hasSameTitle = ({ title, card }) => {
  var _a;
  return !!(title && ((_a = card.title) == null ? void 0 : _a.toLowerCase()) === title);
};
const hasCommonPersonPropertiesValues = ({
  cardToSearch,
  cardToCompare
}) => SEARCH_PERSON_BY_DESCRIPTOR_ID.find((descriptorId) => {
  var _a, _b, _c, _d, _e, _f;
  const valuesToSearch = (_c = (_b = (_a = cardToSearch == null ? void 0 : cardToSearch.metadata) == null ? void 0 : _a.propertiesValues) == null ? void 0 : _b[descriptorId]) == null ? void 0 : _c.values;
  const valuesToCompare = (_f = (_e = (_d = cardToCompare == null ? void 0 : cardToCompare.metadata) == null ? void 0 : _d.propertiesValues) == null ? void 0 : _e[descriptorId]) == null ? void 0 : _f.values;
  if (!valuesToSearch || !valuesToCompare)
    return false;
  if (valuesToCompare == null ? void 0 : valuesToCompare.find((v) => valuesToSearch.includes(v)))
    return true;
  return false;
});
const searchPersonSelector = (cards) => (card) => {
  var _a;
  if (!cards)
    return;
  const titleLowerCase = (_a = card.title) == null ? void 0 : _a.toLowerCase();
  const associatedPersonCards = Object.values(cards).filter(isPersonCard);
  return associatedPersonCards.find((c) => {
    if (hasSameTitle({ card: c, title: titleLowerCase }))
      return true;
    if (hasCommonPersonPropertiesValues({ cardToSearch: card, cardToCompare: c }))
      return true;
    return false;
  });
};
const isSourceCard = (card) => (card == null ? void 0 : card.type) === CARD_SOURCE_TYPE && (card == null ? void 0 : card.status) !== STATUS_TRASH;
const hasCommonSourcePropertiesValues = ({
  cardToSearch,
  cardToCompare
}) => SEARCH_SOURCE_BY_DESCRIPTOR_ID.find((descriptorId) => {
  var _a, _b, _c, _d, _e, _f;
  const valuesToSearch = (_c = (_b = (_a = cardToSearch == null ? void 0 : cardToSearch.metadata) == null ? void 0 : _a.propertiesValues) == null ? void 0 : _b[descriptorId]) == null ? void 0 : _c.values;
  const valuesToCompare = (_f = (_e = (_d = cardToCompare == null ? void 0 : cardToCompare.metadata) == null ? void 0 : _d.propertiesValues) == null ? void 0 : _e[descriptorId]) == null ? void 0 : _f.values;
  if (!valuesToSearch || !valuesToCompare)
    return false;
  if (valuesToCompare == null ? void 0 : valuesToCompare.find((v) => valuesToSearch.includes(v)))
    return true;
  return false;
});
const searchSourceSelector = (cards) => (card) => {
  if (!cards)
    return;
  const associatedSourceCards = Object.values(cards).filter(isSourceCard);
  return associatedSourceCards.find((c) => {
    return !!hasCommonSourcePropertiesValues({
      cardToSearch: card,
      cardToCompare: c
    });
  });
};
const getCardContextsSelector = (cards) => _.memoize((uuid) => {
  var _a, _b, _c, _d;
  const card = cards[uuid] ? { ...cards[uuid] } : void 0;
  if (!card)
    return DEFAULT_UNDEFINED;
  const sourcesValues = getPropertyValues({ propertyDescriptorId: "source", metadata: card.metadata });
  const meetingValues = getPropertyValues({ propertyDescriptorId: "meeting", metadata: card.metadata });
  const personValues = getPropertyValues({ propertyDescriptorId: "person", metadata: card.metadata });
  const sourceUuid = sourcesValues[sourcesValues.length - 1];
  const meetingUuid = meetingValues[meetingValues.length - 1];
  const personUuid = personValues[personValues.length - 1];
  return {
    source: sourceUuid ? cards[sourceUuid] : DEFAULT_UNDEFINED,
    meeting: meetingUuid ? cards[meetingUuid] : DEFAULT_UNDEFINED,
    person: personUuid ? cards[personUuid] : DEFAULT_UNDEFINED,
    geoloc: (_b = (_a = card.metadata) == null ? void 0 : _a.data) == null ? void 0 : _b.geoloc,
    timestamp: (_d = (_c = card.metadata) == null ? void 0 : _c.data) == null ? void 0 : _d.timestamp
  };
});
const isValueInDescriptorOptions = ({ descriptor, value }) => !descriptor.options || descriptor.options.find((o) => o.id === value);
const shouldValueBeInDescriptorOptions = (descriptorType) => descriptorType !== "date";
const formatValueAsProperty = ({
  propertyValue,
  descriptor,
  cards,
  card
}) => {
  var _a;
  return (_a = propertyValue == null ? void 0 : propertyValue.values) == null ? void 0 : _a.reduce((acc, value) => {
    var _a2, _b, _c, _d, _e, _f;
    const link = (_a2 = descriptor == null ? void 0 : descriptor.generateLink) == null ? void 0 : _a2.call(descriptor, { value, metadata: card.metadata });
    if (shouldValueBeInDescriptorOptions(descriptor.type) && !isValueInDescriptorOptions({ descriptor, value }))
      return acc;
    if (descriptor && isDescriptorLinkToACard(descriptor.type)) {
      const card2 = cards[value];
      if (card2 == null) {
        return acc;
      }
      return acc.concat({
        id: card2.uuid,
        name: (_b = card2.title) != null ? _b : "",
        pictureUrl: (_c = card2.pictureUrl) != null ? _c : "",
        link
      });
    }
    return acc.concat({
      name: (_f = (_e = (_d = descriptor.options) == null ? void 0 : _d.find((o) => o.id === value)) == null ? void 0 : _e.name) != null ? _f : value,
      id: value,
      link
    });
  }, []);
};
const getCardPropertiesSelector = (cards) => _.memoize((uuid) => {
  var _a;
  if (!uuid)
    return DEFAULT_ARRAY;
  const card = cards[uuid];
  if (!card)
    return DEFAULT_ARRAY;
  const parentCard = card.parentCardUuid ? cards[card.parentCardUuid] : void 0;
  if (!((_a = card.metadata) == null ? void 0 : _a.propertiesValues))
    return DEFAULT_ARRAY;
  const valuesKeys = _.uniq([
    ...Object.keys(card.metadata.propertiesValues),
    ...getDefaultPropsByCardType(card.type)
  ]);
  const visibleProps = valuesKeys.filter(
    (id) => {
      var _a2, _b, _c, _d, _e, _f, _g, _h;
      return ((_a2 = allDefaultPropertiesDescriptors[id]) == null ? void 0 : _a2.alwaysShow) || ((_d = (_c = (_b = card.metadata) == null ? void 0 : _b.propertiesValues[id]) == null ? void 0 : _c.values) == null ? void 0 : _d.length) && !((_h = (_e = allDefaultPropertiesDescriptors[id]) == null ? void 0 : _e.isHidden) == null ? void 0 : _h.call(_e, {
        id,
        values: (_g = (_f = card.metadata) == null ? void 0 : _f.propertiesValues[id]) == null ? void 0 : _g.values
      }));
    }
  );
  const sortedProps = visibleProps.sort((a, b) => {
    var _a2, _b, _c, _d, _e, _f;
    if (((_a2 = allDefaultPropertiesDescriptors[a]) == null ? void 0 : _a2.position) !== 0 && !((_b = allDefaultPropertiesDescriptors[b]) == null ? void 0 : _b.position))
      return -1;
    if (!((_c = allDefaultPropertiesDescriptors[a]) == null ? void 0 : _c.position) && ((_d = allDefaultPropertiesDescriptors[b]) == null ? void 0 : _d.position) !== 0)
      return 1;
    return Number((_e = allDefaultPropertiesDescriptors[a]) == null ? void 0 : _e.position) - Number((_f = allDefaultPropertiesDescriptors[b]) == null ? void 0 : _f.position);
  });
  return sortedProps.map((id) => {
    var _a2, _b, _c, _d;
    const descriptor = getDescriptor({ metadata: parentCard == null ? void 0 : parentCard.metadata, descriptorId: id });
    const propertyValue = (_a2 = card.metadata) == null ? void 0 : _a2.propertiesValues[id];
    const values = formatValueAsProperty({ propertyValue, descriptor, cards, card });
    return {
      id,
      name: (_b = descriptor == null ? void 0 : descriptor.name) != null ? _b : "",
      type: descriptor == null ? void 0 : descriptor.type,
      cardUuid: uuid,
      parentCardUuid: card.parentCardUuid,
      workspaceUuid: card.workspaceUuid,
      values: (values == null ? void 0 : values.length) ? values : DEFAULT_ARRAY,
      options: (_d = (_c = descriptor == null ? void 0 : descriptor.options) == null ? void 0 : _c.filter((o) => !o.isDefault).map((option) => ({ name: option.name, id: option.id }))) != null ? _d : DEFAULT_ARRAY
    };
  });
});
const getCardByUuidSelector = (cards) => _.memoize((uuid) => {
  var _a;
  if (!uuid)
    return DEFAULT_UNDEFINED;
  const card = cards[uuid];
  if (!card)
    return DEFAULT_UNDEFINED;
  return { ...card, summarizeContent: (_a = card.summarizeContent) != null ? _a : summarize(getNestedTextFromCard({ card, cards })) };
});
const hasNestedCards = (cards) => _.memoize((uuid) => {
  var _a;
  const card = uuid ? cards[uuid] : void 0;
  const childrenIds = (_a = card == null ? void 0 : card.childrenIds) != null ? _a : DEFAULT_ARRAY;
  const nestedCards = childrenIds.map((nestedCardUuid) => cards[nestedCardUuid]).filter((nestedCard) => !!nestedCard && ["note", "task"].includes(nestedCard.type));
  return nestedCards.length > 0;
});
const isTrashSelector = (cards) => _.memoize((uuid) => {
  var _a, _b, _c, _d;
  if (!cards)
    return DEFAULT_UNDEFINED;
  if (((_a = cards[uuid]) == null ? void 0 : _a.status) === STATUS_TRASH)
    return true;
  let previousUuid = uuid;
  const previousParentCardUuid = [];
  while (previousUuid && !!((_b = cards[previousUuid]) == null ? void 0 : _b.parentCardUuid) && !previousParentCardUuid.includes(previousUuid)) {
    previousParentCardUuid.push(previousUuid);
    if (((_c = cards[previousUuid]) == null ? void 0 : _c.status) === STATUS_TRASH) {
      return true;
    }
    previousUuid = (_d = cards[previousUuid]) == null ? void 0 : _d.parentCardUuid;
  }
  return DEFAULT_FALSE;
});
const isCardDeletedOrTrashed = ({ cardUuid, cards }) => isTrashSelector(cards)(cardUuid);
const blockUuidInContent = "7c3da8e5-48e8-51bd-abf2-a44977edd1a2";
const tableHTMLWithContentFirstNode = `<div>ici</div>`;
const tableHTMLEmpty = "<table><tbody><tr><td><div></div></td></tr></tbody></table>";
const testHtmlWithCustomReactComponentFirstNode = "<h1>Andrew Homeyer</h1>";
const cardContextExpectedResult = `<p>When we think about security on macOS and the tools used by offensive actors, whether those are real in the wild attacks or <a href="https://www.sentinelone.com/blog/macos-red-team-spoofing-privileged-helpers-and-others-to-gain-root/" target="_blank">red team exercises</a>, we tend to think of things like python scripts, <a href="https://www.sentinelone.com/blog/scripting-macs-with-malice-how-shlayer-and-other-malware-installers-infect-macos/" target="_blank">shell scripts</a>, malicious documents, <a href="https://www.sentinelone.com/blog/adware-apple-google-extensions-put-users-at-risk/" target="_blank">shady extensions</a> and of course, the <a href="https://www.sentinelone.com/blog/weakest-link-admins-get-phished-macos-osx-dummy-malware/" target="_blank">fake</a>, <a href="https://www.sentinelone.com/blog/macos-malware-2019-first-six-months/" target="_blank">doctored</a> or <a href="https://www.sentinelone.com/blog/mac-malware-osx-dok-is-back-actively-infecting-victims/" target="_blank">trojan</a> application bundle. There is much less attention in the security field on AppleScript \u2013 a built-in macOS technology \u2013 despite the fact it\u2019s been around for as long as Python and predates macOS 10 itself by 8 or 9 years.</p><p>As I\u2019ll show in this post, AppleScript is widely used by offensive actors. This includes its use in adware, its use for tasks such as <a href="https://attack.mitre.org/software/S0281/" target="_blank">persistence</a>, <a href="https://www.virustotal.com/gui/file/ab4596d3f8347d447051eb4e4075e04c37ce161514b4ce3fae91010aac7ae97f/content/strings" target="_blank">anti-analysis</a>, browser hijacking, <a href="https://www.sentinelone.com/blog/macos-red-team-spoofing-privileged-helpers-and-others-to-gain-root/" target="_blank">spoofing</a> and more. Worryingly, given the lack of attention paid to AppleScript in the research community, that is all without even leveraging some of AppleScript\u2019s most powerful or unique features, some of which we\u2019ll cover below (others I\u2019ve written about before <a href="https://www.sentinelone.com/blog/macos-red-team-calling-apple-apis-without-building-binaries/" target="_blank">here</a>).</p><p></p><h2>Why Have the Good Guys Ignored AppleScript?</h2><p>Unlike <a href="https://www.sentinelone.com/blog/scripting-macs-with-malice-how-shlayer-and-other-malware-installers-infect-macos/" target="_blank">Bash and other shell</a> languages, and unlike Python, a cross-platform, beginner-friendly scripting language that has achieved widespread adoption and praise, AppleScript is a language peculiar to macOS; not only can you NOT use it on other Desktop operating systems like Windows and Linux, you can\u2019t even use it on Apple\u2019s other operating systems like iOS and iPadOS.</p><p>As a language, AppleScript has a reputation for being quirky, slow and difficult to develop even simple scripts with. It\u2019s quirky because it attempts to use \u201Cnatural language\u201D but in a grammar that is entirely artificial, often inconsistent and frustratingly unintuitive. It\u2019s also incredibly verbose. Compare the code for the simple task of counting the number of items in <code>/usr/bin</code> directory. As ever, a shell script will always be the most concise:</p><pre>ls -l /usr/bin | wc -l</pre><p>Python is a little more verbose, but still fairly clean and familiar:</p><pre>#!/usr/bin/pythonimport ospath, dirs, files = next(os.walk("/usr/bin"))print len(files)</pre><p>The AppleScript version, however, is something of an entirely different nature.</p><pre>tell application "System Events"	set theFiles to name of every file of folder "bin" of folder "usr" of startup disk	count of theFilesend tell</pre><p>AppleScript is also slow in execution because, among other things, the underlying technology involves constructing and sending Apple Events through an archaic interface called the Apple Event Manager that was written for Apple\u2019s System 7 operating system (released in 1991) and not optimized for performance even back then.</p><p>Source: AppleScript Overview,  \xA9 2002, 2007 Apple Inc.</p><p>And it\u2019s historically been difficult to develop scripts with AppleScript because most people who come to it will attempt to use the free, built-in but notoriously spartan (Apple)Script Editor.app, which lacks almost every and any feature developers normally expect and need. There\u2019s no debugger, there\u2019s no variable introspection, there\u2019s no code snippets or effective code completion, to name just a few missing features.</p><p></p><p>Until recently, the only 3rd party alternative to Script Editor was priced at $200 and had a time-limited, 20-day demo period.</p><p>In short, the investment in time, effort and money required to produce something that still, after all that, can only be used on the macOS Desktop, effectively puts AppleScript at the bottom of the list for most people when it comes to choosing a useful or productive programming language. As a result, despite being with us for nearly 30 years, AppleScript is barely used by the majority of Mac admins, Mac developers or Mac end users. Indeed, AppleScript may have a good claim to being the Most Unloved and Unlovable Programming Language Ever.</p><h2>So Why Do the Bad Guys Love Using AppleScript?</h2><p>AppleScript was designed for automation and interapplication communication: the goal being to allow ordinary users to chain together repetitive tasks and execute them without further user interaction. For example, you can have Mail.app automatically trigger a script when it receives an email from a certain sender, or with a particular keyword in the subject line or content, extract whatever details you want from the email, and then populate a database in Excel or Numbers with the desired information, formatted and sorted on-the-fly as the data comes in. There\u2019s no need for the user to be involved in any of this once the script is set up.</p><p>And as it turns out, automating interapplication communication and sidestepping user interaction is a godsend for malware authors. What could be more useful than bending popular applications like email clients, web browsers and the Microsoft Office suite to your will without needing to involve the user (<em>aka</em> in this scenario, <strong>the victim</strong>)?</p><p>And so, despite its general lack of appeal in almost all possible audiences for a scripting or programming language, there is one audience that does use AppleScript widely, if not particularly artfully or cleverly, and that is <a href="https://www.sentinelone.com/blog/threat-actor-basics-understanding-5-main-threat-types/" target="_blank">threat actors</a>. Let\u2019s look at some examples.</p><div>How Shlayer and Other Malware Trojans Infect macOS</div><div>Learn from Phil Stokes how to tell real Adobe Flash Players from fake ones</div><a href="https://assets.sentinelone.com/macos-reverse/how-shlayer-and-othe-1" target="_blank">Watch Now </a><h2>Recent Examples of AppleScript in macOS malware</h2><p>A recent browser hijacker targeting Safari installs a hidden LaunchAgent that, via a shell script, loads, compiles and executes AppleScript.</p><p>It starts with a shell installer script packaged inside a DMG that\u2019s supposed to contain an application called \u2018PDFConverter4u\u2019. But in a hidden <code>.assets</code> folder on the disk image is a first stage shell script:</p><p><code>5f198e82c0cf9a9f7d7a8d01273a6ad75a17a95960d8996dcdd028922b3d97bc</code></p><p></p><p>This unpacks and executes a second stage shell script:</p><p><code>55529224e9f70f5cab007e2ca98f6aec5cf31eb923fdfc09f60c01cc45c80666</code></p><p></p><p>Which eventually produces the hidden launch agent that executes another shell script containing the following AppleScript code, launched via <code>osascript</code>.</p><p><code>cdaa2121d79031cf39159198dfe64d3695a9c99ff7c3478a0b8953ade9052ecc</code></p><p></p><p>The purpose of the AppleScript is to replace the user\u2019s search query on popular search engines google, bing and yahoo, with one provided by the attacker\u2019s shell script. It\u2019s a quick and easy way for bad actors to make money out of clicks which negatively impacts the victim\u2019s productivity.</p><p>While this particular sample comes packed in a separate file, many others write their AppleScript directly into a MachO binary, either in plain text strings or in obfuscated <a href="https://www.sentinelone.com/blog/guide-encode-decoded-base64/" target="_blank">base64</a> or similar encoding.</p><p>The following strings extracted from a Bundlore installer show that the code tries to force enable JavaScript execution in Google Chrome, then uses AppleScript to execute it in the active tab of the browser\u2019s front window.</p><p>Strings from <code>41e0d31d52cb93f6a5020a278e8f360a6e134e6cc7092b4a5e575ac8b96a8d74</code></p><p></p><p>The next sample is a variant of a Pirrit malware.</p><p><code>21331ccee215801ca682f1764f3e37ff806e7510ded5576c0fb4d514b4cf2b7c</code></p><p></p><p>The authors use both plain text AppleScript and base64 encoded AppleScript, targeting Safari, Chrome and Firefox browsers.</p><p></p><p>Here\u2019s some of the decoded base64 targeting Firefox and attempting to perform automated keystrokes to copy the current URL to the user\u2019s clipboard before replacing the URL to that of the attacker\u2019s choice:</p><p></p><h2>Using AppleScript Without Apple Events</h2><p>Speaking of base64, the next example illustrates something that many ordinary users and developers have overlooked about AppleScript, but which offensive actors have not: you can use AppleScript to execute any other kind of script, including python scripts like this one which drops the Empire exploit kit.</p><p></p><p>And what\u2019s true of AppleScript and Python, is true of AppleScript and Perl, AppleScript and Bash and indeed AppleScript and absolutely any command line tool at all: you can call them all and bring their functionality into your AppleScript and combine that with other utilities.</p><p></p><p>The examples above all use what we might call \u2018vanilla AppleScript\u2019. That is, the native AppleScript language that\u2019s been around since the early days of the platform. But starting in Yosemite, 10.10 and continuing up to and including the most recent version of macOS, AppleScript has been given increasing power through access to Cocoa frameworks, and this opens up the possibility of creating full-blown, powerful programs and applications with nothing other than AppleScript itself. Objective C executed through AppleScript is, speed-wise, more or less on a par with Objective C executed in a <a href="https://www.sentinelone.com/blog/how-to-reverse-macos-malware-part-two/" target="_blank">MachO</a> binary.</p><p>And interestingly, although we haven\u2019t seen threat actors making use of these powerful capabilities so far, there\u2019s at least two reasons why we may well do so in the future: first to avoid detection, and second, because of the easy availability of a good development environment.</p><div>How Shlayer and Other Malware Trojans Infect macOS</div><div>Learn from Phil Stokes how to tell real Adobe Flash Players from fake ones</div><a href="https://assets.sentinelone.com/macos-reverse/how-shlayer-and-othe-1" target="_blank">Watch Now </a><h2>Using AppleScript to Avoid Detection</h2><p>Avoiding detection on execution is a primary objective for all malware (even <a href="https://www.sentinelone.com/blog/what-is-the-true-cost-of-a-ransomware-attack-6-factors-to-consider/" target="_blank">ransomware</a>, which doesn\u2019t want to get noticed until after execution). AppleScript offers offensive actors a plethora of ways to execute. In addition to simply executing a <code>.scrpt</code> file, you can run AppleScripts from <a href="https://www.n00py.io/2016/10/using-email-for-persistence-on-os-x/" target="_blank">Mail rules</a>, from a shell script, in memory, from the command line, from within a MachO, in a plain text, uncompiled file, from an Automator workflow, from a <a href="https://posts.specterops.io/folder-actions-for-persistence-on-macos-8923f222343d" target="_blank">Folder Action</a>, a Finder Service or from a Calendar event.</p><p></p><p>Because of AppleScript\u2019s ability to execute Objective C code without needing a compiled binary, this opens up a number of interesting attack possibilities. It also potentially opens up the ability to bypass detection tools based on Apple\u2019s new kextless security framework introduced in <a href="https://www.sentinelone.com/blog/7-big-security-surprises-coming-to-macos-10-15-catalina/" target="_blank">macOS Catalina 10.15</a>.</p><p>In an excellent post by Cedric Owens called <a href="https://medium.com/red-teaming-with-a-blue-team-mentaility/taking-the-macos-endpoint-security-framework-for-a-quick-spin-802a462dba06" target="_blank">Taking the macOS Endpoint Security Framework For A Quick Spin</a>, Owens sets out to test what can and cannot be detected using three recently developed 3rd-party security tools that leverage the new <a href="https://developer.apple.com/documentation/endpointsecurity" target="_blank">Apple Endpoint Security framework</a>.</p><p>One of the interesting things that Owens found was that if you tried capturing the user\u2019s clipboard via <code>osascript</code> and vanilla AppleScript, this activity would be easily picked up by all the tools he was testing.</p><pre>osascript -e 'the clipboard'</pre><p>However, when using the native Cocoa API, NSPasteboard, none of the Endpoint Security framework-powered tools Owens tested appeared to capture that activity. But now, of course, we can execute NSPasteboard natively from AppleScript, too!</p><p></p><p>Notice that our one line, simple but also detectable <code>osascript</code> has turned into about 14 lines of complex-looking AppleScript-ObjC. Few people, certainly not I, would want to try and construct that kind of code in Script Editor.</p><p>However, the problem of developing complex AppleScripts is now more or less a thing of the past. The <a href="https://latenightsw.com/" target="_blank">3rd party alternative</a> mentioned earlier in this post now has an unlimited free trial version and retails at half of its old price; more importantly, it also allows you to drag and drop a great deal of boilerplate code like that used in the script above straight into your scripts. And it provides developer-friendly functionality like code completion and API lookups that really take the pain out of developing AppleScript code.</p><p>Let\u2019s look at another example. A lot of offensive operations want to avoid targets that are running particular software. Little Snitch is a prime example, various VM software is another. We can easily get a list of running apps by name and test for those, again directly by calling into Cocoa APIs, this time via NSWorkspace.</p><p></p><p>If we just want a true/false test for the existence of specific apps, we can just put the app names in a list and return true on the first hit.</p><p></p><p>In other words, by leveraging AppleScript\u2019s hook into Cocoa frameworks, we can execute native code without the overhead of building MachO binaries or MachO apps (although you can do both of those with AppleScript, too!). We can do this filelessly so that we don\u2019t get caught by new \u2018kextless\u2019 tools such as those tested by Owens, and we can execute this code in far more ways than any other kind of code available on macOS, whether that\u2019s shell scripts, Python scripts or native macOS bundles.</p><h2>Conclusion</h2><p>The upshot here is that the main reasons why the good guys have typically eschewed AppleScript are in fact no longer relevant or true. Since we\u2019ve already seen threat actors taking advantage of AppleScript despite those obstacles in the past, it\u2019s only reasonable to assume that they may delve deeper into what this unique language has to offer in the future. Thanks to the native hook into Objective C and the powerful Cocoa frameworks, the variety of execution methods and now the availability of an excellent, free-to-use IDE, AppleScript has become a tool that is powerful, versatile and easy-to-develop with.</p><p>Attackers will always look to exploit the things defenders ignore, and to say that AppleScript has been ignored by the security community thus far is an understatement. I have <a href="https://www.sentinelone.com/blog/macos-red-team-calling-apple-apis-without-building-binaries/" target="_blank">elsewhere</a> described AppleScript as \u201Cthe PowerShell of macOS\u201D. Certainly, it\u2019s time we stopped thinking of AppleScript as the Most Unlovable Programming Language Ever and recognize that it may actually be the One macOS Programming Language to Rule Them All.</p><hr><p><strong>Like this article? Follow us on <a href="https://www.linkedin.com/company/2886771/" target="_blank">LinkedIn</a>, <a href="https://twitter.com/SentinelOne" target="_blank">Twitter</a>, <a href="https://goo.gl/e5C9f4" target="_blank">YouTube</a> or <a href="https://www.facebook.com/SentinelOne" target="_blank">Facebook</a> to see the content we post.</strong></p><h3>Read more about Cyber Security</h3><ul><li><a href="https://www.sentinelone.com/blog/covid-19-outbreak-defending-against-the-psychology-of-fear-uncertainty-and-doubt/" target="_blank">COVID-19 Outbreak | Defending Against the Psychology of Fear, Uncertainty and Doubt</a></li><li><a href="https://www.sentinelone.com/blog/macos-malware-researchers-how-to-bypass-xprotect-on-catalina/" target="_blank">macOS Malware Researchers | How To Bypass XProtect on Catalina</a></li><li><a href="https://www.sentinelone.com/blog/covid-19-outbreak-employees-working-from-home-its-time-to-prepare/" target="_blank">COVID-19 Outbreak | Employees Working from Home? It\u2019s Time to Prepare</a></li><li><a href="https://www.sentinelone.com/blog/mind-games-the-evolving-psychology-of-ransom-notes/" target="_blank">Mind Games | The Evolving Psychology of Ransom Notes</a></li><li><a href="https://www.sentinelone.com/blog/business-email-compromise-what-is-bec-and-how-can-you-defend-against-it/" target="_blank">Business Email Compromise | What is BEC (And How Can You Defend Against It)?</a></li><li><a href="https://www.sentinelone.com/blog/what-is-hacktivism-and-why-should-enterprise-care/" target="_blank">What is Hacktivism? And Why Should Enterprise Care?</a></li></ul><ul class="todo-list"><li><label class="todo-list__label"><input type="checkbox" disabled="disabled"> </label></li></ul><hr><ol><li>dddd</li></ol><ul><li>dddd</li></ul><h1>hello</h1><h2>hello</h2><h3>hello</h3><h4>hello</h4><h5>hello</h5><h6>hello</h6>`;
const textHTMLFirstBlockElement = `<span>When we think about security on macOS and the tools used by offensive actors, whether those are real in the wild attacks or <a href="https://www.sentinelone.com/blog/macos-red-team-spoofing-privileged-helpers-and-others-to-gain-root/">red team exercises</a>, we tend to think of things like python scripts, <a href="https://www.sentinelone.com/blog/scripting-macs-with-malice-how-shlayer-and-other-malware-installers-infect-macos/">shell scripts</a>, malicious documents, <a href="https://www.sentinelone.com/blog/adware-apple-google-extensions-put-users-at-risk/">shady extensions</a> and of course, the <a href="https://www.sentinelone.com/blog/weakest-link-admins-get-phished-macos-osx-dummy-malware/">fake</a>, <a href="https://www.sentinelone.com/blog/macos-malware-2019-first-six-months/">doctored</a> or <a href="https://www.sentinelone.com/blog/mac-malware-osx-dok-is-back-actively-infecting-victims/">trojan</a> application bundle. There is much less attention in the security field on AppleScript \u2013 a built-in macOS technology \u2013 despite the fact it\u2019s been around for as long as Python and predates macOS 10 itself by 8 or 9 years.</span>`;
const testHTML = `<p>${textHTMLFirstBlockElement}</p><p><span>As I\u2019ll show in this post, AppleScript is widely used by offensive actors. This includes its use in adware, its use for tasks such as <a href="https://attack.mitre.org/software/S0281/">persistence</a>, <a href="https://www.virustotal.com/gui/file/ab4596d3f8347d447051eb4e4075e04c37ce161514b4ce3fae91010aac7ae97f/content/strings">anti-analysis</a>, browser hijacking, <a href="https://www.sentinelone.com/blog/macos-red-team-spoofing-privileged-helpers-and-others-to-gain-root/">spoofing</a> and more. Worryingly, given the lack of attention paid to AppleScript in the research community, that is all without even leveraging some of AppleScript\u2019s most powerful or unique features, some of which we\u2019ll cover below (others I\u2019ve written about before <a href="https://www.sentinelone.com/blog/macos-red-team-calling-apple-apis-without-building-binaries/">here</a>).</span></p><p></p><h2>Why Have the Good Guys Ignored AppleScript?</h2><p><span>Unlike <a href="https://www.sentinelone.com/blog/scripting-macs-with-malice-how-shlayer-and-other-malware-installers-infect-macos/">Bash and other shell</a> languages, and unlike Python, a cross-platform, beginner-friendly scripting language that has achieved widespread adoption and praise, AppleScript is a language peculiar to macOS; not only can you NOT use it on other Desktop operating systems like Windows and Linux, you can\u2019t even use it on Apple\u2019s other operating systems like iOS and iPadOS.</span></p><p><span>As a language, AppleScript has a reputation for being quirky, slow and difficult to develop even simple scripts with. It\u2019s quirky because it attempts to use \u201Cnatural language\u201D but in a grammar that is entirely artificial, often inconsistent and frustratingly unintuitive. It\u2019s also incredibly verbose. Compare the code for the simple task of counting the number of items in <code>/usr/bin</code> directory. As ever, a shell script will always be the most concise:</span></p><pre>ls -l /usr/bin | wc -l</pre><p><span>Python is a little more verbose, but still fairly clean and familiar:</span></p><pre>#!/usr/bin/pythonimport ospath, dirs, files = next(os.walk("/usr/bin"))print len(files)</pre><p><span>The AppleScript version, however, is something of an entirely different nature.</span></p><pre>tell application "System Events"	set theFiles to name of every file of folder "bin" of folder "usr" of startup disk	count of theFilesend tell</pre><p><span>AppleScript is also slow in execution because, among other things, the underlying technology involves constructing and sending Apple Events through an archaic interface called the Apple Event Manager that was written for Apple\u2019s System 7 operating system (released in 1991) and not optimized for performance even back then.</span></p><p><br /><span>Source: AppleScript Overview,  \xA9 2002, 2007 Apple Inc.</span></p><p><span>And it\u2019s historically been difficult to develop scripts with AppleScript because most people who come to it will attempt to use the free, built-in but notoriously spartan (Apple)Script Editor.app, which lacks almost every and any feature developers normally expect and need. There\u2019s no debugger, there\u2019s no variable introspection, there\u2019s no code snippets or effective code completion, to name just a few missing features.</span></p><p></p><p><span>Until recently, the only 3rd party alternative to Script Editor was priced at $200 and had a time-limited, 20-day demo period.</span></p><p><span>In short, the investment in time, effort and money required to produce something that still, after all that, can only be used on the macOS Desktop, effectively puts AppleScript at the bottom of the list for most people when it comes to choosing a useful or productive programming language. As a result, despite being with us for nearly 30 years, AppleScript is barely used by the majority of Mac admins, Mac developers or Mac end users. Indeed, AppleScript may have a good claim to being the Most Unloved and Unlovable Programming Language Ever.</span></p><h2>So Why Do the Bad Guys Love Using AppleScript?</h2><p><span>AppleScript was designed for automation and interapplication communication: the goal being to allow ordinary users to chain together repetitive tasks and execute them without further user interaction. For example, you can have Mail.app automatically trigger a script when it receives an email from a certain sender, or with a particular keyword in the subject line or content, extract whatever details you want from the email, and then populate a database in Excel or Numbers with the desired information, formatted and sorted on-the-fly as the data comes in. There\u2019s no need for the user to be involved in any of this once the script is set up.</span></p><p><span>And as it turns out, automating interapplication communication and sidestepping user interaction is a godsend for malware authors. What could be more useful than bending popular applications like email clients, web browsers and the Microsoft Office suite to your will without needing to involve the user (<em>aka</em> in this scenario, <strong>the victim</strong>)?</span></p><p><span>And so, despite its general lack of appeal in almost all possible audiences for a scripting or programming language, there is one audience that does use AppleScript widely, if not particularly artfully or cleverly, and that is <a href="https://www.sentinelone.com/blog/threat-actor-basics-understanding-5-main-threat-types/">threat actors</a>. Let\u2019s look at some examples.</span></p><div><div><div><div><div>How Shlayer and Other Malware Trojans Infect macOS</div><div>Learn from Phil Stokes how to tell real Adobe Flash Players from fake ones</div><div><a href="https://assets.sentinelone.com/macos-reverse/how-shlayer-and-othe-1"><span>Watch Now</span> </a></div></div></div></div></div><h2>Recent Examples of AppleScript in macOS malware</h2><p><span>A recent browser hijacker targeting Safari installs a hidden LaunchAgent that, via a shell script, loads, compiles and executes AppleScript.</span></p><p><span>It starts with a shell installer script packaged inside a DMG that\u2019s supposed to contain an application called \u2018PDFConverter4u\u2019. But in a hidden <code>.assets</code> folder on the disk image is a first stage shell script:</span></p><p><code>5f198e82c0cf9a9f7d7a8d01273a6ad75a17a95960d8996dcdd028922b3d97bc</code></p><p></p><p><span>This unpacks and executes a second stage shell script:</span></p><p><code>55529224e9f70f5cab007e2ca98f6aec5cf31eb923fdfc09f60c01cc45c80666</code></p><p></p><p><span>Which eventually produces the hidden launch agent that executes another shell script containing the following AppleScript code, launched via <code>osascript</code>.</span></p><p><code>cdaa2121d79031cf39159198dfe64d3695a9c99ff7c3478a0b8953ade9052ecc</code></p><p></p><p><span>The purpose of the AppleScript is to replace the user\u2019s search query on popular search engines google, bing and yahoo, with one provided by the attacker\u2019s shell script. It\u2019s a quick and easy way for bad actors to make money out of clicks which negatively impacts the victim\u2019s productivity.</span></p><p><span>While this particular sample comes packed in a separate file, many others write their AppleScript directly into a MachO binary, either in plain text strings or in obfuscated <a href="https://www.sentinelone.com/blog/guide-encode-decoded-base64/">base64</a> or similar encoding.</span></p><p><span>The following strings extracted from a Bundlore installer show that the code tries to force enable JavaScript execution in Google Chrome, then uses AppleScript to execute it in the active tab of the browser\u2019s front window.</span></p><p><span>Strings from <code>41e0d31d52cb93f6a5020a278e8f360a6e134e6cc7092b4a5e575ac8b96a8d74</code></span></p><p></p><p><span>The next sample is a variant of a Pirrit malware.</span></p><p><code>21331ccee215801ca682f1764f3e37ff806e7510ded5576c0fb4d514b4cf2b7c</code></p><p></p><p><span>The authors use both plain text AppleScript and base64 encoded AppleScript, targeting Safari, Chrome and Firefox browsers.</span></p><p></p><p><span>Here\u2019s some of the decoded base64 targeting Firefox and attempting to perform automated keystrokes to copy the current URL to the user\u2019s clipboard before replacing the URL to that of the attacker\u2019s choice:</span></p><p></p><h2>Using AppleScript Without Apple Events</h2><p><span>Speaking of base64, the next example illustrates something that many ordinary users and developers have overlooked about AppleScript, but which offensive actors have not: you can use AppleScript to execute any other kind of script, including python scripts like this one which drops the Empire exploit kit.</span></p><p></p><p><span>And what\u2019s true of AppleScript and Python, is true of AppleScript and Perl, AppleScript and Bash and indeed AppleScript and absolutely any command line tool at all: you can call them all and bring their functionality into your AppleScript and combine that with other utilities.</span></p><p></p><p><span>The examples above all use what we might call \u2018vanilla AppleScript\u2019. That is, the native AppleScript language that\u2019s been around since the early days of the platform. But starting in Yosemite, 10.10 and continuing up to and including the most recent version of macOS, AppleScript has been given increasing power through access to Cocoa frameworks, and this opens up the possibility of creating full-blown, powerful programs and applications with nothing other than AppleScript itself. Objective C executed through AppleScript is, speed-wise, more or less on a par with Objective C executed in a <a href="https://www.sentinelone.com/blog/how-to-reverse-macos-malware-part-two/">MachO</a> binary.</span></p><p><span>And interestingly, although we haven\u2019t seen threat actors making use of these powerful capabilities so far, there\u2019s at least two reasons why we may well do so in the future: first to avoid detection, and second, because of the easy availability of a good development environment.</span></p><div><div><div><div><div>How Shlayer and Other Malware Trojans Infect macOS</div><div>Learn from Phil Stokes how to tell real Adobe Flash Players from fake ones</div><div><a href="https://assets.sentinelone.com/macos-reverse/how-shlayer-and-othe-1"><span>Watch Now</span> </a></div></div></div></div></div><h2>Using AppleScript to Avoid Detection</h2><p><span>Avoiding detection on execution is a primary objective for all malware (even <a href="https://www.sentinelone.com/blog/what-is-the-true-cost-of-a-ransomware-attack-6-factors-to-consider/">ransomware</a>, which doesn\u2019t want to get noticed until after execution). AppleScript offers offensive actors a plethora of ways to execute. In addition to simply executing a <code>.scrpt</code> file, you can run AppleScripts from <a href="https://www.n00py.io/2016/10/using-email-for-persistence-on-os-x/">Mail rules</a>, from a shell script, in memory, from the command line, from within a MachO, in a plain text, uncompiled file, from an Automator workflow, from a <a href="https://posts.specterops.io/folder-actions-for-persistence-on-macos-8923f222343d">Folder Action</a>, a Finder Service or from a Calendar event.</span></p><p></p><p><span>Because of AppleScript\u2019s ability to execute Objective C code without needing a compiled binary, this opens up a number of interesting attack possibilities. It also potentially opens up the ability to bypass detection tools based on Apple\u2019s new kextless security framework introduced in <a href="https://www.sentinelone.com/blog/7-big-security-surprises-coming-to-macos-10-15-catalina/">macOS Catalina 10.15</a>.</span></p><p><span>In an excellent post by Cedric Owens called <a href="https://medium.com/red-teaming-with-a-blue-team-mentaility/taking-the-macos-endpoint-security-framework-for-a-quick-spin-802a462dba06">Taking the macOS Endpoint Security Framework For A Quick Spin</a>, Owens sets out to test what can and cannot be detected using three recently developed 3rd-party security tools that leverage the new <a href="https://developer.apple.com/documentation/endpointsecurity">Apple Endpoint Security framework</a>.</span></p><p><span>One of the interesting things that Owens found was that if you tried capturing the user\u2019s clipboard via <code>osascript</code> and vanilla AppleScript, this activity would be easily picked up by all the tools he was testing.</span></p><pre>osascript -e 'the clipboard'</pre><p><span>However, when using the native Cocoa API, NSPasteboard, none of the Endpoint Security framework-powered tools Owens tested appeared to capture that activity. But now, of course, we can execute NSPasteboard natively from AppleScript, too!</span></p><p></p><p><span>Notice that our one line, simple but also detectable <code>osascript</code> has turned into about 14 lines of complex-looking AppleScript-ObjC. Few people, certainly not I, would want to try and construct that kind of code in Script Editor.</span></p><p><span>However, the problem of developing complex AppleScripts is now more or less a thing of the past. The <a href="https://latenightsw.com/">3rd party alternative</a> mentioned earlier in this post now has an unlimited free trial version and retails at half of its old price; more importantly, it also allows you to drag and drop a great deal of boilerplate code like that used in the script above straight into your scripts. And it provides developer-friendly functionality like code completion and API lookups that really take the pain out of developing AppleScript code.</span></p><p><span>Let\u2019s look at another example. A lot of offensive operations want to avoid targets that are running particular software. Little Snitch is a prime example, various VM software is another. We can easily get a list of running apps by name and test for those, again directly by calling into Cocoa APIs, this time via NSWorkspace.</span></p><p></p><p><span>If we just want a true/false test for the existence of specific apps, we can just put the app names in a list and return true on the first hit.</span></p><p></p><p><span>In other words, by leveraging AppleScript\u2019s hook into Cocoa frameworks, we can execute native code without the overhead of building MachO binaries or MachO apps (although you can do both of those with AppleScript, too!). We can do this filelessly so that we don\u2019t get caught by new \u2018kextless\u2019 tools such as those tested by Owens, and we can execute this code in far more ways than any other kind of code available on macOS, whether that\u2019s shell scripts, Python scripts or native macOS bundles.</span></p><h2>Conclusion</h2><p><span>The upshot here is that the main reasons why the good guys have typically eschewed AppleScript are in fact no longer relevant or true. Since we\u2019ve already seen threat actors taking advantage of AppleScript despite those obstacles in the past, it\u2019s only reasonable to assume that they may delve deeper into what this unique language has to offer in the future. Thanks to the native hook into Objective C and the powerful Cocoa frameworks, the variety of execution methods and now the availability of an excellent, free-to-use IDE, AppleScript has become a tool that is powerful, versatile and easy-to-develop with.</span></p><p><span>Attackers will always look to exploit the things defenders ignore, and to say that AppleScript has been ignored by the security community thus far is an understatement. I have <a href="https://www.sentinelone.com/blog/macos-red-team-calling-apple-apis-without-building-binaries/">elsewhere</a> described AppleScript as \u201Cthe PowerShell of macOS\u201D. Certainly, it\u2019s time we stopped thinking of AppleScript as the Most Unlovable Programming Language Ever and recognize that it may actually be the One macOS Programming Language to Rule Them All.</span></p><hr /><p><strong>Like this article? Follow us on <a href="https://www.linkedin.com/company/2886771/">LinkedIn</a>, <a href="https://twitter.com/SentinelOne">Twitter</a>, <a href="https://goo.gl/e5C9f4">YouTube</a> or <a href="https://www.facebook.com/SentinelOne">Facebook</a> to see the content we post.</strong></p><h3>Read more about Cyber Security</h3><ul><li><a href="https://www.sentinelone.com/blog/covid-19-outbreak-defending-against-the-psychology-of-fear-uncertainty-and-doubt/">COVID-19 Outbreak | Defending Against the Psychology of Fear, Uncertainty and Doubt</a></li><li><a href="https://www.sentinelone.com/blog/macos-malware-researchers-how-to-bypass-xprotect-on-catalina/">macOS Malware Researchers | How To Bypass XProtect on Catalina</a></li><li><a href="https://www.sentinelone.com/blog/covid-19-outbreak-employees-working-from-home-its-time-to-prepare/">COVID-19 Outbreak | Employees Working from Home? It\u2019s Time to Prepare</a></li><li><a href="https://www.sentinelone.com/blog/mind-games-the-evolving-psychology-of-ransom-notes/">Mind Games | The Evolving Psychology of Ransom Notes</a></li><li><a href="https://www.sentinelone.com/blog/business-email-compromise-what-is-bec-and-how-can-you-defend-against-it/">Business Email Compromise | What is BEC (And How Can You Defend Against It)?</a></li><li><a href="https://www.sentinelone.com/blog/what-is-hacktivism-and-why-should-enterprise-care/">What is Hacktivism? And Why Should Enterprise Care?</a></li></ul><ul class="todo-list"><li><label class="todo-list__label"><input type="checkbox" disabled="disabled"><span class="todo-list__label__description">&nbsp;</span></label></li></ul><hr><ol><li>dddd</li></ol><ul><li>dddd</li></ul><h1>hello</h1><h2>hello</h2><h3>hello</h3><h4>hello</h4><h5>hello</h5><h6>hello</h6>`;
const testHtmlWithCustomReactComponentExpected = `${testHtmlWithCustomReactComponentFirstNode}<p>fdgdf dfg <code>fdgfd</code> gdf gdfr</p><section class="file" data-id="f7d93d51-ce81-568f-a695-d1fa430ad88c"></section>`;
const testHtmlWithCaptureContext = `<h1>&nbsp;</h1><section class="capture-block" data-uuid="${blockUuidInContent}"><p>dff</p></section><p>&nbsp;</p>`;
const testHtmlWithCaptureBlockExpected = `<section class="capture-block" data-uuid="${blockUuidInContent}"><p>dff</p></section>`;
const testHtmlWithImg = `<img src="test-img">`;
const captureTest = `<table class="d-table tab-size mb-0 width-full" data-paste-markdown-skip style="box-sizing:border-box;border-spacing:0px;border-collapse:collapse;width:max-content;margin-bottom:16px;display:block;margin-top:0px;max-width:100%;overflow:auto;color:rgb(201, 209, 217);font-family:-apple-system, system-ui, &quot;Segoe UI&quot;, Helvetica, Arial, sans-serif, &quot;Apple Color Emoji&quot;, &quot;Segoe UI Emoji&quot;;font-size:14px;font-style:normal;font-variant-ligatures:normal;font-variant-caps:normal;font-weight:400;letter-spacing:normal;orphans:2;text-align:start;text-transform:none;white-space:normal;widows:2;word-spacing:0px;-webkit-text-stroke-width:0px;text-decoration-thickness:initial;text-decoration-style:initial;text-decoration-color:initial"><tbody style="box-sizing:border-box"><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"><span class="pl-k x x-first" style="box-sizing:border-box;color:var(--color-auto-gray-9);border-top-left-radius:0.2em;border-bottom-left-radius:0.2em;background-color:var(--color-diff-blob-addition-word-bg)">export</span><span class="x" style="box-sizing:border-box;color:var(--color-auto-gray-9);background-color:var(--color-diff-blob-addition-word-bg)"> </span><span class="pl-k x" style="box-sizing:border-box;color:var(--color-auto-gray-9);background-color:var(--color-diff-blob-addition-word-bg)">default</span><span class="x" style="box-sizing:border-box;color:var(--color-auto-gray-9);background-color:var(--color-diff-blob-addition-word-bg)"> </span><span class="pl-kos x x-last" style="box-sizing:border-box;border-top-right-radius:0.2em;border-bottom-right-radius:0.2em;color:var(--color-auto-gray-9);background-color:var(--color-diff-blob-addition-word-bg)">[</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="40" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLatest</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">appActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">START_APP</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">makeSync</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="41" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLatest</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">cardActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">MOVE_CARD_TO_TRASH</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">makeSync</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="42" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLatest</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">cardActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">UPDATE_JOURNAL_CARD</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">makeSync</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="43" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLatest</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">cardActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">ADD_CARD_DATA</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">makeSync</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="44" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLatest</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">cardActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">UPDATE_CARD</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">makeSync</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="45" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-c" style="box-sizing:border-box;color:var(--color-prettylights-syntax-comment)">// takeLatest(cardActions.UNDO_CARD, makeSync),</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="46" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLeading</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">appActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">EXECUTE_DATA_SYNC</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">syncCardToApi</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-c" style="box-sizing:border-box;color:var(--color-prettylights-syntax-comment)">// HERE THE TAKE LEADING IS SUPPOSE TO DO THE SYNC_ONGOING</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="47" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLatest</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">appActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">START_APP</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">watchForOnlineEvent</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="48" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">  <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">takeLatest</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">authActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">ADD_TOKEN_INTEGRATION</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">makeSync</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">,</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="49" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"><span class="pl-kos" style="box-sizing:border-box">]</span><span class="pl-kos" style="box-sizing:border-box">;</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="50" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="51" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">/</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">*</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">*</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="52" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">*</span> <span class="pl-s1" style="box-sizing:border-box">calling</span> <span class="pl-s1" style="box-sizing:border-box">takeLeading</span> <span class="pl-s1" style="box-sizing:border-box">syncCardToApi</span><span class="pl-kos" style="box-sizing:border-box">,</span> <span class="pl-s1" style="box-sizing:border-box">to</span> <span class="pl-s1" style="box-sizing:border-box">don</span>'t break the sync flow</td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="53" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">*</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">/</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="54" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"><span class="pl-k" style="box-sizing:border-box;color:var(--color-prettylights-syntax-keyword)">function</span><span class="pl-c1" style="box-sizing:border-box;color:var(--color-prettylights-syntax-constant)">*</span> <span class="pl-s1" style="box-sizing:border-box">makeSync</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-kos" style="box-sizing:border-box">)</span> <span class="pl-kos" style="box-sizing:border-box">{</span><span class="pl-kos" style="box-sizing:border-box"></span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:initial"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="55" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px">   <span class="pl-k" style="box-sizing:border-box;color:var(--color-prettylights-syntax-keyword)">yield</span> <span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">put</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-s1" style="box-sizing:border-box">appActions</span><span class="pl-kos" style="box-sizing:border-box">.</span><span class="pl-en" style="box-sizing:border-box;color:var(--color-prettylights-syntax-entity)">executeDataSync</span><span class="pl-kos" style="box-sizing:border-box">(</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">)</span><span class="pl-kos" style="box-sizing:border-box">;</span></td></tr><tr class="border-0" style="box-sizing:border-box;border-top:1px solid var(--color-markdown-table-tr-border);border-right:0px;border-bottom:0px;border-left:0px;border-image:initial;background-color:var(--color-bg-primary)"><td class="blob-num blob-num-addition text-right border-0 px-2 py-1 lh-default" data-line-number="56" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);line-height:20px;text-align:right;width:50px;min-width:50px;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-diff-blob-addition-num-text);white-space:nowrap;vertical-align:top;cursor:pointer;user-select:none;background-color:var(--color-diff-blob-addition-num-bg)"></td><td class="border-0 px-2 py-1 blob-code-inner blob-code-addition js-blob-code-addition blob-code-marker-addition" style="box-sizing:border-box;padding:6px 13px;border:1px solid var(--color-markdown-table-border);overflow:visible;font-family:SFMono-Regular, Consolas, &quot;Liberation Mono&quot;, Menlo, monospace;font-size:12px;color:var(--color-text-primary);overflow-wrap:break-word;white-space:pre-wrap;background-color:var(--color-diff-blob-addition-line-bg);outline:transparent dotted 1px"><span class="pl-kos" style="box-sizing:border-box">}</span></td></tr></tbody></table>`;
const captureTestExpectedResult = `<table><tbody><tr><td>export default [</td></tr><tr><td>  takeLatest(appActions.START_APP, makeSync),</td></tr><tr><td>  takeLatest(cardActions.MOVE_CARD_TO_TRASH, makeSync),</td></tr><tr><td>  takeLatest(cardActions.UPDATE_JOURNAL_CARD, makeSync),</td></tr><tr><td>  takeLatest(cardActions.ADD_CARD_DATA, makeSync),</td></tr><tr><td>  takeLatest(cardActions.UPDATE_CARD, makeSync),</td></tr><tr><td>  // takeLatest(cardActions.UNDO_CARD, makeSync),</td></tr><tr><td>  takeLeading(appActions.EXECUTE_DATA_SYNC, syncCardToApi), // HERE THE TAKE LEADING IS SUPPOSE TO DO THE SYNC_ONGOING</td></tr><tr><td>  takeLatest(appActions.START_APP, watchForOnlineEvent),</td></tr><tr><td>  takeLatest(authActions.ADD_TOKEN_INTEGRATION, makeSync),</td></tr><tr><td>];</td></tr><tr><td>/**</td></tr><tr><td>* calling takeLeading syncCardToApi, to don't break the sync flow</td></tr><tr><td>*/</td></tr><tr><td>function* makeSync() {</td></tr><tr><td>   yield put(appActions.executeDataSync());</td></tr><tr><td>}</td></tr></tbody></table>`;
const tableWithErrorAttributeKey = `<blockquote><table><tbody><tr><td><span><tr><td><span><span>import</span> <span>useLauncherTrailingMenuList</span> <span>from</span> <span>'./useLauncherTrailingMenuList'</span><span>;</span></span></td></tr><tr><td><span><span>import</span> <span>{</span> <span>sendTrackToAnalyticsInterface</span> <span>}</span> <span>from</span> <span>'@lazy-app/interface'</span><span>;</span></span></td></tr><tr><td><span><span>import</span> <span>{</span> <span>TITLE_ANCHOR</span> <span>}</span> <span>from</span> <span>'src/launcher/params/trailingMenu'</span><span>;</span></span></td></tr><tr><td><span><span>import</span> <span>{</span> <span>getFirstLineOfLauncherContent</span> <span>}</span> <span>from</span> <span>'src/common/services/<span>DOM</span>'</span><span>;</span></span></td></tr><tr><td><span><span>import</span> <span>{</span> <span>mergeContentToTitle</span> <span>}</span> <span>from</span> <span>'src/common/services/<span>cards/cardsMerge</span>'</span><span>;</span></span></td></tr></span></td></tr></tbody></table></blockquote>`;
const tableWithErrorAttributeKeyExpectedResult = `<blockquote><table><tbody><tr><td>import useLauncherTrailingMenuList from './useLauncherTrailingMenuList';</td></tr><tr><td>import { sendTrackToAnalyticsInterface } from '@lazy-app/interface';</td></tr><tr><td>import { TITLE_ANCHOR } from 'src/launcher/params/trailingMenu';</td></tr><tr><td>import { getFirstLineOfLauncherContent } from 'src/common/services/DOM';</td></tr><tr><td>import { mergeContentToTitle } from 'src/common/services/cards/cardsMerge';</td></tr></tbody></table></blockquote>`;
const blogPostWithPicture = `<hr /><div><div><img /><picture><img src="https://blog.theodo.com/static/16e4c2935a98f83c82a9e190e4872e9a/83a78/0-shopify-logo-window-with-plant.jpg" /></picture></div><p>As a software developer, I often hear that No-Code is the future of app development. That in a few years, automatically generated code will replace all the frameworks that we currently have. With my young experience in this field I'm quite sceptical, because I still believe that to truly answer a\u2026</p><a href="https://blog.theodo.com/2021/04/develop-on-shopify-in-2021/">Continue reading \u2192</a></div><div><div><img /><picture><img src="https://blog.theodo.com/static/5b3d487112d172127145100f82be6648/a79d3/mobile-checking-decision-tree.png" /></picture></div><p>At Theodo, we develop solutions for both mobile and desktop. Most of the time we use the chrome device toolbar to see the visual rendering of the website when we develop in the local environment. Then when the code is deployed, we also check in the development/staging environment that everything\u2026</p><a href="https://blog.theodo.com/2021/04/check-website-on-mobile-in-local-environment/">Continue reading \u2192</a></div><div><div><img /><picture><img src="https://blog.theodo.com/static/dfae80d02f29938244fa328765a243a9/a79d3/tree-shaking.png" /></picture></div><p>At Theodo, our aim is to build reliable and fast applications for our customers. Some of our projects include improving the performance of already existing applications. During one of these missions, we managed to reduce the bundle size of all our pages by a whopping 500KB Gzipped by tree shaking\u2026</p><a href="https://blog.theodo.com/2021/04/library-tree-shaking/">Continue reading \u2192</a></div><div><div><img /><picture><img src="https://blog.theodo.com/static/876fca6bc5701bcfba7a66ed8f0db777/46604/0-dynamo-moving-castle-cp.png" /></picture></div><p>I discovered DynamoDB a few months ago, in the context of a very high traffic web application built using the Serverless Framework. DynamoDB - as well as Single-Table design - was fairly new to me, and I quickly noticed that there are a few concepts to grasp before designing the first data models\u2026</p><a href="https://blog.theodo.com/2021/04/introduction-to-dynamo-db-modeling/">Continue reading \u2192</a></div><div><div><img /><picture><img src="https://blog.theodo.com/static/ab5e39810c1c98213466a0a0b73af63f/a79d3/cover-icons.png" /></picture></div><p>How I managed to choose a performant icon system adapted to a Next.js project with styled-components. Almost every website uses icons in order to quickly guide users or to improve the aesthetics of the page. Usually, designers can export icons to SVG format. SVG stands for Scalable Vector Graphics\u2026</p><a href="https://blog.theodo.com/2021/03/icon-library-react-why-inline-svg-better-than-font/">Continue reading \u2192</a></div>`;
const blogPostWithPictureExpected = `<hr><img><img src="https://blog.theodo.com/static/16e4c2935a98f83c82a9e190e4872e9a/83a78/0-shopify-logo-window-with-plant.jpg"><p>As a software developer, I often hear that No-Code is the future of app development. That in a few years, automatically generated code will replace all the frameworks that we currently have. With my young experience in this field I'm quite sceptical, because I still believe that to truly answer a\u2026</p><a href="https://blog.theodo.com/2021/04/develop-on-shopify-in-2021/" target="_blank">Continue reading \u2192</a><img><img src="https://blog.theodo.com/static/5b3d487112d172127145100f82be6648/a79d3/mobile-checking-decision-tree.png"><p>At Theodo, we develop solutions for both mobile and desktop. Most of the time we use the chrome device toolbar to see the visual rendering of the website when we develop in the local environment. Then when the code is deployed, we also check in the development/staging environment that everything\u2026</p><a href="https://blog.theodo.com/2021/04/check-website-on-mobile-in-local-environment/" target="_blank">Continue reading \u2192</a><img><img src="https://blog.theodo.com/static/dfae80d02f29938244fa328765a243a9/a79d3/tree-shaking.png"><p>At Theodo, our aim is to build reliable and fast applications for our customers. Some of our projects include improving the performance of already existing applications. During one of these missions, we managed to reduce the bundle size of all our pages by a whopping 500KB Gzipped by tree shaking\u2026</p><a href="https://blog.theodo.com/2021/04/library-tree-shaking/" target="_blank">Continue reading \u2192</a><img><img src="https://blog.theodo.com/static/876fca6bc5701bcfba7a66ed8f0db777/46604/0-dynamo-moving-castle-cp.png"><p>I discovered DynamoDB a few months ago, in the context of a very high traffic web application built using the Serverless Framework. DynamoDB - as well as Single-Table design - was fairly new to me, and I quickly noticed that there are a few concepts to grasp before designing the first data models\u2026</p><a href="https://blog.theodo.com/2021/04/introduction-to-dynamo-db-modeling/" target="_blank">Continue reading \u2192</a><img><img src="https://blog.theodo.com/static/ab5e39810c1c98213466a0a0b73af63f/a79d3/cover-icons.png"><p>How I managed to choose a performant icon system adapted to a Next.js project with styled-components. Almost every website uses icons in order to quickly guide users or to improve the aesthetics of the page. Usually, designers can export icons to SVG format. SVG stands for Scalable Vector Graphics\u2026</p><a href="https://blog.theodo.com/2021/03/icon-library-react-why-inline-svg-better-than-font/" target="_blank">Continue reading \u2192</a>`;
const tableHTMLWithContent = `<table>dsfsdf<tbody><tr>hello<td>${tableHTMLWithContentFirstNode}</td></tr></tbody></table>`;
const tableHTMLWithContentSanitize = `<table><tbody><tr><td>${tableHTMLWithContentFirstNode}</td></tr></tbody></table>dsfsdfhello`;
const cardContextExpectedResultStringify = `When we think about security on macOS and the tools used by offensive actors, whether those are real in the wild attacks or red team exercises, we tend to think of things like python scripts, shell scripts, malicious documents, shady extensions and of course, the fake, doctored or trojan application bundle. There is much less attention in the security field on AppleScript \u2013 a built-in macOS technology \u2013 despite the fact it\u2019s been around for as long as Python and predates macOS 10 itself by 8 or 9 years. As I\u2019ll show in this post, AppleScript is widely used by offensive actors. This includes its use in adware, its use for tasks such as persistence, anti-analysis, browser hijacking, spoofing and more. Worryingly, given the lack of attention paid to AppleScript in the research community, that is all without even leveraging some of AppleScript\u2019s most powerful or unique features, some of which we\u2019ll cover below (others I\u2019ve written about before here). Why Have the Good Guys Ignored AppleScript? Unlike Bash and other shell languages, and unlike Python, a cross-platform, beginner-friendly scripting language that has achieved widespread adoption and praise, AppleScript is a language peculiar to macOS; not only can you NOT use it on other Desktop operating systems like Windows and Linux, you can\u2019t even use it on Apple\u2019s other operating systems like iOS and iPadOS. As a language, AppleScript has a reputation for being quirky, slow and difficult to develop even simple scripts with. It\u2019s quirky because it attempts to use \u201Cnatural language\u201D but in a grammar that is entirely artificial, often inconsistent and frustratingly unintuitive. It\u2019s also incredibly verbose. Compare the code for the simple task of counting the number of items in /usr/bin directory. As ever, a shell script will always be the most concise: ls -l /usr/bin | wc -l Python is a little more verbose, but still fairly clean and familiar: #!/usr/bin/pythonimport ospath, dirs, files = next(os.walk("/usr/bin"))print len(files) The AppleScript version, however, is something of an entirely different nature. tell application "System Events"	set theFiles to name of every file of folder "bin" of folder "usr" of startup disk	count of theFilesend tell AppleScript is also slow in execution because, among other things, the underlying technology involves constructing and sending Apple Events through an archaic interface called the Apple Event Manager that was written for Apple\u2019s System 7 operating system (released in 1991) and not optimized for performance even back then. Source: AppleScript Overview,  \xA9 2002, 2007 Apple Inc. And it\u2019s historically been difficult to develop scripts with AppleScript because most people who come to it will attempt to use the free, built-in but notoriously spartan (Apple)Script Editor.app, which lacks almost every and any feature developers normally expect and need. There\u2019s no debugger, there\u2019s no variable introspection, there\u2019s no code snippets or effective code completion, to name just a few missing features. Until recently, the only 3rd party alternative to Script Editor was priced at $200 and had a time-limited, 20-day demo period. In short, the investment in time, effort and money required to produce something that still, after all that, can only be used on the macOS Desktop, effectively puts AppleScript at the bottom of the list for most people when it comes to choosing a useful or productive programming language. As a result, despite being with us for nearly 30 years, AppleScript is barely used by the majority of Mac admins, Mac developers or Mac end users. Indeed, AppleScript may have a good claim to being the Most Unloved and Unlovable Programming Language Ever. So Why Do the Bad Guys Love Using AppleScript? AppleScript was designed for automation and interapplication communication: the goal being to allow ordinary users to chain together repetitive tasks and execute them without further user interaction. For example, you can have Mail.app automatically trigger a script when it receives an email from a certain sender, or with a particular keyword in the subject line or content, extract whatever details you want from the email, and then populate a database in Excel or Numbers with the desired information, formatted and sorted on-the-fly as the data comes in. There\u2019s no need for the user to be involved in any of this once the script is set up. And as it turns out, automating interapplication communication and sidestepping user interaction is a godsend for malware authors. What could be more useful than bending popular applications like email clients, web browsers and the Microsoft Office suite to your will without needing to involve the user (aka in this scenario, the victim)? And so, despite its general lack of appeal in almost all possible audiences for a scripting or programming language, there is one audience that does use AppleScript widely, if not particularly artfully or cleverly, and that is threat actors. Let\u2019s look at some examples. How Shlayer and Other Malware Trojans Infect macOS Learn from Phil Stokes how to tell real Adobe Flash Players from fake ones Recent Examples of AppleScript in macOS malware A recent browser hijacker targeting Safari installs a hidden LaunchAgent that, via a shell script, loads, compiles and executes AppleScript. It starts with a shell installer script packaged inside a DMG that\u2019s supposed to contain an application called \u2018PDFConverter4u\u2019. But in a hidden .assets folder on the disk image is a first stage shell script: 5f198e82c0cf9a9f7d7a8d01273a6ad75a17a95960d8996dcdd028922b3d97bc This unpacks and executes a second stage shell script: 55529224e9f70f5cab007e2ca98f6aec5cf31eb923fdfc09f60c01cc45c80666 Which eventually produces the hidden launch agent that executes another shell script containing the following AppleScript code, launched via osascript. cdaa2121d79031cf39159198dfe64d3695a9c99ff7c3478a0b8953ade9052ecc The purpose of the AppleScript is to replace the user\u2019s search query on popular search engines google, bing and yahoo, with one provided by the attacker\u2019s shell script. It\u2019s a quick and easy way for bad actors to make money out of clicks which negatively impacts the victim\u2019s productivity. While this particular sample comes packed in a separate file, many others write their AppleScript directly into a MachO binary, either in plain text strings or in obfuscated base64 or similar encoding. The following strings extracted from a Bundlore installer show that the code tries to force enable JavaScript execution in Google Chrome, then uses AppleScript to execute it in the active tab of the browser\u2019s front window. Strings from 41e0d31d52cb93f6a5020a278e8f360a6e134e6cc7092b4a5e575ac8b96a8d74 The next sample is a variant of a Pirrit malware. 21331ccee215801ca682f1764f3e37ff806e7510ded5576c0fb4d514b4cf2b7c The authors use both plain text AppleScript and base64 encoded AppleScript, targeting Safari, Chrome and Firefox browsers. Here\u2019s some of the decoded base64 targeting Firefox and attempting to perform automated keystrokes to copy the current URL to the user\u2019s clipboard before replacing the URL to that of the attacker\u2019s choice: Using AppleScript Without Apple Events Speaking of base64, the next example illustrates something that many ordinary users and developers have overlooked about AppleScript, but which offensive actors have not: you can use AppleScript to execute any other kind of script, including python scripts like this one which drops the Empire exploit kit. And what\u2019s true of AppleScript and Python, is true of AppleScript and Perl, AppleScript and Bash and indeed AppleScript and absolutely any command line tool at all: you can call them all and bring their functionality into your AppleScript and combine that with other utilities. The examples above all use what we might call \u2018vanilla AppleScript\u2019. That is, the native AppleScript language that\u2019s been around since the early days of the platform. But starting in Yosemite, 10.10 and continuing up to and including the most recent version of macOS, AppleScript has been given increasing power through access to Cocoa frameworks, and this opens up the possibility of creating full-blown, powerful programs and applications with nothing other than AppleScript itself. Objective C executed through AppleScript is, speed-wise, more or less on a par with Objective C executed in a MachO binary. And interestingly, although we haven\u2019t seen threat actors making use of these powerful capabilities so far, there\u2019s at least two reasons why we may well do so in the future: first to avoid detection, and second, because of the easy availability of a good development environment. How Shlayer and Other Malware Trojans Infect macOS Learn from Phil Stokes how to tell real Adobe Flash Players from fake ones Using AppleScript to Avoid Detection Avoiding detection on execution is a primary objective for all malware (even ransomware, which doesn\u2019t want to get noticed until after execution). AppleScript offers offensive actors a plethora of ways to execute. In addition to simply executing a .scrpt file, you can run AppleScripts from Mail rules, from a shell script, in memory, from the command line, from within a MachO, in a plain text, uncompiled file, from an Automator workflow, from a Folder Action, a Finder Service or from a Calendar event. Because of AppleScript\u2019s ability to execute Objective C code without needing a compiled binary, this opens up a number of interesting attack possibilities. It also potentially opens up the ability to bypass detection tools based on Apple\u2019s new kextless security framework introduced in macOS Catalina 10.15. In an excellent post by Cedric Owens called Taking the macOS Endpoint Security Framework For A Quick Spin, Owens sets out to test what can and cannot be detected using three recently developed 3rd-party security tools that leverage the new Apple Endpoint Security framework. One of the interesting things that Owens found was that if you tried capturing the user\u2019s clipboard via osascript and vanilla AppleScript, this activity would be easily picked up by all the tools he was testing. osascript -e 'the clipboard' However, when using the native Cocoa API, NSPasteboard, none of the Endpoint Security framework-powered tools Owens tested appeared to capture that activity. But now, of course, we can execute NSPasteboard natively from AppleScript, too! Notice that our one line, simple but also detectable osascript has turned into about 14 lines of complex-looking AppleScript-ObjC. Few people, certainly not I, would want to try and construct that kind of code in Script Editor. However, the problem of developing complex AppleScripts is now more or less a thing of the past. The 3rd party alternative mentioned earlier in this post now has an unlimited free trial version and retails at half of its old price; more importantly, it also allows you to drag and drop a great deal of boilerplate code like that used in the script above straight into your scripts. And it provides developer-friendly functionality like code completion and API lookups that really take the pain out of developing AppleScript code. Let\u2019s look at another example. A lot of offensive operations want to avoid targets that are running particular software. Little Snitch is a prime example, various VM software is another. We can easily get a list of running apps by name and test for those, again directly by calling into Cocoa APIs, this time via NSWorkspace. If we just want a true/false test for the existence of specific apps, we can just put the app names in a list and return true on the first hit. In other words, by leveraging AppleScript\u2019s hook into Cocoa frameworks, we can execute native code without the overhead of building MachO binaries or MachO apps (although you can do both of those with AppleScript, too!). We can do this filelessly so that we don\u2019t get caught by new \u2018kextless\u2019 tools such as those tested by Owens, and we can execute this code in far more ways than any other kind of code available on macOS, whether that\u2019s shell scripts, Python scripts or native macOS bundles. Conclusion The upshot here is that the main reasons why the good guys have typically eschewed AppleScript are in fact no longer relevant or true. Since we\u2019ve already seen threat actors taking advantage of AppleScript despite those obstacles in the past, it\u2019s only reasonable to assume that they may delve deeper into what this unique language has to offer in the future. Thanks to the native hook into Objective C and the powerful Cocoa frameworks, the variety of execution methods and now the availability of an excellent, free-to-use IDE, AppleScript has become a tool that is powerful, versatile and easy-to-develop with. Attackers will always look to exploit the things defenders ignore, and to say that AppleScript has been ignored by the security community thus far is an understatement. I have elsewhere described AppleScript as \u201Cthe PowerShell of macOS\u201D. Certainly, it\u2019s time we stopped thinking of AppleScript as the Most Unlovable Programming Language Ever and recognize that it may actually be the One macOS Programming Language to Rule Them All. --- Like this article? Follow us on LinkedIn, Twitter, YouTube or Facebook to see the content we post. Read more about Cyber Security COVID-19 Outbreak | Defending Against the Psychology of Fear, Uncertainty and Doubt macOS Malware Researchers | How To Bypass XProtect on Catalina COVID-19 Outbreak | Employees Working from Home? It\u2019s Time to Prepare Mind Games | The Evolving Psychology of Ransom Notes Business Email Compromise | What is BEC (And How Can You Defend Against It)? What is Hacktivism? And Why Should Enterprise Care? --- dddd dddd hello hello hello hello hello hello`;
const cardContextExpectedResultSummarize = "When we think about security on macOS and the tools used by offensive actors, whether those are real in the wild attacks or red team exercises , we te";
const testHtmlWithCustomReactComponent = `${testHtmlWithCustomReactComponentFirstNode}<p>fdgdf dfg <code>fdgfd</code> gdf gdfr</p><section class="file" data-id="f7d93d51-ce81-568f-a695-d1fa430ad88c"></section><p>&nbsp;</p>`;
const testHtmlWithRelatedCard = `<h1>Andrew Homeyer</h1><p>fdgdf dfg <code>fdgfd</code> gdf gdfr</p><p><a class="reference" data-uuid="cardRelated1"></a><a class="reference" data-uuid="cardRelated2"></a></p><p>&nbsp;</p>`;
const testHtmlWithRelatedCardSummarize = `Andrew Homeyer fdgdf dfg fdgfd gdf gdfr`;
const generateReferencedLink = (cardUuid, content) => `<a class="reference" data-uuid="${cardUuid}">${content || cardUuid}</a>`;
const complicatedTitleJSON = [
  {
    type: "text",
    marks: [
      {
        type: "underline"
      }
    ],
    text: "Title "
  },
  {
    type: "text",
    marks: [
      {
        type: "link",
        attrs: {
          href: "https://lazy.so",
          target: "_blank"
        }
      }
    ],
    text: "This is a link "
  },
  {
    type: "text",
    marks: [
      {
        type: "link",
        attrs: {
          href: "https://lazy.so",
          target: "_blank"
        }
      },
      {
        type: "italic"
      },
      {
        type: "textStyle",
        attrs: {
          fontColor: "white"
        }
      }
    ],
    text: "First "
  },
  {
    type: "text",
    marks: [
      {
        type: "link",
        attrs: {
          href: "https://lazy.so",
          target: "_blank"
        }
      },
      {
        type: "strike"
      },
      {
        type: "textStyle",
        attrs: {
          fontColor: "white"
        }
      }
    ],
    text: "Paragraph..."
  },
  {
    type: "text",
    marks: [
      {
        type: "link",
        attrs: {
          href: "https://lazy.so",
          target: "_blank"
        }
      },
      {
        type: "textStyle",
        attrs: {
          fontColor: "white"
        }
      }
    ],
    text: " "
  },
  {
    type: "text",
    marks: [
      {
        type: "link",
        attrs: {
          href: "https://lazy.so",
          target: "_blank"
        }
      }
    ],
    text: "This is a link"
  }
];
const titleReferenceJSON = (uuidCardRef) => [
  { type: "text", text: "sdfsdf " },
  {
    type: REFERENCE_TE_ITEM.name,
    attrs: { cardUuid: uuidCardRef }
  },
  { type: "text", text: " dfsfsdf klllksdfsvffdf" }
];
const contentJSON = (uuidCardRef) => [
  {
    type: SeshatNodes.BLOCK,
    attrs: {
      textAlign: "left",
      type: "paragraph",
      "block-id": "ef007bd1-28e9-5efc-abbc-dec853676ceb"
    },
    content: [
      {
        type: SeshatNodes.BLOCK_CONTENT,
        content: [
          {
            type: SeshatNodes.CONTENT,
            content: [
              { type: "text", text: "I\u2019m some " },
              {
                type: REFERENCE_TE_ITEM.name,
                attrs: {
                  cardUuid: uuidCardRef,
                  id: uuidCardRef
                }
              },
              { type: "text", text: " here I want to be summarized please" }
            ]
          }
        ]
      },
      {
        type: SeshatNodes.BLOCK,
        attrs: {
          textAlign: "left",
          type: "paragraph",
          "block-id": "ef007bd1-28e9-5efc-abbc-dec853676ceb"
        },
        content: [
          {
            type: SeshatNodes.BLOCK_CONTENT,
            content: [
              {
                type: SeshatNodes.CONTENT,
                content: [{ type: "text", text: "Another line to be summarize." }]
              }
            ]
          }
        ]
      },
      {
        type: SeshatNodes.BLOCK,
        attrs: {
          textAlign: "left",
          type: "paragraph",
          "block-id": "ef007bd1-28e9-5efc-abbc-dec853676ceb"
        },
        content: [
          {
            type: SeshatNodes.BLOCK_CONTENT,
            content: [
              {
                type: SeshatNodes.CONTENT,
                content: [{ type: "text", text: "And one more." }]
              }
            ]
          }
        ]
      },
      {
        type: SeshatNodes.BLOCK,
        attrs: {
          textAlign: "left",
          type: void 0,
          "block-id": "e0090d8c-673b-505b-8ae5-0d04cd37de3d"
        },
        content: [
          {
            type: SeshatNodes.BLOCK_CONTENT,
            content: [{ type: SeshatNodes.CONTENT }]
          }
        ]
      }
    ]
  }
];
const randomCardContent = [
  {
    attrs: {
      "block-id": "37d036f1-faff-5222-b6ea-f2c1f459fc2f",
      textAlign: "left",
      type: BlockTypes.CARD_CONTENT,
      cardType: "person",
      isDone: false
    },
    content: [
      {
        content: [
          {
            content: [
              {
                text: "paolo castro lemaitre",
                type: "text"
              }
            ],
            type: SeshatNodes.CONTENT
          }
        ],
        type: SeshatNodes.BLOCK_CONTENT
      },
      {
        attrs: {
          "block-id": void 0,
          "context-uuid": void 0,
          textAlign: "left",
          type: "bulletedList"
        },
        content: [
          {
            content: [
              {
                content: [
                  {
                    attrs: {
                      cardUuid: "db7d491d-8d8b-5e9c-bcd0-f2281b14c0b8",
                      id: "db7d491d-8d8b-5e9c-bcd0-f2281b14c0b8"
                    },
                    type: REFERENCE_TE_ITEM.name
                  },
                  {
                    text: " hello world Lorem",
                    type: "text"
                  }
                ],
                type: SeshatNodes.CONTENT
              }
            ],
            type: SeshatNodes.BLOCK_CONTENT
          },
          {
            attrs: {
              "block-id": void 0,
              "context-uuid": void 0,
              textAlign: "left",
              type: "bulletedList"
            },
            content: [
              {
                content: [
                  {
                    content: [
                      {
                        text: "Hello my name is ",
                        type: "text"
                      },
                      {
                        attrs: {
                          cardUuid: "7n0qbkttphpi66d5j9mskp6h3p",
                          id: "7n0qbkttphpi66d5j9mskp6h3p"
                        },
                        type: REFERENCE_TE_ITEM.name
                      }
                    ],
                    type: SeshatNodes.CONTENT
                  }
                ],
                type: SeshatNodes.BLOCK_CONTENT
              }
            ],
            type: SeshatNodes.BLOCK
          }
        ],
        type: SeshatNodes.BLOCK
      },
      {
        attrs: {
          "block-id": void 0,
          "context-uuid": void 0,
          textAlign: "left",
          type: "bulletedList"
        },
        content: [
          {
            content: [
              {
                content: [
                  {
                    text: "Yes I'm good",
                    type: "text"
                  }
                ],
                type: SeshatNodes.CONTENT
              }
            ],
            type: SeshatNodes.BLOCK_CONTENT
          }
        ],
        type: SeshatNodes.BLOCK
      },
      {
        attrs: {
          "block-id": void 0,
          "context-uuid": void 0,
          textAlign: "left",
          type: "bulletedList"
        },
        content: [
          {
            content: [
              {
                content: [
                  {
                    text: "Well",
                    type: "text"
                  }
                ],
                type: SeshatNodes.CONTENT
              }
            ],
            type: SeshatNodes.BLOCK_CONTENT
          }
        ],
        type: SeshatNodes.BLOCK
      },
      {
        attrs: {
          "block-id": void 0,
          "context-uuid": void 0,
          textAlign: "left",
          type: "heading2"
        },
        content: [
          {
            content: [
              {
                content: [
                  {
                    text: "paolo.castro@bonjour.io ",
                    type: "text"
                  }
                ],
                type: SeshatNodes.CONTENT
              }
            ],
            type: SeshatNodes.BLOCK_CONTENT
          }
        ],
        type: SeshatNodes.BLOCK
      },
      {
        attrs: {
          "block-id": void 0,
          "context-uuid": void 0,
          textAlign: "left",
          type: "paragraph"
        },
        content: [
          {
            content: [
              {
                content: [
                  {
                    text: "rrrr",
                    type: "text"
                  }
                ],
                type: SeshatNodes.CONTENT
              }
            ],
            type: SeshatNodes.BLOCK_CONTENT
          }
        ],
        type: SeshatNodes.BLOCK
      },
      {
        attrs: {
          "block-id": "2816a7d9-3f95-5a3e-bd1c-b89bddc4fc60",
          textAlign: "left",
          type: "seshatCardContent",
          cardType: "person",
          isDone: false
        },
        content: [
          {
            content: [
              {
                content: [
                  {
                    text: "Syed Umair",
                    type: "text"
                  }
                ],
                type: SeshatNodes.CONTENT
              }
            ],
            type: SeshatNodes.BLOCK_CONTENT
          },
          {
            attrs: {
              "block-id": void 0,
              "context-uuid": void 0,
              textAlign: "left",
              type: "paragraph"
            },
            content: [
              {
                content: [
                  {
                    content: [
                      {
                        attrs: {
                          cardUuid: "f588adb3-cf83-57ac-aad7-400be68d1103",
                          id: "f588adb3-cf83-57ac-aad7-400be68d1103"
                        },
                        type: REFERENCE_TE_ITEM.name
                      },
                      {
                        text: " sdfsdfds",
                        type: "text"
                      }
                    ],
                    type: SeshatNodes.CONTENT
                  }
                ],
                type: SeshatNodes.BLOCK_CONTENT
              }
            ],
            type: SeshatNodes.BLOCK
          }
        ],
        type: SeshatNodes.BLOCK
      }
    ],
    type: SeshatNodes.BLOCK
  }
];
const setLastUpdate = setLastUpdate$1;
const generateCard = (card) => {
  const cardType = card.type || CARD_NOTE_TYPE;
  const workspaceUuid = card.workspaceUuid || "workspace1";
  const cardUuid = card.uuid || "1234123";
  const title = card.type === "capture" ? void 0 : card.title || "book_title";
  const text = typeof title === "string" && !card.text ? [{ text: title, type: "text" }] : void 0;
  return {
    pictureUrl: "picture",
    title,
    metadata: maybeMakeMetadataReady({}),
    parentCardUuid: "inbox1",
    workspaceUuid,
    text,
    summarizeContent: "",
    uuid: cardUuid,
    type: cardType,
    creationDate: setLastUpdate(),
    lastUpdate: setLastUpdate(),
    ...card
  };
};
const generateKindleAnnotation = (annotation = {}) => ({
  id: "annotation1",
  location: "1",
  header: `annotation_header_1`,
  highlight: "annotation highlight",
  note: "annotation note",
  ...annotation
});
const generateKindleBook = (book = {}) => ({
  pictureUrl: "picture",
  title: "book_title",
  bookId: "bookId1",
  author: "author1",
  authors: ["author1"],
  ...book,
  annotations: {
    ...book.annotations || {}
  }
});
const generateArticle = (article) => ({
  ...article,
  url: "https://hello.fr",
  title: "hello",
  description: "string",
  links: ["link1", "link2"],
  image: "",
  content: "string",
  author: "string",
  published: "string",
  ttr: 223,
  source: "string",
  paywallDetected: false
});
const generateFile = (fileName, path, type) => new File([path], fileName, {
  type
});
const generateAttachment = (attachment) => {
  const file = generateFile("foo.pdf", "foo", "application/pdf");
  return {
    uuid: "attachment1",
    workspace: "workspace1",
    type: file.type,
    fileName: file.name,
    size: file.size,
    ...attachment
  };
};
const generateSourceTab = (option = {}) => ({
  url: "fake://url",
  title: "fake",
  ...option
});
const generate = {
  card: generateCard,
  attachment: generateAttachment,
  file: generateFile,
  article: generateArticle,
  kindleBook: generateKindleBook,
  kindleAnnotation: generateKindleAnnotation,
  sourceTab: generateSourceTab
};
const isArticleParserResult = (result) => !!(result == null ? void 0 : result["url"]);
export { ADD_CONTEXT_ANCHOR, AI_ENVIRONMENT, AI_SHORTCUTS, ALL_CARD_DEFAULT_OPTION, ALL_CARD_TRASH_DEFAULT_OPTION, All_CARD_SHORTCUT, ArcBrowser, ArrayOperation, AttachmentMaxFileSize, AttachmentTypesBlackList, AttachmentTypesWhiteList, AudioAttachmentTypesWhiteListObj, BALLON_MENU_POPOVER_ID, BLOCK_MENU_ID, BUCKETS, BUCKETS_CHILDREN_OBJECT_TYPE, BUCKET_SPECIFIC_CHILD_TYPE, BUCKET_TYPE, BlockAttrsParams, BlockTypes, CAPTURE_TO_ANCHOR, CARD_ALL_STATUS, CARD_CAPTURE_TYPE, CARD_DAY_TYPE, CARD_EDITABLE_TYPE, CARD_INBOX_TYPE, CARD_JOURNAL_TYPE, CARD_NOTE_TYPE, CARD_PEOPLE_TYPE, CARD_PERSON_TYPE, CARD_SOURCES_TYPE, CARD_SOURCE_TYPE, CARD_SPLIT_TYPE, CARD_STATUS_NO_TRASH, CARD_STATUS_NO_TRASH_NO_DONE, CARD_TASK_TYPE, CARD_TRASH_TYPE, CARD_TYPE, CARD_UNTITLED, CARD_VIEW, CARD_VIEW_DEFAULT, CARD_VIEW_TASKS, CARD_VIEW_TYPE, CARD_WITHOUT_TITLE_IN_TEXT_EDITOR_CONTENT, CARD_WITH_CONTENT, CARD_WITH_EDITOR_VIEW, CARD_WITH_TITLE_IN_TEXT_EDITOR_CONTENT, CATEGORY_NAMES, COLOR, COMMAND_J_DIALOG_ID, COMMAND_J_ID, CONVERT_LINK_MENU_ID, CardClipStatus, ClipBookmarksTypes, CommandGroups, DEFAULT_ARRAY, DEFAULT_CARD_EMOJI, DEFAULT_CARD_TITLE, DEFAULT_CARD_TITLE_LENGTH, DEFAULT_CARD_TYPE, DEFAULT_FALSE, DEFAULT_LAUNCHER_CARD_TYPE, DEFAULT_NOOP, DEFAULT_OBJECT, DEFAULT_REFERENCE, DEFAULT_TRUE, DEFAULT_UNDEFINED, DRAG_HANDLE_ID, EDITOR_RELATED_ELEMENT_IDS, EDIT_LINK_MENU_CONTENT_INPUT_ID, EXPORT_WINDOW_EVENT, FAKE_AI_ENVIRONMENT, FileAttachmentTypesWhiteListObj, HASH_REFERENCE, INBOX_DEFAULT_OPTION, IPC_MAIN_TO_IPC_RENDERER, IPC_RENDERER_CONNECTION, IPC_RENDERER_DISCONNECTION, IPC_RENDERER_TO_IPC_MAIN, ImageAttachmentTypesWhiteListObj, ImageAttachmentTypesWhiteListObjList, LATER_DEFAULT_OPTION, LAUNCHER_EDITOR_ID, LAZY_APP_NAME, LINKEDIN_FETCH_CATEGORY, LOCAL_DATA_MIGRATION_VERSION, LazyAppEvents, LazyBlockTypes, LazyPerformanceNames, MENTION_PLACEHOLDER, MENTION_PLACEHOLDER_INPUT_ID, MENTION_PLACEHOLDER_POPOVER_ID, METADATA_CARD_TYPE, METADATA_DEFAULT_OPTION, MOVE_TO_TYPE, MarkAttrsParams, MarkTypes, NOTES_DEFAULT_VIEW_ID, NOTES_LATER_DEFAULT_SPLIT_ID, NOT_REAL_CARD_UUID, NOT_REAL_CARD_UUIDS, NUMBER_OF_WORD_READ_PRE_MINUTE, NodeViewTypes, PDFAttachmentTypesWhiteListObj, RECALCULATE_PLACEHOLDER, REFERENCE_BRACKET_PLACEHOLDER, REFERENCE_HASH_PLACEHOLDER, REFERENCE_TE_ITEM, SEARCH_PERSON_BY_DESCRIPTOR_ID, SEARCH_SOURCE_BY_DESCRIPTOR_ID, SESHAT_JSON_MIME, SLACK_APP_NAME, SLOT_BETWEEN_RENDERERS_KEYS, STATUS_DONE, STATUS_TODO, STATUS_TRASH, SUPERHUMAN_APP_NAME, SeshatCssClass, SeshatInlineNodes, SeshatNodeGroups, SeshatNodes, SeshatWrappingBlocks, TARGET_RENDERER, TASKS_DEFAULT_VIEW_ID, TRAILING_MENU_POPOVER_ID, TRASH_DEFAULT_OPTION, Uint8ArrayToBuffer, VideoAttachmentTypesWhiteListObj, VideoAttachmentTypesWhiteListObjList, WS_TYPE, WsAppToWebService, WsWebToAppService, YOUTUBE_REGEX, YOUTUBE_TITLE_LENGTH, addDays, addDescriptorOptionsToMetadata, addDescriptorToMetadataIfMissing, addQueryToYoutubeUrl, addToPropertyValue, addValueToMetadata, addValueToPropertyValueMetadata, allCardsQuery, allDefaultPropertiesDescriptors, arrayMove, asyncMap, blockUuidInContent, blogPostWithPicture, blogPostWithPictureExpected, breakpoints, browserAllowed, browserList, bufferToFile, bufferToHex, bufferToString, buildAttachmentIRI, buildCardIRI, buildWorkspaceIRI, calculateReadingTime, capitalizeFirstLetter, captureTest, captureTestExpectedResult, cardContextExpectedResult, cardContextExpectedResultStringify, cardContextExpectedResultSummarize, cleanAuthorName, cloneDate, complicatedTitleJSON, concatBuffers, contactCommand, containsPaywallRelatedHTML, contentJSON, convertContentToTitle, convertDateToDayMonth, convertDateToDayMonthYear, convertDateToLocal, convertKeyEventToString, debounce, debug, deepMerge, deepMergeWithConcat, defaultLength, defaultPromptsCategory, delay, emailToUrl, endOfDay, escapeRegexMetaCharacter, extractExcerptFromValue, extractTextFromHtml, findCardDefaultView, findSubstringOffset, firstDayOfWeek, formatRelativeDate, formatTranscriptAsText, generate, generateArticle, generateAttachment, generateFile, generateKindleAnnotation, generateKindleBook, generateMarkText, generateNewCard$2 as generateNewCard, generateRandomId, generateReferencedLink, generateSourceTab, generateText, getAuthorNameFromMetadata, getCardByUuidSelector, getCardContextsSelector, getCardDescriptors, getCardPropertiesSelector, getCardReadViewUrl, getCardUrl, getCardUuidFromUrl, getCategoryFromUrl, getChildTypeForCardType, getClipEmoji, getClipUuidFromUrl, getDayCardDueDate, getDayDeltaBetweenDate, getDefaultDescriptors, getDefaultPropsByCardType, getDescriptor, getDoneTaskPercentage, getFigmaEmbed, getFormattedDate, getFromEmail, getInstagramEmbed, getNameAndExtFromFileName, getNestedTextFromCard, getPropertyValues, getReferencePrefix, getSecondDeltaBetweenDate, getTextFromCard, getTwitterUrl, getUserPublicName, getValidDate, getYoutubeInLazyCurrentTimestamp, getYoutubePlayerByQueryString, getYoutubeVideoId, hasNestedCards, hasSplit, hexToBuffer, includesCardType, interpolatePromptParameters, isAppleBookUrl, isArticleParserResult, isCardDeletedOrTrashed, isCardInLaterSplit, isCardInReadSplit, isCardStatus, isCardType, isCardTypeWithContext, isCardTypeWithProperties, isCoreCardView, isDailymotionVideo, isDateAfter, isDateBefore, isDateEqual, isDefaultNotesView, isDefaultTasksView, isDefined, isDescriptorLinkToACard, isDescriptorLinkToExternal, isEmail, isEmoji, isEmptyMetadata, isEqual, isEqualWithUndefinedValues, isFacebookMessenger, isFacebookVideo, isFigmaUrl, isGithubUrl, isGmail, isIMDBVideo, isInstagramPost, isInstagramVideoOrPicture, isLazyBlock, isLinkedinDM, isLinkedinUrl, isMultiSelectPropertyDescriptor, isMultipleValue, isNetflixVideo, isNoClipNoAiApp, isPDFUrl, isPaywallContent, isPaywallUrl, isReadLaterSplit, isRedditUrl, isSameDay, isSameMonth, isSlackUrl, isStringDateBetweenOtherStringDates, isSuperhuman, isTiktokVideo, isToday, isTrashSelector, isTweetUrl, isTwitter, isTwitterUrl, isTypeFilterCardView, isUrl, isUrlOfType, isViewHeaderDateFilter, isViewHeaderMultiSelectFilter, isVimeoVideo, isWhatsappWeb, isYesterday, isYoutubeAPI, isYoutubeUrl, isYoutubeVideo, jsonToQueryString, keyboardEventToString, lastDayOfWeek, lazyCommandGroups, loadYoutubeApi, makeAuthorOption, makeCardView, makeDefaultDescriptorFilter, makeEmptyCardView, makeFilterFromCardView, makeFilterFromTypeFilterCardView, makeGroupsDescriptor, makeGroupsDescriptorDefaultOptions, makeLaterOption, makeMultiSelectDescriptor, makeMultiSelectOption, makeMultiSelectOptionId, makeMultiSelectPropertyValue, makeNowOption, makePropertyDescriptorId, makePropertyValue, makePropertyValueCardContext, makePropertyValueGroupWithAuthorValue, makePropertyValueReadLaterWithLaterValue, makePropertyValueWithAuthorOption, makePropertyValueWithValue, makeReadLaterDescriptor, makeReadLaterValue, makeReadOption, makeSplit, makeTypeFilterCardView, makeTypeFilterFromMetadata, makeViewId, mapBase64ToFile, mapBlobToBuffer, maxDate, maybeMakeMetadataReady, maybeSetDefaultWorkspaceMetadata, mediaQueries, mergeCards, minuteToTimestamp, mock, noClipNoAiApps, normalizeShortcutForGlobalShortcut, objectToFormData, parseYoutubeEmbedUrl, pipe, preStringToHtml, queryParser, randomCardContent, reformatSubject, removeDescriptorOptionsToMetadata, removeDynamicQueryFromYoutubeUrl, removePrefixInFrontOrUrl, safariBrowser, sanitizeFacebookEmoji, sanitizeForFileName, sanitizeSlackEmoji, searchPersonSelector, searchSourceSelector, setLastUpdate$2 as setLastUpdate, shouldShowLaterStatusInSplit, slackToChannelUrl, slackToUrl, stringToBuffer, subDays, substringTitle, summarize, summarizeAuthorName, tableHTMLEmpty, tableHTMLWithContent, tableHTMLWithContentFirstNode, tableHTMLWithContentSanitize, tableWithErrorAttributeKey, tableWithErrorAttributeKeyExpectedResult, testHTML, testHtmlWithCaptureBlockExpected, testHtmlWithCaptureContext, testHtmlWithCustomReactComponent, testHtmlWithCustomReactComponentExpected, testHtmlWithCustomReactComponentFirstNode, testHtmlWithImg, testHtmlWithRelatedCard, testHtmlWithRelatedCardSummarize, textHTMLFirstBlockElement, timestampToMinute, titleReferenceJSON, toIsoDate$1 as toIsoDate, transformContent, transformEmailString, transformEmailToTitle, transformNameToTitle, traverseProsemirrorDoc, tryResolvingPromiseWithinMs, turnFileIntoLocalUrl, turnOrdinalNounIntoDate, urlMediaParser, uuidForBlockMigration$1 as uuidForBlockMigration, uuidForBlockMigrationInterface, uuidInterface, uuidV1$1 as uuidV1, uuidV1Interface, uuidV5$1 as uuidV5, xmlToJson };
